/**
 * Adaptador de demonstração.
 *
 * Os dados vêm do protótipo (`ui_kits/vecchia_app/`), que o
 * `docs/DEMO_DATA_CATALOG.md` cataloga como referência de contrato e de seed —
 * ponto de partida do banco, não definição final.
 *
 * **Uma semana só para todas as telas.** No protótipo, Início e Agenda tinham
 * listas separadas para o mesmo dia e discordavam entre si; aqui a agenda da
 * semana é a fonte, e o resumo do dia é um filtro dela.
 *
 * Escreve em memória: a alteração vale enquanto a aba viver. Persistir aqui
 * daria a impressão de banco e esconderia a falta dele.
 */

import type { PortaDeDados } from './porta';
import { atual as sessaoAtual, pode as sessaoPode } from '@/session/sessao';
import { unidadeAtiva } from '@/session/unidadeAtiva';
import { normalizarGatilho } from './cadastros';
import type {
    AssinaturaVistaPelaClinica, CargaDeAtendimentoECrm, CategoriaDeProcedimento, DadosDaEtiqueta, DadosDaFila,
    DadosDaMensagemRapida, DadosDoDepartamento, DadosDoPacoteDoCatalogo, PacoteDoCatalogo, PreferenciaSalva, ValorDePreferencia,
} from './cadastros';
import { DEPARTAMENTOS, ETIQUETAS, ETIQUETAS_DAS_CONVERSAS, FILAS_DE_ATENDIMENTO, MENSAGENS_RAPIDAS, MODELOS_DE_ATESTADO, PACOTES_DO_CATALOGO } from './cadastrosDemo';
import type { DepartamentoSemente, EtiquetaSemente, FilaSemente, MensagemRapidaSemente } from './cadastrosDemo';
import { preferenciaPorChave } from './preferencias';
import { situacaoDaConversa } from './comunicacao';
import type { SinaisDaConversa } from './comunicacao';
import { competenciaDoDia, primeiroDiaDoMesSeguinte, valoresDasParcelas, vencimentoDaParcela } from './financeiroAvulso';
import type {
    CadastrosFinanceiros, DadosDaConta, DadosDoMetodo, DetalheDoTitulo, EdicaoDeTitulo, LinhaDoOfx, LinhaDoExtratoImportada,
    NovaTransferencia, NovoTitulo, PagamentoEstornado, TipoDeMetodo, Transferencia,
} from './financeiroAvulso';
import { CONVENIOS, LEADS, MEMBROS, PACIENTES } from './contatosDemo';
import { etapasAtivas, etapasDoModelo, funilPadrao, montarPipeline, ordenarFunis, problemaDaEdicao, proximaEtapa } from './funis';
import type { EtapaEditada, ModeloDeFunil } from './funis';
import { FUNIS_SEMENTE } from './funisDemo';
import { QR_DE_DEMONSTRACAO, indisponibilidadeDoProvedor } from './integracoes';
import { LIMITE_DO_DISPARO, problemaDaMensagemDaCampanha } from './disparoDeCampanha';
import type { ResultadoDoDisparo } from './disparoDeCampanha';
import { problemaDoArquivo, tipoDaMensagemDoArquivo } from './midiaDaMensagem';
import { CANAIS, CONVERSAS, FILAS, MENSAGENS } from './comunicacaoDemo';
import { FICHAS, FINANCEIRO, FINANCEIRO_VAZIO, LINHA_DO_TEMPO } from './pacienteDemo';
import { DOCUMENTOS_EMITIDOS, MODELOS_DE_DOCUMENTO } from './documentosDemo';
import type { DocumentoSemente, ModeloSemente } from './documentosDemo';
import { CATEGORIAS, CONTAS, METODOS, MOVIMENTACOES, TITULOS } from './financeiroDemo';
import { FICHAS as FICHAS_CLINICAS, MODELOS as MODELOS_SEMENTE } from './fichasDemo';
import { ORCAMENTOS, PACOTES } from './vendasDemo';
import {
    CATEGORIAS_DE_PRODUTO, FORNECEDORES, MARCAS, MOVIMENTACOES_DE_ESTOQUE, PRODUTOS, RESERVAS,
} from './estoqueDemo';
import type { ProdutoSemente, ReservaSemente } from './estoqueDemo';
import { INSUMOS_DOS_PROCEDIMENTOS, PROCEDIMENTOS } from './procedimentosDemo';
import { PEDIDOS } from './comprasDemo';
import type { PedidoSemente } from './comprasDemo';
import type { InsumoSemente, ProcedimentoSemente } from './procedimentosDemo';
import { custoDeConsumoCentavos, embalagensDe } from '@/comuns/unidades';
import { contarCampos, preencherCorpo } from '@/comuns/documentos';
import { fluxoDiario, fluxoMensal, janelaDeMeses, relatorioDeCompetencia, totaisPorCategoria } from './relatoriosFinanceiros';
import { montarRelatorio } from './relatoriosDaClinica';
import { faixaSobreposta, horasSemanais, normalizarDias } from './jornada';
import { GATILHOS, indicadoresDeAutomacoes, montarAutomacao } from './automacoesDaClinica';
import { derivadosDaCampanha, indicadoresDeMarketing, publicosDisponiveis } from './marketingDaClinica';
import { indicadoresDaPlataforma, indicadoresDoFaturamento, situacaoDaFatura } from './plataformaDaVecchia';
import { executarProposta } from './propostasDoAgente';
import { executar, simular } from './simulacaoDeFluxo';
import {
    etapasDe, horasAteResolver, indicadoresDoOnboarding, indicadoresDoSuporte,
} from './suporteDaPlataforma';
import type { LinhaDeAutomacao } from './automacoesDaClinica';
import type { AgendamentoDoRelatorio } from './relatoriosDaClinica';
import type {
    AcaoDePareamento, ArquivoDaFicha, PareamentoDoCanal, ProvedorDeCanalDaPlataforma,
    AgendaDaSemana, Agendamento, CargaDeContatos, EdicaoDeAgendamento, EdicaoDeLead, EdicaoDeMembro, NovoUsuarioDaEquipe,
    Atendimento, CargaDaCaixa, CargaDeAtendimentos, CargaDoPaciente, Conversa, FichaDoPaciente,
    FiltroDeAtendimentos, FiltroDeContatos, FiltroDeConversas, Lead, Lembrete, MembroDaEquipe,
    CargaDeVendas, CargaDoPipeline, EtapaDoFunil, FiltroDeVendas, Funil, Mensagem, NovoAgendamento,
    NovoContato, NovoOrcamento, Orcamento, Profissional, RegistroDeHistorico, ResumoDoDia,
    CargaDoFinanceiro, CategoriaDeConta, CategoriaFinanceira, FiltroDoFinanceiro, NovoPagamento, SituacaoDoAtendimento,
    AtendimentoEmCurso, CampoDeFicha, FichaPreenchida, ModeloDeFicha, NovaFicha,
    ProntuarioDoPaciente, ResumoDoPaciente, StatusDoOrcamento, CondicaoDeCobranca, Tarefa, TipoDeContato, TipoImportavel, Titulo,
    CategoriaParaEscolha, DadosDoPrestador, PrestadorDeServico, StatusDoCanal, TipoDeServico,
    CargaDoEstoque, CategoriaDeProduto, DadosDaCategoriaDeProduto, DadosDaMarca, DadosDoFornecedor,
    DadosDoProduto, FiltroDoEstoque, Fornecedor, Marca, MovimentacaoDeEstoque, NovaMovimentacao,
    NovoProduto, Produto, ReservaDeEstoque, SituacaoDoProduto,
    CargaDeProcedimentos, ContextoDaReserva, DadosDoProcedimento, FiltroDeProcedimentos,
    InsumoDoProcedimento, Procedimento,
    CargaDeCompras, DadosDoPedido, FiltroDeCompras, ItemDoPedido, PedidoDeCompra, Recebimento,
    SugestaoDeCompra,
    CargaDeDocumentos, DadosDoModeloDeDocumento, Documento, DocumentoDoPaciente, FiltroDeDocumentos,
    ModeloDeDocumento, NovoDocumento, StatusDoDocumento,
    CargaDeRelatorios, FiltroDeRelatorios,
    AbaDeConfiguracoes, CargaDeConfiguracoes, DadosDaClinica, DiaDeFuncionamento, EdicaoDaClinica,
    EdicaoDaUnidade, NovaUnidade, SalaDaClinica, UnidadeDaClinica,
    AbaDeAutomacoes, Automacao, CargaDeAutomacoes, ResultadoDaExecucao,
    AbaDeMarketing, Campanha, CargaDeMarketing, DadosDaCampanha, LinhaDeOrigem, PublicoDaCampanha,
    DadosDoLembrete,
    CargaDaClinicaNaPlataforma, CargaDaPlataforma, ClinicaNaPlataforma, EventoDeAuditoria,
    UsuarioNaPlataforma,
    AssinaturaDaClinica, CargaDoFaturamento, DadosDoPlano, Fatura, Plano,
    CargaDoOnboarding, CargaDoSuporte, Chamado, DadosDoChamado, OnboardingDaClinica,
    CargaDasFlags, FlagDeModulo,
    Canal, CanalDeIntegracao, ChaveDeIa, DadosDoCanal, DocumentoDaBase, LinhaDeConsumoDeIa, MeuOrcamentoDeIa, OrcamentoDeIa, ProvedorDeIa, RetornoEmAberto,
    Agente, DadosDoAgente, PrioridadeDaTarefa, PropostaDoAgente, RespostaDoAgente,
    DadosDoFluxo, Fluxo, ResultadoDoFluxo, SimulacaoDoFluxo,
    ContaFinanceira, MetodoDePagamento,
    ContatoParaEscolha, MinhaConta, OpcoesDoAgendamento, LinhaImportada, RecusaDaImportacao, ResultadoDaImportacao,
    ChaveDeApi, ChaveDeApiCriada, CreditoDaClinica, DadosDaFaturaAvulsa, DadosDaNovaClinica, DadosDoCredito,
    DadosDoDesconto, DescontoDaClinica, OperadorDaPlataforma, SaudeDoBanco, SituacaoDaClinica, TipoDeCredito,
    UsoDeChaveDeApi,
    CobrancaDaFatura, CobrancasCriadas, DadosDoGateway, GatewayDePagamento, InadimplenciaDaClinica,
    ProvedorDeGateway, TesteDoGateway,
    EtiquetaAplicada,
} from './tipos';
import { MOTIVO_MINIMO_PARA_REABRIR, diaEmSaoPaulo, estaPausado } from './atendimentoEmCurso';
import type { AtendimentoEmAndamento, PausaDoAtendimento, RascunhoDaFicha } from './atendimentoEmCurso';
import { normalizarPlano, problemaDoPlano } from './planosDeSaude';
import type { DadosDoPlanoDeSaude, PlanoDeSaude } from './planosDeSaude';
import { PLANOS_DE_SAUDE_SEMENTE } from './planosDeSaudeDemo';
import { cabecalhoDe, problemasDaAlteracao } from './fichaImpressa';
import type { CabecalhoDaClinica } from './fichaImpressa';
import { gerarLinkDeTeleconsulta } from './teleconsulta';
import { faltasDaClinica, primeiraFalta } from './dadosDaClinica';
import { operadorTem, recusaAoDefinirPermissoes } from './permissoesDaPlataforma';
import { competenciaDeHoje, competenciaSomada, flagLigadaPara, valorDaFatura } from './plataformaDaVecchia';
import { atual } from '@/session/sessao';
import type { DadosDoWebhook, Webhook } from './webhooks';
import { brainNaDemonstracao, reiniciarBrainDaDemonstracao } from './adaptadorDemoBrain';
import { notaDeIaNaDemonstracao, reiniciarNotasDaDemonstracao } from './adaptadorDemoNotaDeIa';
import { notasFiscaisNaDemonstracao, reiniciarNotasFiscaisDaDemonstracao } from './adaptadorDemoNotasFiscais';
import { aplicarCampanhaNoLead, anunciosNaDemonstracao, reiniciarAnunciosDaDemonstracao } from './adaptadorDemoAnuncios';

/** Dia de referência dos dados de demonstração — terça-feira. */
const HOJE = '2026-09-08';

/**
 * Tetos de IA da demonstração: um sem teto, um perto do limite, um estourado.
 *
 * A semente é separada da variável porque `salvarOrcamentoDeIa` MUTA a lista —
 * e estado que sobrevive a `reiniciarDemonstracao()` faz um teste contaminar o
 * seguinte. Já contaminou: um teste que definia teto deixava o próximo vendo o
 * aviso errado, e a falha aparecia no teste inocente.
 */
type OrcamentoSemeado = { clinicaId: string; clinica: string; limiteTokensMes: number | null; tokensDoMes: number };

const ORCAMENTOS_SEMENTE: OrcamentoSemeado[] = [
    { clinicaId: 'c-vecchia', clinica: 'Clínica Vecchia', limiteTokensMes: null, tokensDoMes: 31744 },
    { clinicaId: 'c-aurora', clinica: 'Estética Aurora', limiteTokensMes: 200000, tokensDoMes: 186420 },
    { clinicaId: 'c-lumina', clinica: 'Lumina Saúde', limiteTokensMes: 50000, tokensDoMes: 50318 },
];

let orcamentosDaDemonstracao: OrcamentoSemeado[] = copiar(ORCAMENTOS_SEMENTE);

/**
 * A base de conhecimento da demonstração.
 *
 * Inclui um documento FALHADO de propósito: o PDF digitalizado é o caso mais
 * comum de frustração, e a tela precisa mostrar como ela explica isso antes de
 * alguém tropeçar nele com um arquivo de verdade.
 */
const BASE_SEMENTE: DocumentoDaBase[] = [
    { id: 'base-remarcacao', titulo: 'Política de remarcação', tipo: 'texto', bytes: 892, situacao: 'pronto', trechos: 1, criadoEm: `${HOJE}T09:12:00-03:00` },
    { id: 'base-protocolos', titulo: 'Protocolos de peeling', tipo: 'application/pdf', bytes: 248320, situacao: 'pronto', trechos: 34, criadoEm: `${HOJE}T09:05:00-03:00` },
    {
        id: 'base-tabela', titulo: 'Tabela de preços (digitalizada)', tipo: 'application/pdf', bytes: 1904128,
        situacao: 'falhou', trechos: 0,
        erro: 'Não saiu texto deste PDF. Ele provavelmente é uma digitalização (imagem), e imagem não tem texto para ler — envie o arquivo original, ou cole o conteúdo como texto.',
        criadoEm: `${HOJE}T08:40:00-03:00`,
    },
];

let baseDaDemonstracao: DocumentoDaBase[] = copiar(BASE_SEMENTE);

const PROFISSIONAIS: Profissional[] = [
    { id: 'ana', nome: 'Dra. Ana Prado' },
    { id: 'paulo', nome: 'Dr. Paulo Menezes' },
];

const SALAS: SalaDaClinica[] = [
    { id: 's1', nome: 'Sala 1', ativa: true },
    { id: 's2', nome: 'Sala 2', ativa: true },
];

const CLINICA_SEMENTE: DadosDaClinica = {
    id: 'clinica-vecchia',
    nome: 'Vecchia — Jardins',
    razaoSocial: 'Vecchia Estética e Saúde LTDA',
    documento: '12.345.678/0001-90',
    tipoPessoa: 'juridica',
    email: 'contato@clinicavecchia.com.br',
    fuso: 'America/Sao_Paulo',
    plano: 'profissional',
};

const UNIDADES_SEMENTE: UnidadeDaClinica[] = [
    {
        id: 'u-jardins', nome: 'Jardins', telefone: '(11) 3555-9080',
        email: 'contato@clinicavecchia.com.br', cep: '01452-000', estado: 'SP', cidade: 'São Paulo',
        bairro: 'Jardim Paulistano', rua: 'Rua Pedroso Alvarenga', numero: '1245', complemento: 'Conjunto 82',
    },
];

/* A jornada da clínica, igual à da semente do banco (`0019`): segunda a quinta
   com almoço, sexta corrida, sábado só de manhã, domingo fechado. */
/* As seis da `0020`. Contadores em zero: nenhuma jamais executou, e escrever
   outro número aqui seria inventar histórico de operação. */
const AUTOMACOES_SEMENTE: LinhaDeAutomacao[] = [
    { id: 'auto-1', nome: 'Confirmação de agendamento', gatilho: 'agendamento-criado', acao: 'enviar-whatsapp', ativa: true, execucoes: 0 },
    { id: 'auto-2', nome: 'Lembrete 24h antes', gatilho: 'agendamento-24h', acao: 'enviar-whatsapp', ativa: true, execucoes: 0 },
    { id: 'auto-3', nome: 'Pesquisa de satisfação', gatilho: 'atendimento-concluido', acao: 'enviar-whatsapp', ativa: true, execucoes: 0 },
    { id: 'auto-4', nome: 'Cobrança de título vencido', gatilho: 'titulo-vencido', acao: 'criar-tarefa', ativa: false, execucoes: 0 },
    { id: 'auto-5', nome: 'Reativação 90 dias', gatilho: 'sem-retorno-90d', acao: 'adicionar-campanha', ativa: true, execucoes: 0 },
    { id: 'auto-6', nome: 'Alerta de estoque mínimo', gatilho: 'estoque-minimo', acao: 'criar-tarefa', ativa: true, execucoes: 0 },
];

/* As cinco do protótipo, com público virado regra — as mesmas da `0021`. */
interface CampanhaSemente {
    id: string;
    nome: string;
    canal: Campanha['canal'];
    publicoTipo: PublicoDaCampanha;
    publicoParam: Campanha['publicoParam'];
    origemLead?: string;
    status: Campanha['status'];
    inicio?: string;
    fim?: string;
    investimentoCentavos: number;
}

const CAMPANHAS_SEMENTE: CampanhaSemente[] = [
    { id: 'camp-1', nome: 'Reativação — 90 dias sem retorno', canal: 'whatsapp', publicoTipo: 'sem-retorno', publicoParam: { dias: 90 }, status: 'ativa', inicio: '2026-09-01', investimentoCentavos: 0 },
    { id: 'camp-2', nome: 'Botox setembro — Meta Ads', canal: 'instagram', publicoTipo: 'por-origem', publicoParam: { origem: 'Instagram' }, origemLead: 'Instagram', status: 'ativa', inicio: '2026-08-28', investimentoCentavos: 185000 },
    { id: 'camp-3', nome: 'Aniversariantes do mês', canal: 'whatsapp', publicoTipo: 'aniversariantes', publicoParam: {}, status: 'ativa', inicio: '2026-09-01', investimentoCentavos: 0 },
    { id: 'camp-4', nome: 'Peeling primavera', canal: 'email', publicoTipo: 'todos-pacientes', publicoParam: {}, status: 'encerrada', inicio: '2026-08-15', fim: '2026-08-31', investimentoCentavos: 0 },
    { id: 'camp-5', nome: 'Harmonização — remarketing', canal: 'instagram', publicoTipo: 'por-origem', publicoParam: { origem: 'Google Ads' }, origemLead: 'Google Ads', status: 'rascunho', investimentoCentavos: 0 },
];

/* As cinco clínicas da base, iguais às da `0023`: duas em operação e três que
   existem para as abas por situação terem o que filtrar. */
const CLINICAS_DA_PLATAFORMA: ClinicaNaPlataforma[] = [
    { id: 'clinica-vecchia', nome: 'Vecchia — Jardins', apelido: 'vecchia-jardins', plano: 'profissional', situacao: 'ativa', criadoEm: '2025-03-11', membrosAtivos: 4, contatos: 6, agendamentos30d: 13, ultimoAcesso: '2026-09-08T09:12:00-03:00' },
    { id: 'clinica-bellavita', nome: 'Clínica Bellavita', apelido: 'bellavita', plano: 'essencial', situacao: 'ativa', criadoEm: '2025-08-02', membrosAtivos: 1, contatos: 1, agendamentos30d: 0, ultimoAcesso: '2026-08-19T14:40:00-03:00' },
    { id: 'clinica-aurora', nome: 'Estética Aurora', apelido: 'aurora', plano: 'essencial', situacao: 'trial', criadoEm: '2026-08-30', membrosAtivos: 0, contatos: 0, agendamentos30d: 0 },
    { id: 'clinica-bemviver', nome: 'Clínica Bem Viver', apelido: 'bem-viver', plano: 'profissional', situacao: 'inadimplente', criadoEm: '2025-07-08', membrosAtivos: 0, contatos: 0, agendamentos30d: 0 },
    { id: 'clinica-lumine', nome: 'Studio Lumine', apelido: 'lumine', plano: 'essencial', situacao: 'cancelada', criadoEm: '2024-09-01', membrosAtivos: 0, contatos: 0, agendamentos30d: 0 },
];

/* Os dois planos SEM preço, como na `0024`: nulo não é zero. */
interface PlanoSemente {
    id: string;
    codigo: string;
    nome: string;
    ciclo: Plano['ciclo'];
    precoCentavos?: number;
    limiteUnidades?: number;
    limiteMembros?: number;
    ativo: boolean;
}

/* A folga de vagas que a plataforma deu a cada clínica (0165). Vazio = vale o
   limite do plano. Mora aqui, e não na semente, porque é decisão de operador. */
const folgasDeMembros = new Map<string, number | null>();

/** O limite em vigor: a folga do cadastro vence o plano; nulo é sem teto. */
function limiteDeMembrosDaDemo(clinicaId: string, planoCodigo: string): number | null {
    const folga = folgasDeMembros.get(clinicaId);
    if (folga !== undefined && folga !== null) return folga;
    return PLANOS_SEMENTE.find((p) => p.codigo === planoCodigo)?.limiteMembros ?? null;
}

/** Vaga é acesso: conta ativo e convite pendente, nunca desativado. */
function vagasOcupadasDaDemo(): number {
    return membros.filter((m) => m.acesso !== 'desativado').length;
}

const PLANOS_SEMENTE: PlanoSemente[] = [
    { id: 'plano-essencial', codigo: 'essencial', nome: 'Essencial', ciclo: 'mensal', limiteUnidades: 1, limiteMembros: 5, ativo: true },
    { id: 'plano-profissional', codigo: 'profissional', nome: 'Profissional', ciclo: 'mensal', limiteUnidades: 3, limiteMembros: 20, ativo: true },
];

interface FaturaSemente {
    id: string;
    clinicaId: string;
    competencia: string;
    vencimento: string;
    valorCentavos: number;
    pagoEm?: string;
    notaFiscal?: string;
    tipo?: 'assinatura' | 'avulsa';
    descricao?: string;
    descontoCentavos?: number;
    creditoCentavos?: number;
    estornadaEm?: string;
    estornoMotivo?: string;
}

/* Nenhuma fatura: sem preço em plano nenhum, não há o que faturar. É o estado
   real, e é o que faz a tela dizer o que falta em vez de listar valores. */
const FATURAS_SEMENTE: FaturaSemente[] = [];

interface ChamadoSemente {
    id: string;
    clinicaId: string;
    assunto: string;
    descricao: string;
    canal: Chamado['canal'];
    prioridade: Chamado['prioridade'];
    situacao: Chamado['situacao'];
    responsavelNome?: string;
    abertoEm: string;
    resolvidoEm?: string;
}

/* Os dois da `0025`: um aberto sem dono e um resolvido — o par que faz as duas
   situações existirem na tela. */
const CHAMADOS_SEMENTE: ChamadoSemente[] = [
    { id: 'ch-1', clinicaId: 'clinica-vecchia', assunto: 'WhatsApp não conecta', descricao: 'A recepção tenta parear o Canal 01 e o QR expira antes da leitura.', canal: 'whatsapp', prioridade: 'alta', situacao: 'aberto', abertoEm: '2026-09-06T09:00:00-03:00' },
    { id: 'ch-2', clinicaId: 'clinica-bellavita', assunto: 'Importar a lista de pacientes', descricao: 'Planilha de 340 contatos exportada do sistema antigo.', canal: 'email', prioridade: 'media', situacao: 'resolvido', responsavelNome: 'Rafael Nunes', abertoEm: '2026-08-30T10:00:00-03:00', resolvidoEm: '2026-09-01T15:00:00-03:00' },
];

/* As seis da `0027`, todas ligadas: flag nasce sem mudar nada. */
const FLAGS_SEMENTE: FlagDeModulo[] = [
    { id: 'flag-marketing', chave: 'modulo.marketing', modulo: 'marketing', descricao: 'Campanhas, públicos e origem dos leads', ativoPadrao: true, excecoes: [] },
    { id: 'flag-automacoes', chave: 'modulo.automacoes', modulo: 'automacoes', descricao: 'Regras de gatilho e ação', ativoPadrao: true, excecoes: [] },
    { id: 'flag-compras', chave: 'modulo.compras', modulo: 'compras', descricao: 'Pedidos de compra e recebimento', ativoPadrao: true, excecoes: [] },
    { id: 'flag-estoque', chave: 'modulo.estoque', modulo: 'estoque', descricao: 'Produtos, saldo e reservas', ativoPadrao: true, excecoes: [] },
    { id: 'flag-documentos', chave: 'modulo.documentos', modulo: 'documentos', descricao: 'Modelos, emissão e assinatura', ativoPadrao: true, excecoes: [] },
    { id: 'flag-procedimentos', chave: 'modulo.procedimentos', modulo: 'procedimentos', descricao: 'Catálogo, insumos e custo', ativoPadrao: true, excecoes: [] },
    { id: 'flag-agentes', chave: 'modulo.agentes', modulo: 'agentes', descricao: 'Agentes de atendimento, pré-atendimento e fluxos', ativoPadrao: true, excecoes: [] },
];

/* O copiloto que a `0031` semeia. Ele saiu do produto em 13/09/2026: o registro
   fica, como a linha fica no banco, e `agentes()` não o entrega. */
const AGENTES_SEMENTE: Agente[] = [
    {
        id: 'agente-copiloto',
        tipo: 'copiloto',
        nome: 'Copiloto da clínica',
        ativo: true,
        tom: 'direto',
        missao: 'Ajudar a equipe a decidir o que fazer hoje, lendo os números da tela.',
        instrucoes: 'Priorize agenda e retorno de paciente; entre no financeiro quando perguntarem. Termine sempre com o próximo passo.',
        temperatura: 0.3,
        ferramentas: [],
        limites: {},
        atualizadoEm: '2026-09-08T08:00:00-03:00',
    },
];

/* O fluxo de exemplo da `0032`: desligado, porque fluxo que chega ativo executa
   antes de alguém ler o que ele faz. */
const FLUXOS_SEMENTE: Fluxo[] = [
    {
        id: 'fluxo-reativacao',
        nome: 'Reativação de quem sumiu',
        descricao: 'Quem foi atendido há muito tempo e não voltou vira tarefa para a recepção ligar.',
        gatilho: 'sem-retorno-90d',
        ativo: false,
        execucoes: 0,
        atualizadoEm: '2026-09-08T08:00:00-03:00',
        passos: [
            { id: 'p1', tipo: 'condicao', campo: 'diasSemRetorno', operador: 'maior', valor: '120' },
            { id: 'p2', tipo: 'acao', acao: 'followup_criar', campos: { titulo: 'Ligar para reativar' } },
        ],
    },
];

const AUDITORIA_SEMENTE: EventoDeAuditoria[] = [
    { id: 'aud-1', clinica: 'Vecchia — Jardins', autorNome: 'Sistema', acao: 'migracao.aplicada', tabela: 'clinicas', ocorridoEm: '2026-09-08T08:00:00-03:00' },
    { id: 'aud-2', clinica: 'Vecchia — Jardins', autorNome: 'Sistema', acao: 'semente.carregada', tabela: 'contatos', ocorridoEm: '2026-09-08T08:00:10-03:00' },
    { id: 'aud-3', clinica: 'Clínica Bellavita', autorNome: 'Sistema', acao: 'semente.carregada', tabela: 'contatos', ocorridoEm: '2026-09-08T08:00:20-03:00' },
];

/* ---- trilha T5b: o painel da plataforma na demonstração ---- */

/* As clínicas da base mudam pelo painel (abrir, suspender, trocar plano). A
   lista é mutada no lugar — outras leituras a referenciam — e volta a esta cópia
   em `reiniciarDemonstracao()`. */
const CLINICAS_ORIGINAIS: ClinicaNaPlataforma[] = CLINICAS_DA_PLATAFORMA.map((c) => ({ ...c }));

/* A equipe da plataforma, com os nomes e escopos do protótipo
   (`ui_kits/vecchia_admin/adminData.jsx`). O servidor tem só o super admin; a
   demonstração tem três para a tela de permissões ter o que mostrar. */
const OPERADORES_SEMENTE: Omit<OperadorDaPlataforma, 'souEu'>[] = [
    {
        usuarioId: 'op-rafael', nome: 'Rafael Nunes', email: 'admin@vecchia.com.br', temMfa: false,
        permissoes: ['*'], ultimoAcesso: '2026-09-08T09:52:00-03:00',
    },
    {
        usuarioId: 'op-bianca', nome: 'Bianca Antunes', email: 'bianca@vecchia.com.br', temMfa: true,
        permissoes: ['platform', 'clientes.ver', 'impersonar', 'auditoria.ver'], ultimoAcesso: '2026-09-08T09:14:00-03:00',
    },
    {
        usuarioId: 'op-tiago', nome: 'Tiago Ferraz', email: 'tiago@vecchia.com.br', temMfa: true,
        permissoes: ['platform', 'clientes.ver', 'financeiro.operar', 'faturamento.gerenciar', 'estorno.executar', 'creditos.adicionar', 'creditos.alterar', 'creditos.excluir'],
        ultimoAcesso: '2026-09-07T18:40:00-03:00',
    },
];

type CreditoDaDemo = CreditoDaClinica & { clinicaId: string };
type DescontoDaDemo = Omit<DescontoDaClinica, 'vigente' | 'fimCompetencia'> & { clinicaId: string };
type ChaveDaDemo = ChaveDeApi & { clinicaId: string };

const TIPOS_DE_CREDITO: TipoDeCredito[] = ['cortesia', 'compensacao', 'migracao', 'indicacao', 'acordo'];

let auditoriaDaPlataforma: EventoDeAuditoria[] = AUDITORIA_SEMENTE.map((a) => ({ ...a }));
let creditosDaDemonstracao: CreditoDaDemo[] = [];
let descontosDaDemonstracao: DescontoDaDemo[] = [];
let chavesDaDemonstracao: ChaveDaDemo[] = [];
let operadoresDaDemonstracao = OPERADORES_SEMENTE.map((o) => ({ ...o, permissoes: [...o.permissoes] }));
let unidadesDasClinicasNovas = new Map<string, UnidadeDaClinica[]>();
let proprietariasDasClinicasNovas = new Map<string, UsuarioNaPlataforma>();
let sequenciaDoPainel = 0;

const HORARIOS_SEMENTE: DiaDeFuncionamento[] = [
    { diaSemana: 0, faixas: [] },
    { diaSemana: 1, faixas: [{ inicio: '08:00', fim: '12:00' }, { inicio: '13:30', fim: '19:00' }] },
    { diaSemana: 2, faixas: [{ inicio: '08:00', fim: '12:00' }, { inicio: '13:30', fim: '19:00' }] },
    { diaSemana: 3, faixas: [{ inicio: '08:00', fim: '12:00' }, { inicio: '13:30', fim: '19:00' }] },
    { diaSemana: 4, faixas: [{ inicio: '08:00', fim: '12:00' }, { inicio: '13:30', fim: '19:00' }] },
    { diaSemana: 5, faixas: [{ inicio: '08:00', fim: '18:00' }] },
    { diaSemana: 6, faixas: [{ inicio: '09:00', fim: '13:00' }] },
];

/* O fuso vai escrito no dado, como virá do banco: instante, não "hora local"
   solta, que se perde na primeira consulta feita de outro fuso. */
function em(dia: string, hora: string): string {
    return `${dia}T${hora}:00-03:00`;
}

const AGENDA_SEMENTE: Agendamento[] = [
    /* Segunda, 07 */
    { id: 'ag-1', inicioEm: em('2026-09-07', '09:00'), duracaoMin: 60, pacienteId: 'p-helena', paciente: 'Helena Vasques', procedimento: 'Peeling', profissional: 'Dra. Ana Prado', sala: 'Sala 1', status: 'confirmado', vinculo: { tipo: 'paciente', id: 'p-helena', nome: 'Helena Vasques' } },
    { id: 'ag-2', inicioEm: em('2026-09-07', '12:00'), duracaoMin: 60, pacienteId: '', paciente: 'Bloqueio — almoço', procedimento: '', profissional: '', status: 'bloqueio' },

    /* Terça, 08 — o dia de hoje, que o resumo mostra */
    { id: 'ag-3', inicioEm: em(HOJE, '08:00'), duracaoMin: 30, pacienteId: 'p-bruno', paciente: 'Bruno Sartori', procedimento: 'Retorno', profissional: 'Dr. Paulo Menezes', status: 'concluido', vinculo: { tipo: 'paciente', id: 'p-bruno', nome: 'Bruno Sartori' } },
    { id: 'ag-4', inicioEm: em(HOJE, '10:00'), duracaoMin: 60, pacienteId: 'p-marina', paciente: 'Marina Duarte', procedimento: 'Avaliação facial', profissional: 'Dra. Ana Prado', sala: 'Sala 1', status: 'pendente', vinculo: { tipo: 'lead', id: 'l-marina', nome: 'Marina Duarte', oportunidade: 'Avaliação facial · R$ 4.200', etapa: 'Em contato' } },
    { id: 'ag-5', inicioEm: em(HOJE, '14:30'), duracaoMin: 120, pacienteId: 'p-clara', paciente: 'Clara Ribeiro', procedimento: 'Limpeza de pele', profissional: 'Dra. Ana Prado', sala: 'Sala 2', status: 'confirmado', vinculo: { tipo: 'paciente', id: 'p-clara', nome: 'Clara Ribeiro' } },
    { id: 'ag-6', inicioEm: em(HOJE, '16:00'), duracaoMin: 60, pacienteId: 'p-helena', paciente: 'Helena Vasques', procedimento: 'Peeling', profissional: 'Dra. Ana Prado', sala: 'Sala 1', status: 'confirmado', vinculo: { tipo: 'paciente', id: 'p-helena', nome: 'Helena Vasques' } },

    /* Quarta a sábado */
    { id: 'ag-7', inicioEm: em('2026-09-09', '10:00'), duracaoMin: 60, pacienteId: 'p-marina', paciente: 'Marina Duarte', procedimento: 'Avaliação facial', profissional: 'Dra. Ana Prado', status: 'pendente', vinculo: { tipo: 'lead', id: 'l-marina', nome: 'Marina Duarte', oportunidade: 'Avaliação facial · R$ 4.200', etapa: 'Em contato' } },
    { id: 'ag-8', inicioEm: em('2026-09-10', '13:00'), duracaoMin: 60, pacienteId: 'p-rafael', paciente: 'Rafael Antunes', procedimento: 'Harmonização', profissional: 'Dr. Paulo Menezes', status: 'cancelado', vinculo: { tipo: 'paciente', id: 'p-rafael', nome: 'Rafael Antunes' } },
    { id: 'ag-9', inicioEm: em('2026-09-11', '11:00'), duracaoMin: 120, pacienteId: 'p-julia', paciente: 'Júlia Bastos', procedimento: 'Botox', profissional: 'Dra. Ana Prado', sala: 'Sala 1', status: 'confirmado', vinculo: { tipo: 'paciente', id: 'p-julia', nome: 'Júlia Bastos' } },
    { id: 'ag-10', inicioEm: em('2026-09-12', '09:00'), duracaoMin: 60, pacienteId: 'p-clara', paciente: 'Clara Ribeiro', procedimento: 'Retorno', profissional: 'Dra. Ana Prado', status: 'confirmado', vinculo: { tipo: 'paciente', id: 'p-clara', nome: 'Clara Ribeiro' } },

    /* Fora da semana: sem isto, o recorte de mês da tela de Consultas mostraria
       exatamente o mesmo que o de semana, e a diferença entre os dois não
       apareceria em teste nenhum. */
    { id: 'ag-11', inicioEm: em('2026-09-05', '11:00'), duracaoMin: 60, pacienteId: 'p-julia', paciente: 'Júlia Bastos', procedimento: 'Toxina botulínica', profissional: 'Dra. Ana Prado', sala: 'Sala 1', status: 'concluido', vinculo: { tipo: 'paciente', id: 'p-julia', nome: 'Júlia Bastos' } },
    { id: 'ag-12', inicioEm: em('2026-09-22', '10:00'), duracaoMin: 60, pacienteId: 'p-bruno', paciente: 'Bruno Sartori', procedimento: 'Limpeza de pele', profissional: 'Dr. Paulo Menezes', sala: 'Sala 2', status: 'confirmado', vinculo: { tipo: 'paciente', id: 'p-bruno', nome: 'Bruno Sartori' } },
    /* Março: o atendimento que faz de Rafael o paciente que sumiu. Sem ele, a
       régua de reativação não tem um caso sequer — e três telas mostram zero
       sem que ninguém saiba se contam certo. Igual à semente da `0030`. */
    { id: 'ag-14', inicioEm: em('2026-03-12', '15:00'), duracaoMin: 90, pacienteId: 'p-rafael', paciente: 'Rafael Antunes', procedimento: 'Harmonização', profissional: 'Dr. Paulo Menezes', sala: 'Sala 1', status: 'concluido', vinculo: { tipo: 'paciente', id: 'p-rafael', nome: 'Rafael Antunes' } },

    { id: 'ag-13', inicioEm: em('2026-09-28', '14:00'), duracaoMin: 60, pacienteId: 'p-helena', paciente: 'Helena Vasques', procedimento: 'Toxina botulínica', profissional: 'Dra. Ana Prado', sala: 'Sala 1', status: 'confirmado', vinculo: { tipo: 'paciente', id: 'p-helena', nome: 'Helena Vasques' } },
];

/** Tabela de preços dos procedimentos, em centavos. */
const PRECOS: Record<string, number> = {
    'Retorno': 0,
    'Avaliação facial': 15000,
    'Limpeza de pele': 38000,
    'Peeling': 74000,
    'Peeling de renovação': 74000,
    'Toxina botulínica': 210000,
    'Botox': 210000,
    'Harmonização': 240000,
    'Harmonização facial': 240000,
};

/** Fichas clínicas já preenchidas, por agendamento. */
const FICHAS_DO_ATENDIMENTO: Record<string, string[]> = {
    'ag-3': ['Anamnese', 'Evolução'],
    'ag-11': ['Anamnese', 'Termo de consentimento'],
    'ag-1': ['Evolução'],
};

const TAREFAS_SEMENTE: Tarefa[] = [
    { id: 't-1', titulo: 'Retornar contato com Marina Duarte', venceEm: HOJE, responsavel: 'Paulo Menezes', prioridade: 'alta', concluida: false },
    { id: 't-2', titulo: 'Enviar orçamento para Bruno Sartori', venceEm: '2026-09-09', responsavel: 'Ana Prado', prioridade: 'media', concluida: false },
    { id: 't-3', titulo: 'Conferir estoque de toxina botulínica', venceEm: '2026-09-10', responsavel: 'Recepção', prioridade: 'baixa', concluida: true },
];

const LEMBRETES_SEMENTE: Lembrete[] = [
    { id: 'r1', quando: 'Ao confirmar', descricao: 'Confirmação com data, hora, profissional e endereço', ativo: true, canal: 'ambos', offsetMin: 0 },
    { id: 'r2', quando: '1 dia antes', descricao: 'Lembrete com opção de remarcar', ativo: true, canal: 'whatsapp', offsetMin: 1440 },
    { id: 'r3', quando: '15 minutos antes', descricao: 'Aviso de proximidade', ativo: true, canal: 'whatsapp', offsetMin: 15 },
];

/* Cópia rasa na saída: sem ela, a tela receberia a mesma referência do módulo e
   uma alteração de estado no React mudaria o "banco" pelas costas do adaptador. */
function copiar<T>(lista: T[]): T[] {
    return lista.map((item) => ({ ...item }));
}

/**
 * Resumos escritos à mão para a demonstração (0171).
 *
 * Só a Clara tem: é ela quem tem consultas o bastante nos dados de demonstração para um
 * resumo fazer sentido. Nos outros o card mostra o estado "ainda não gerado", que é
 * justamente o que a clínica vê antes da primeira geração.
 */
const resumosDaDemonstracao = new Map<string, ResumoDoPaciente>([
    ['p-clara', {
        texto: 'Seis consultas entre abril e setembro de 2026, concentradas em harmonização facial e um pacote de Bléfaro sem Cortes. '
            + 'A paciente relata ardência leve nas 24 horas seguintes às sessões de peeling, sem sinais de irritação no retorno. '
            + 'Alergia a dipirona registrada na anamnese e repetida em duas consultas. '
            + 'Na última consulta, em 02/09, ficou combinado retorno em quatro semanas para avaliar a terceira sessão do pacote.',
        consultas: 6,
        ateEm: '2026-09-02T14:00:00.000Z',
        desatualizado: false,
        geradoEm: '2026-09-02T18:30:00.000Z',
    }],
]);

let agenda = copiar(AGENDA_SEMENTE);
let tarefas = copiar(TAREFAS_SEMENTE);
let pacientes = copiar(PACIENTES);
let leads = copiar(LEADS);
reiniciarAnunciosDaDemonstracao(leads);
/* ---- trilha T7 ---- */
let funis = copiar(FUNIS_SEMENTE);
/* Arquivar não apaga: a unidade sai das listas e continua dando nome ao histórico. */
let unidadesArquivadas = new Set<string>();

function etapaDaDemonstracao(id: string): EtapaDoFunil | undefined {
    for (const f of funis) {
        const etapa = f.etapas.find((e) => e.id === id);
        if (etapa) return etapa;
    }
    return undefined;
}

function unidadesAtivasDaDemonstracao(): UnidadeDaClinica[] {
    return unidades.filter((u) => !unidadesArquivadas.has(u.id));
}

/** A unidade da topbar, se ainda ativa. A principal é a primeira cadastrada, como no banco. */
function recorteDaDemonstracao(): { id: string; principal: boolean } | null {
    const escolhida = unidadeAtiva();
    const ativas = unidadesAtivasDaDemonstracao();
    if (!escolhida || !ativas.some((u) => u.id === escolhida)) return null;
    return { id: escolhida, principal: ativas[0]?.id === escolhida };
}

/** Sem unidade é da principal, salvo bloqueio, que vale para a clínica inteira. */
function agendamentoNoRecorte(a: Agendamento, r: { id: string; principal: boolean } | null): boolean {
    if (!r) return true;
    if (a.unidadeId) return a.unidadeId === r.id;
    return r.principal || a.status === 'bloqueio';
}
let membros = copiar(MEMBROS);
/* Desligado, como no banco: a demonstração não fala com paciente sozinha. */
let enviosAutomaticosDaDemo = false;
/* Quem já entrou na fila de cada campanha: a mesma chave do livro de envios. */
let disparosDaDemo = new Map<string, Set<string>>();
let salas = copiar(SALAS);
let clinica: DadosDaClinica = { ...CLINICA_SEMENTE };
let unidades = copiar(UNIDADES_SEMENTE);
let horarios: DiaDeFuncionamento[] = HORARIOS_SEMENTE.map((d) => ({ ...d, faixas: copiar(d.faixas) }));
let automacoes = copiar(AUTOMACOES_SEMENTE);
let campanhas = copiar(CAMPANHAS_SEMENTE);
let planos = copiar(PLANOS_SEMENTE);
let chamados = copiar(CHAMADOS_SEMENTE);
let flags: FlagDeModulo[] = FLAGS_SEMENTE.map((f) => ({ ...f, excecoes: copiar(f.excecoes) }));
let agentes = copiar(AGENTES_SEMENTE);
let fluxosDaClinica: Fluxo[] = FLUXOS_SEMENTE.map((f) => ({ ...f, passos: JSON.parse(JSON.stringify(f.passos)) }));
let canais = copiar(CANAIS);
/* Guarda só a EXISTÊNCIA da credencial, como o servidor: nem a demonstração
   devolve segredo, porque a tela não teria como mostrar um que não pediu. */
let credenciaisDeCanal = new Map<string, { criadoEm: string; rotacionadoEm?: string }>();
/** Fila e pessoa padrão de cada canal (0161). */
let destinoDoCanal = new Map<string, { filaPadraoId?: string; atendentePadraoId?: string }>();
/* Só na demonstração: a senha "errada" faz o teste do e-mail falhar, para a tela ter o caminho de erro. Nenhuma senha é guardada. */
let senhaDeEmailRecusada = new Set<string>();
/* Quantas vezes a tela já perguntou "já pareou?" para cada canal. Ver o
   comentário em `parearCanal`. */
let leiturasDoPareamento = new Map<string, number>();
/* Os anexos enviados na demonstração. Não persistem — e não deveriam. */
let anexosDaDemonstracao = new Map<string, File>();
let chavesDeIa: ChaveDeIa[] = [];
/* A demonstração nasce com o servidor ligado: sem ele o pareamento por QR não
   apareceria, e é justamente o que se quer mostrar. O token é fictício e a
   dica são quatro caracteres — nem na demonstração há segredo de verdade. */
const PROVEDORES_DE_CANAL_SEMENTE: ProvedorDeCanalDaPlataforma[] = [{
    provedor: 'uazapi',
    baseUrl: 'https://demonstracao.uazapi.com',
    temToken: true,
    dica: 'demo',
    ativo: true,
    atualizadoEm: '2026-09-01T12:00:00-03:00',
}];

let provedoresDeCanal: ProvedorDeCanalDaPlataforma[] = copiar(PROVEDORES_DE_CANAL_SEMENTE);
let faturas = copiar(FATURAS_SEMENTE);
let lembretes = copiar(LEMBRETES_SEMENTE);
let fichas: Record<string, FichaDoPaciente> = { ...FICHAS };
let conversas = copiar(CONVERSAS);
let orcamentos = copiar(ORCAMENTOS);
let titulos = copiar(TITULOS);
let fichasClinicas = copiar(FICHAS_CLINICAS);
let modelosDeFicha = copiar(MODELOS_SEMENTE);
/* O relógio da consulta vive aqui enquanto não há tabela: `iniciado_em` e
   `encerrado_em` existem no banco, e o adaptador do servidor usa os dois. */
let estadoDoAtendimento = new Map<string, SituacaoDoAtendimento>();
let iniciosDeAtendimento = new Map<string, string>();
let finaisDeAtendimento = new Map<string, string>();
let movimentacoes = copiar(MOVIMENTACOES);
let categorias = copiar(CATEGORIAS);
/* Hierarquia e situação da categoria vivem fora da semente porque a semente é
   compartilhada com o banco, que já tem colunas para as duas. */
let paisDeCategoria = new Map<string, string | undefined>();
let categoriasInativas = new Set<string>();
let pacotes = copiar(PACOTES);
let mensagens = copiar(MENSAGENS);
let historicos: Record<string, RegistroDeHistorico[]> = {};
let fornecedores = copiar(FORNECEDORES);
/* ---- fornecedores e prestadores nos Contatos (0159) ---- */
let prestadores: PrestadorSemente[] = [];
let tiposDeServico = tiposDeServicoPadrao();
let categoriasDeProduto = copiar(CATEGORIAS_DE_PRODUTO);
let marcas = copiar(MARCAS);
let produtos = copiar(PRODUTOS);
/* O saldo de cada produto é a soma destas linhas, como no banco: não há
   `quantidade` guardada em lugar nenhum para os dois discordarem. */
let movimentosDeEstoque = copiar(MOVIMENTACOES_DE_ESTOQUE);
let reservas = copiarReservas(RESERVAS);
let procedimentos = copiarProcedimentos(PROCEDIMENTOS);
let insumosDosProcedimentos = copiar(INSUMOS_DOS_PROCEDIMENTOS);
let pedidos = copiarPedidos(PEDIDOS);
let modelosDeDocumento = copiar(MODELOS_DE_DOCUMENTO);
let documentos = copiar(DOCUMENTOS_EMITIDOS);
let sequencia = agenda.length;
/* ---- trilha T2 ---- */
/* Exceção da régua por agendamento, `<agendamento>:<lembrete>` → ativo. Sem
   chave, vale a régua — como a tabela `agendamentos_lembretes`. */
let excecoesDeLembrete = new Map<string, boolean>();
/* Conta e presença alteradas na sessão, por e-mail. */
let contasDaDemonstracao = new Map<string, MinhaConta>();
/* O que "Registrar contato" gravou — no banco, `eventos_do_contato`. */
let notasDosContatos: { contatoId: string; texto: string; quandoEm: string }[] = [];
/* Contatos arquivados — no banco, `contatos.arquivado_em`. */
let arquivados = new Set<string>();

function copiarPedidos(lista: PedidoSemente[]): PedidoSemente[] {
    return lista.map((p) => ({ ...p, itens: p.itens.map((i) => ({ ...i })) }));
}

/* Custos extras e campos personalizados são listas dentro do registro, e
   `copiar` é raso: sem copiar as duas, editar um procedimento num teste mexeria
   na semente do teste seguinte. */
function copiarProcedimentos(lista: ProcedimentoSemente[]): ProcedimentoSemente[] {
    return lista.map((p) => ({
        ...p,
        profissionaisIds: [...p.profissionaisIds],
        custosExtras: p.custosExtras.map((c) => ({ ...c })),
        camposPersonalizados: p.camposPersonalizados.map((c) => ({ ...c })),
    }));
}

/* `copiar` é raso, e a reserva tem itens dentro: sem copiar a lista, dar baixa
   num teste mexeria na semente que o teste seguinte usa. */
function copiarReservas(lista: ReservaSemente[]): ReservaSemente[] {
    return lista.map((r) => ({ ...r, itens: r.itens.map((i) => ({ ...i })) }));
}

/**
 * Volta aos dados de origem.
 *
 * O adaptador guarda estado no módulo, então um teste que conclui uma tarefa
 * deixaria a tarefa concluída para o teste seguinte — e a falha apareceria no
 * teste errado.
 */
/* Webhooks na demonstração: cadastro de verdade, em memória; envio nenhum — sem servidor não há borda. */
let webhooksDaDemo: { clinicaId: string; webhook: Webhook }[] = [];
const SEM_ENVIO_DE_WEBHOOK_NA_DEMO = 'A demonstração não envia webhooks: o envio precisa da aplicação ligada ao servidor.';

function clinicaDoGestorDeWebhooks(): string {
    const sessao = atual();
    if (!sessao?.clinicaId || sessao.impersonando || !(sessao.permissoes.includes('*') || sessao.permissoes.includes('configuracoes'))) {
        throw new Error('Só quem configura a clínica gerencia webhooks.');
    }
    return sessao.clinicaId;
}

function segredoDeWebhookDaDemo(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return `whsec_${btoa(String.fromCharCode(...bytes))}`;
}

function conferirWebhookDaDemo(d: DadosDoWebhook): void {
    if (d.nome.trim().length < 3 || d.nome.trim().length > 60) throw new Error('Dê ao webhook um nome de 3 a 60 caracteres.');
    if (!/^https:\/\/[^\s/?#]+\.[^\s/?#]+/.test(d.url.trim())) throw new Error('O webhook precisa usar https:// e um endereço que a internet alcance.');
    if (d.eventos.length === 0) throw new Error('Escolha pelo menos um evento.');
}

export function reiniciarDemonstracao(): void {
    webhooksDaDemo = [];
    enviosAutomaticosDaDemo = false;
    disparosDaDemo = new Map();
    agenda = copiar(AGENDA_SEMENTE);
    tarefas = copiar(TAREFAS_SEMENTE);
    pacientes = copiar(PACIENTES);
    leads = copiar(LEADS);
    reiniciarAnunciosDaDemonstracao(leads);
    funis = copiar(FUNIS_SEMENTE);
    unidadesArquivadas = new Set();
    membros = copiar(MEMBROS);
    folgasDeMembros.clear();
    salas = copiar(SALAS);
    clinica = { ...CLINICA_SEMENTE };
    unidades = copiar(UNIDADES_SEMENTE);
    horarios = HORARIOS_SEMENTE.map((d) => ({ ...d, faixas: copiar(d.faixas) }));
    automacoes = copiar(AUTOMACOES_SEMENTE);
    campanhas = copiar(CAMPANHAS_SEMENTE);
    planos = copiar(PLANOS_SEMENTE);
    chamados = copiar(CHAMADOS_SEMENTE);
    flags = FLAGS_SEMENTE.map((f) => ({ ...f, excecoes: copiar(f.excecoes) }));
    agentes = copiar(AGENTES_SEMENTE);
    fluxosDaClinica = FLUXOS_SEMENTE.map((f) => ({ ...f, passos: JSON.parse(JSON.stringify(f.passos)) }));
    canais = copiar(CANAIS);
    credenciaisDeCanal = new Map();
    destinoDoCanal = new Map();
    senhaDeEmailRecusada = new Set();
    leiturasDoPareamento = new Map();
    anexosDaDemonstracao = new Map();
    orcamentosDaDemonstracao = copiar(ORCAMENTOS_SEMENTE);
    baseDaDemonstracao = copiar(BASE_SEMENTE);
    chavesDeIa = [];
    provedoresDeCanal = copiar(PROVEDORES_DE_CANAL_SEMENTE);
    faturas = copiar(FATURAS_SEMENTE);
    gatewaysDaDemonstracao = [];
    lembretes = copiar(LEMBRETES_SEMENTE);
    fichas = { ...FICHAS };
    conversas = copiar(CONVERSAS);
    mensagens = copiar(MENSAGENS);
    orcamentos = copiar(ORCAMENTOS);
    pacotes = copiar(PACOTES);
    titulos = copiar(TITULOS);
    movimentacoes = copiar(MOVIMENTACOES);
    categorias = copiar(CATEGORIAS);
    paisDeCategoria = new Map();
    categoriasInativas = new Set();
    fichasClinicas = copiar(FICHAS_CLINICAS);
    modelosDeFicha = copiar(MODELOS_SEMENTE);
    estadoDoAtendimento = new Map();
    iniciosDeAtendimento = new Map();
    finaisDeAtendimento = new Map();
    historicos = {};
    fornecedores = copiar(FORNECEDORES);
    categoriasDeProduto = copiar(CATEGORIAS_DE_PRODUTO);
    prestadores = [];
    tiposDeServico = tiposDeServicoPadrao();
    marcas = copiar(MARCAS);
    produtos = copiar(PRODUTOS);
    movimentosDeEstoque = copiar(MOVIMENTACOES_DE_ESTOQUE);
    reservas = copiarReservas(RESERVAS);
    procedimentos = copiarProcedimentos(PROCEDIMENTOS);
    insumosDosProcedimentos = copiar(INSUMOS_DOS_PROCEDIMENTOS);
    pedidos = copiarPedidos(PEDIDOS);
    modelosDeDocumento = copiar(MODELOS_DE_DOCUMENTO);
    documentos = copiar(DOCUMENTOS_EMITIDOS);
    sequencia = agenda.length;
    excecoesDeLembrete = new Map();
    contasDaDemonstracao = new Map();
    notasDosContatos = [];
    arquivados = new Set();
    reiniciarPainelDaDemonstracao();
    /* ---- trilha T5a ---- */
    reiniciarCadastrosDaTrilhaT5a();
    /* ---- trilha B4: brain ---- */
    reiniciarBrainDaDemonstracao();
    reiniciarNotasDaDemonstracao();
    reiniciarNotasFiscaisDaDemonstracao();
}

/** Modelos com a contagem de uso, que é o que o construtor mostra. */
function modelosComUsos(): ModeloDeFicha[] {
    return modelosDeFicha.map((m) => ({
        ...m,
        campos: m.campos.map((c) => ({ ...c })),
        usos: fichasClinicas.filter((f) => f.modeloId === m.id).length,
    }));
}

function somaDiasIso(dia: string, dias: number): string {
    const d = new Date(`${dia}T12:00:00-03:00`);
    d.setDate(d.getDate() + dias);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

function diaDeHoje(): string {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

/** Dia (YYYY-MM-DD) do instante, no fuso da clínica. */
function diaDo(iso: string): string {
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

function segundaDa(dia: string): string {
    const d = new Date(`${dia}T12:00:00-03:00`);
    /* getDay() 0 é domingo; a semana da clínica começa na segunda, e o sábado
       ainda pertence a ela — por isso o domingo recua seis dias, não zero. */
    const passo = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - passo);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

function historicoInicial(id: string): RegistroDeHistorico[] {
    return [
        { id: `${id}-h1`, titulo: 'Agendamento criado', quandoEm: '2026-09-02T09:14:00-03:00', autor: 'Bruna Lima', icone: 'calendar-plus' },
        { id: `${id}-h2`, titulo: 'Confirmação enviada', quandoEm: '2026-09-02T09:15:00-03:00', autor: 'Automático', icone: 'message-circle', tom: 'success', descricao: 'WhatsApp e e-mail' },
    ];
}

/** Sem acento, sem caixa e sem espaço à toa — para comparar nome com nome. */
function mesmoNome(a: string, b: string): boolean {
    const normal = (v: string) => v.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return normal(a) === normal(b);
}

function combina(texto: string, busca: string): boolean {
    /* Sem acento e sem caixa: quem digita "julia" tem de achar "Júlia", e quem
       digita o telefone com pontuação também. */
    const normal = (v: string) => v.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
    return normal(texto).includes(normal(busca));
}

/** A categoria como cadastro: grupo, quantos títulos a usam e se está ativa. */
function categoriaDeConta(c: CategoriaFinanceira): CategoriaDeConta {
    const paiId = paisDeCategoria.get(c.id);
    return {
        ...c,
        paiId,
        grupo: paiId ? categorias.find((x) => x.id === paiId)?.nome : undefined,
        /* O título guarda o NOME da categoria, como vem do banco pelo embed. */
        lancamentos: titulos.filter((t) => t.categoria === c.nome).length,
        ativa: !categoriasInativas.has(c.id),
    };
}

function categoriasDeConta(): CategoriaDeConta[] {
    return categorias.map(categoriaDeConta);
}

function paginar<T>(itens: T[], filtro: FiltroDeContatos) {
    const porPagina = filtro.porPagina ?? 8;
    const paginas = Math.max(1, Math.ceil(itens.length / porPagina));
    const pagina = Math.min(Math.max(filtro.pagina ?? 1, 1), paginas);
    return {
        itens: itens.slice((pagina - 1) * porPagina, pagina * porPagina),
        total: itens.length,
        pagina,
        paginas,
    };
}

function totaisDeContatos() {
    return {
        pacientes: pacientes.filter((p) => !arquivados.has(p.id)).length, leads: leads.length, profissionais: membros.length,
        fornecedores: fornecedores.length, prestadores: prestadores.length,
    };
}

/* ---------------------------------------------------------------- estoque */

/** Saldo do produto: a soma das movimentações, e nada mais. */
function saldoDe(produtoId: string): number {
    return movimentosDeEstoque
        .filter((m) => m.produtoId === produtoId)
        .reduce((s, m) => s + (m.tipo === 'entrada' ? m.quantidade : -m.quantidade), 0);
}

/**
 * O que as reservas abertas comprometem, em EMBALAGENS.
 *
 * A reserva pede em unidade de consumo; somar 50 UI ao saldo de frascos
 * reservaria cinquenta frascos para meio frasco de toxina.
 */
function reservadoDe(p: ProdutoSemente): number {
    return reservas
        .filter((r) => r.status !== 'consumida')
        .flatMap((r) => r.itens.filter((i) => i.produtoId === p.id))
        .reduce((s, i) => s + embalagensDe(p, i.quantidadeConsumo), 0);
}

/* Metade do mínimo é a linha do crítico: abaixo dela não dá para remarcar, dá
   para cancelar. */
function situacaoDe(disponivel: number, minimo: number): SituacaoDoProduto {
    if (disponivel <= 0 || disponivel < minimo * 0.5) return 'critico';
    if (disponivel < minimo) return 'baixo';
    return 'normal';
}

function paraProduto(p: ProdutoSemente): Produto {
    const quantidade = saldoDe(p.id);
    const reservado = reservadoDe(p);
    /* Disponível negativo não é erro de conta: é a reserva que passou do
       estoque, e é exatamente o que a tela precisa mostrar. */
    const disponivel = quantidade - reservado;
    return {
        ...p,
        categoria: categoriasDeProduto.find((c) => c.id === p.categoriaId)?.nome,
        marca: marcas.find((m) => m.id === p.marcaId)?.nome,
        fornecedor: fornecedores.find((f) => f.id === p.fornecedorId)?.nome,
        quantidade,
        reservado,
        disponivel,
        situacao: situacaoDe(disponivel, p.minimo),
    };
}

/** Nenhum canal pareado: a semente nasce com os quatro desconectados. */
function canalConectado(): boolean {
    return canais.some((c) => c.status === 'conectado');
}

/** Pacientes atendidos há mais de 90 dias e sem nada marcado daqui para a frente. */
function semRetorno(dias: number): string[] {
    const hoje = diaDeHoje();
    const limite = new Date(`${hoje}T12:00:00-03:00`);
    limite.setDate(limite.getDate() - dias);
    const corte = limite.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });

    const nomes = new Set<string>();
    for (const p of pacientes) {
        const dele = agenda.filter((a) => a.paciente === p.nome && a.status !== 'bloqueio' && a.status !== 'cancelado');
        /* Consulta cancelada não é visita: contá-la como último atendimento
           esconderia justamente quem marcou e não veio. */
        const ultimo = dele.filter((a) => diaDo(a.inicioEm) <= hoje).map((a) => diaDo(a.inicioEm)).sort().pop();
        const futuro = dele.some((a) => diaDo(a.inicioEm) > hoje);
        if (ultimo && ultimo < corte && !futuro) nomes.add(p.nome);
    }
    return [...nomes];
}

function titulosVencidos(dias: number) {
    const hoje = diaDeHoje();
    const limite = new Date(`${hoje}T12:00:00-03:00`);
    limite.setDate(limite.getDate() - dias);
    const corte = limite.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
    return titulos.filter((t) => t.tipo === 'receber' && t.vencimento <= corte && t.pagoCentavos < t.valorCentavos);
}

function produtosAbaixoDoMinimo() {
    return produtos.map(paraProduto).filter((p) => p.situacao !== 'normal');
}

function paraChamado(c: ChamadoSemente): Chamado {
    const clinica = CLINICAS_DA_PLATAFORMA.find((x) => x.id === c.clinicaId);
    const horas = horasAteResolver(c);
    return {
        ...c,
        clinica: clinica?.nome ?? '—',
        ...(horas === undefined ? {} : { horasAteResolver: horas }),
    };
}

/** A mesma conferência do gatilho `confere_destino` da 0161. */
function conferirDestinoDoCanal(dados: DadosDoCanal): { filaPadraoId?: string; atendentePadraoId?: string } {
    const fila = dados.filaPadraoId ? filasDeAtendimento.find((f) => f.id === dados.filaPadraoId) : undefined;
    if (dados.filaPadraoId && !fila?.ativa) throw new Error('Esta fila está desativada: escolha outra.');
    if (fila && dados.atendentePadraoId && !fila.membrosIds.includes(dados.atendentePadraoId)) {
        throw new Error('Essa pessoa não atende a fila escolhida.');
    }
    return {
        ...(dados.filaPadraoId ? { filaPadraoId: dados.filaPadraoId } : {}),
        ...(dados.atendentePadraoId ? { atendentePadraoId: dados.atendentePadraoId } : {}),
    };
}

function paraCanalDeIntegracao(c: Canal): CanalDeIntegracao {
    const cred = credenciaisDeCanal.get(c.id);
    return {
        ...c,
        ...destinoDoCanal.get(c.id),
        temCredencial: Boolean(cred),
        ...(cred?.criadoEm ? { credencialEm: cred.criadoEm } : {}),
        ...(cred?.rotacionadoEm ? { credencialRotacionadaEm: cred.rotacionadoEm } : {}),
    };
}

/** A assinatura de cada clínica, com o plano por trás. */
function assinaturasDaBase(): AssinaturaDaClinica[] {
    return CLINICAS_DA_PLATAFORMA.map((c): AssinaturaDaClinica => {
        const plano = planos.find((p) => p.codigo === c.plano) ?? planos[0];
        return {
            clinicaId: c.id,
            clinica: c.nome,
            /* A situação é a da CLÍNICA — não existe uma segunda para a
               assinatura discordar dela. */
            situacao: c.situacao,
            plano: plano?.nome ?? c.plano,
            planoCodigo: plano?.codigo ?? c.plano,
            ciclo: plano?.ciclo ?? 'mensal',
            ...(plano?.precoCentavos === undefined ? {} : { precoCentavos: plano.precoCentavos }),
            inicio: c.criadoEm,
            ...(c.situacao === 'cancelada' ? { canceladaEm: c.criadoEm } : { proximaCobranca: '2026-10-10' }),
        };
    });
}

function paraFatura(f: FaturaSemente): Fatura {
    const clinica = CLINICAS_DA_PLATAFORMA.find((c) => c.id === f.clinicaId);
    return {
        ...f,
        clinica: clinica?.nome ?? '—',
        situacao: situacaoDaFatura(f),
    };
}

/** A equipe de todas as clínicas, como a plataforma a enxerga. */
function usuariosDaPlataforma(): UsuarioNaPlataforma[] {
    return membros.map((m): UsuarioNaPlataforma => ({
        id: m.id,
        nome: m.nome,
        email: `${m.nome.split(' ')[0]?.toLowerCase()}@clinicavecchia.com.br`,
        papel: m.papel,
        acesso: m.acesso,
        clinica: 'Vecchia — Jardins',
        temLogin: m.acesso === 'ativo',
        ...(m.acesso === 'ativo' ? { ultimoAcesso: '2026-09-08T09:12:00-03:00' } : {}),
    }));
}

/* A régua se lê de trás para a frente: o mais distante do atendimento
   primeiro. Ordenar pelo texto poria "15 minutos" antes de "1 dia". */
function ordenarRegua(lista: Lembrete[]): Lembrete[] {
    return [...lista].sort((a, b) => b.offsetMin - a.offsetMin).map((l) => ({ ...l }));
}

/** Quantas pessoas cada regra de público alcança agora. */
function tamanhoDoPublico(tipo: PublicoDaCampanha, param: Campanha['publicoParam']): number {
    if (tipo === 'sem-retorno') return semRetorno(param.dias ?? 90).length;
    if (tipo === 'aniversariantes') {
        const mes = diaDeHoje().slice(5, 7);
        return pacientes.filter((p) => p.nascimento?.slice(5, 7) === mes).length;
    }
    if (tipo === 'por-origem') {
        return leads.filter((l) => l.origem === param.origem && l.situacao === 'aberta').length;
    }
    return pacientes.length;
}

/** O que cada origem trouxe no mês corrente. */
function origensDoMes(): LinhaDeOrigem[] {
    const mes = diaDeHoje().slice(0, 7);
    const doMes = leads.filter((l) => l.criadoEm.startsWith(mes));
    const porOrigem = new Map<string, LinhaDeOrigem>();

    for (const l of doMes) {
        const origem = l.origem || 'Sem origem';
        const atual = porOrigem.get(origem)
            ?? { origem, leads: 0, ganhos: 0, receitaCentavos: 0, investimentoCentavos: 0 };
        atual.leads += 1;
        if (l.situacao === 'ganho') {
            atual.ganhos += 1;
            atual.receitaCentavos += l.valorEstimadoCentavos;
        }
        porOrigem.set(origem, atual);
    }

    for (const c of campanhas) {
        if (!c.origemLead || !c.investimentoCentavos) continue;
        const atual = porOrigem.get(c.origemLead)
            ?? { origem: c.origemLead, leads: 0, ganhos: 0, receitaCentavos: 0, investimentoCentavos: 0 };
        atual.investimentoCentavos += c.investimentoCentavos;
        porOrigem.set(c.origemLead, atual);
    }

    return [...porOrigem.values()].sort((a, b) => b.leads - a.leads);
}

/** A campanha com público e resultado calculados agora. */
function paraCampanha(c: CampanhaSemente): Campanha {
    /* O resultado sai dos leads da origem declarada, DENTRO do período da
       campanha: sem origem não há atribuição, e a tela diz isso em vez de
       mostrar zero como se ninguém tivesse chegado. */
    const doPeriodo = c.origemLead
        ? leads.filter((l) => l.origem === c.origemLead
            && (!c.inicio || l.criadoEm >= c.inicio)
            && (!c.fim || l.criadoEm <= c.fim))
        : [];
    const ganhos = doPeriodo.filter((l) => l.situacao === 'ganho');
    const receitaCentavos = ganhos.reduce((s, l) => s + l.valorEstimadoCentavos, 0);

    return {
        ...c,
        publicoTamanho: tamanhoDoPublico(c.publicoTipo, c.publicoParam),
        leads: doPeriodo.length,
        ganhos: ganhos.length,
        receitaCentavos,
        ...derivadosDaCampanha({ investimentoCentavos: c.investimentoCentavos, leads: doPeriodo.length, receitaCentavos }),
    };
}

/** Quantos registros a regra alcança agora; evento não tem fila. */
function alcanceDaAutomacao(gatilho: LinhaDeAutomacao['gatilho']): number | undefined {
    if (GATILHOS[gatilho].evento) return undefined;
    if (gatilho === 'agendamento-24h') {
        const agora = Date.now();
        return agenda.filter((a) => {
            const inicio = new Date(a.inicioEm).getTime();
            return a.status !== 'bloqueio' && a.status !== 'cancelado'
                && inicio > agora && inicio <= agora + 24 * 60 * 60 * 1000;
        }).length;
    }
    if (gatilho === 'titulo-vencido') return titulosVencidos(3).length;
    if (gatilho === 'sem-retorno-90d') return semRetorno(90).length;
    return produtosAbaixoDoMinimo().length;
}

/** O que cada regra de ação interna escreve como tarefa. */
function titulosDeTarefa(gatilho: LinhaDeAutomacao['gatilho']): string[] {
    if (gatilho === 'titulo-vencido') {
        return titulosVencidos(3).map((t) => `Cobrar ${t.contraparte} — ${t.descricao}`);
    }
    if (gatilho === 'estoque-minimo') {
        return produtosAbaixoDoMinimo().map((p) => `Repor ${p.nome} — abaixo do mínimo`);
    }
    return [];
}

function produtoOuErro(id: string): ProdutoSemente {
    const p = produtos.find((x) => x.id === id);
    if (!p) throw new Error('Produto não encontrado.');
    return p;
}

function paraFornecedor(f: (typeof fornecedores)[number]): Fornecedor {
    const categoria = f.categoriaId ? categorias.find((c) => c.id === f.categoriaId)?.nome : undefined;
    return {
        ...f,
        marcas: marcas.filter((m) => m.fornecedorId === f.id).map((m) => m.nome),
        ...(categoria ? { categoria } : {}),
    };
}

interface PrestadorSemente {
    id: string;
    nome: string;
    documento?: string;
    telefone: string;
    email?: string;
    tipoServicoId?: string;
    categoriaId?: string;
    observacoes?: string;
    ativo: boolean;
}

/** Os quatro tipos com que toda clínica nasce, como a `app.semear_tipos_de_servico` (0159). */
function tiposDeServicoPadrao(): { id: string; nome: string; ativo: boolean }[] {
    return ['Eletricista', 'Pintor', 'Manutenção', 'Equipamento'].map((nome, i) => ({ id: `tsv-${i + 1}`, nome, ativo: true }));
}

function paraPrestador(p: PrestadorSemente): PrestadorDeServico {
    const tipoServico = tiposDeServico.find((t) => t.id === p.tipoServicoId)?.nome;
    const categoria = p.categoriaId ? categorias.find((c) => c.id === p.categoriaId)?.nome : undefined;
    return { ...p, ...(tipoServico ? { tipoServico } : {}), ...(categoria ? { categoria } : {}) };
}

function conferirCategoriaDeCadastro(categoriaId: string | null | undefined): void {
    if (categoriaId && !categorias.some((c) => c.id === categoriaId)) throw new Error('Categoria de conta não encontrada nesta clínica.');
}

function paraMarca(m: (typeof marcas)[number]): Marca {
    return {
        ...m,
        fornecedor: fornecedores.find((f) => f.id === m.fornecedorId)?.nome,
        produtos: produtos.filter((p) => p.marcaId === m.id).length,
    };
}

function paraCategoriaDeProduto(c: (typeof categoriasDeProduto)[number]): CategoriaDeProduto {
    const daCategoria = produtos.filter((p) => p.categoriaId === c.id);
    return {
        ...c,
        produtos: daCategoria.length,
        valorEmEstoqueCentavos: daCategoria.reduce((s, p) => s + p.custoCentavos * saldoDe(p.id), 0),
    };
}

function paraMovimentacao(m: (typeof movimentosDeEstoque)[number]): MovimentacaoDeEstoque {
    const p = produtos.find((x) => x.id === m.produtoId);
    return {
        id: m.id,
        produtoId: m.produtoId,
        produto: p?.nome ?? '—',
        tipo: m.tipo,
        quantidade: m.quantidade,
        unidade: p?.unidade ?? 'und',
        quantidadeConsumo: m.quantidadeConsumo,
        unidadeConsumo: m.unidadeConsumo,
        origem: m.origem,
        ocorridoEm: m.ocorridoEm,
    };
}

/* ------------------------------------------------------------ documentos */

function paraModeloDeDocumento(m: ModeloSemente): ModeloDeDocumento {
    return {
        ...m,
        /* Uso é contagem do que saiu dele, não contador guardado: contador que
           só sobe fica errado no primeiro documento excluído. */
        usos: documentos.filter((d) => d.modeloId === m.id).length,
        campos: contarCampos(m.corpo),
    };
}

function paraDocumento(d: DocumentoSemente): Documento {
    return { ...d };
}

/** A mesma emissão, na forma reduzida que a ficha do paciente mostra. */
function paraDocumentoDoPaciente(d: DocumentoSemente): DocumentoDoPaciente {
    return {
        id: d.id,
        titulo: d.titulo,
        tipo: d.tipo,
        pacienteId: d.contatoId,
        atualizadoEm: d.atualizadoEm,
        status: d.status,
        paginas: d.paginas,
    };
}

/** Aguardando assinatura há mais de 48 horas. */
function paradosNaAssinatura(agora: number): DocumentoSemente[] {
    return documentos.filter((d) => d.status === 'aguardando'
        && d.enviadoEm !== undefined
        && agora - new Date(d.enviadoEm).getTime() > 48 * 3600 * 1000);
}

/* --------------------------------------------------------------- compras */

function paraPedido(p: PedidoSemente): PedidoDeCompra {
    const itens: ItemDoPedido[] = p.itens.map((i) => {
        const produto = produtos.find((x) => x.id === i.produtoId);
        return {
            id: i.id,
            produtoId: i.produtoId,
            produto: produto?.nome ?? '—',
            unidade: produto?.unidade ?? 'und',
            quantidade: i.quantidade,
            quantidadeRecebida: i.quantidadeRecebida,
            custoUnitarioCentavos: i.custoUnitarioCentavos,
            custoAtualCentavos: produto?.custoCentavos ?? i.custoUnitarioCentavos,
        };
    });
    return {
        id: p.id,
        numero: p.numero,
        fornecedorId: p.fornecedorId,
        fornecedor: fornecedores.find((f) => f.id === p.fornecedorId)?.nome ?? '—',
        status: p.status,
        previsaoEntrega: p.previsaoEntrega,
        observacoes: p.observacoes,
        criadoEm: p.criadoEm,
        ...(p.criadoPor ? { criadoPor: p.criadoPor } : {}),
        enviadoEm: p.enviadoEm,
        recebidoEm: p.recebidoEm,
        itens,
        totalCentavos: itens.reduce((s, i) => s + i.quantidade * i.custoUnitarioCentavos, 0),
        recebidoCentavos: itens.reduce((s, i) => s + i.quantidadeRecebida * i.custoUnitarioCentavos, 0),
    };
}

/**
 * O que repor, por fornecedor.
 *
 * A sugestão leva ao DOBRO do mínimo, e não ao mínimo: comprar exatamente o
 * mínimo faz o alerta voltar na primeira saída. Embalagem inteira, porque é
 * assim que o fornecedor vende.
 */
function sugestoesDeCompra(): SugestaoDeCompra[] {
    const abaixo = produtos.map(paraProduto).filter((p) => p.situacao !== 'normal');
    const porFornecedor = new Map<string, SugestaoDeCompra>();

    for (const produto of abaixo) {
        const chave = produto.fornecedorId ?? 'sem-fornecedor';
        const sugestao = Math.max(1, Math.ceil(produto.minimo * 2 - produto.disponivel));
        const grupo = porFornecedor.get(chave) ?? {
            fornecedorId: produto.fornecedorId,
            fornecedor: produto.fornecedor ?? 'Sem fornecedor',
            itens: [],
            totalCentavos: 0,
        };
        grupo.itens.push({
            produtoId: produto.id,
            produto: produto.nome,
            unidade: produto.unidade,
            disponivel: produto.disponivel,
            minimo: produto.minimo,
            sugestao,
            custoUnitarioCentavos: produto.custoCentavos,
            situacao: produto.situacao,
        });
        grupo.totalCentavos += sugestao * produto.custoCentavos;
        porFornecedor.set(chave, grupo);
    }

    return [...porFornecedor.values()].sort((a, b) => b.totalCentavos - a.totalCentavos);
}

/* ---------------------------------------------------------- procedimentos */

/**
 * O insumo com a conta já feita: custo pela unidade de CONSUMO, quanto isso é
 * em embalagem e como está o saldo do produto hoje.
 */
function paraInsumo(i: InsumoSemente): InsumoDoProcedimento | null {
    const semente = produtos.find((p) => p.id === i.produtoId);
    if (!semente) return null;
    const produto = paraProduto(semente);
    return {
        produtoId: produto.id,
        produto: produto.nome,
        quantidade: i.quantidade,
        unidadeConsumo: produto.unidadeConsumo,
        /* Pela unidade de consumo: multiplicar o custo da embalagem pela
           quantidade consumida inflaria o custo em até cem vezes. */
        custoCentavos: custoDeConsumoCentavos(produto) * i.quantidade,
        embalagens: embalagensDe(produto, i.quantidade),
        unidade: produto.unidade,
        disponivel: produto.disponivel,
        situacao: produto.situacao,
    };
}

function paraProcedimento(p: ProcedimentoSemente): Procedimento {
    const insumos = insumosDosProcedimentos
        .filter((i) => i.procedimentoId === p.id)
        .map(paraInsumo)
        .filter((i): i is InsumoDoProcedimento => i !== null);

    const custoDeInsumosCentavos = insumos.reduce((s, i) => s + i.custoCentavos, 0);
    const custoExtraCentavos = p.custosExtras.reduce((s, c) => s + c.quantidade * c.valorCentavos, 0);
    const custo = custoDeInsumosCentavos + custoExtraCentavos;

    return {
        ...p,
        profissionais: p.profissionaisIds
            .map((id) => membros.find((m) => m.id === id))
            .filter((m): m is MembroDaEquipe => !!m)
            .map((m) => ({ id: m.id, nome: m.nome })),
        insumos,
        custosExtras: p.custosExtras.map((c) => ({ ...c })),
        camposPersonalizados: p.camposPersonalizados.map((c) => ({ ...c })),
        custoDeInsumosCentavos,
        custoExtraCentavos,
        margem: p.precoCentavos === 0 ? 0 : Math.round(((p.precoCentavos - custo) / p.precoCentavos) * 100),
        /* Quantas execuções o estoque cobre: o insumo mais escasso manda. Sem
           insumo, não há trava — por isso `null`, e não zero nem infinito. */
        execucoesPossiveis: insumos.length === 0
            ? null
            : Math.max(0, Math.min(...insumos.map((i) => {
                const semente = produtos.find((x) => x.id === i.produtoId);
                /* Piso em zero: disponível negativo — reserva além do estoque —
                   daria "-1 execuções", que não é número que alguém leia. */
                return semente ? Math.floor(paraProduto(semente).disponivel / (i.embalagens || 1)) : 0;
            }))),
    };
}

function paraReserva(r: ReservaSemente): ReservaDeEstoque {
    return {
        ...r,
        itens: r.itens.map((i) => ({
            ...i,
            produto: produtos.find((p) => p.id === i.produtoId)?.nome ?? '—',
        })),
    };
}

export const adaptadorDeDemonstracao: PortaDeDados = {
    async resumoDoDia(): Promise<ResumoDoDia> {
        return {
            indicadores: [
                { id: 'faturamento', rotulo: 'Faturamento do mês', valor: 'R$ 148.320', variacao: '+12,4%', tendencia: 'up', apoio: 'vs. agosto', icone: 'circle-dollar-sign' },
                { id: 'a-receber', rotulo: 'A receber', valor: 'R$ 23.900', variacao: '18 títulos', tendencia: 'neutral', apoio: 'R$ 4.190 vencidos', icone: 'receipt' },
                { id: 'atendimentos', rotulo: 'Atendimentos', valor: '312', variacao: '+8,1%', tendencia: 'up', apoio: 'setembro', icone: 'stethoscope' },
                { id: 'conversao', rotulo: 'Taxa de conversão', valor: '34%', variacao: '-2,3%', tendencia: 'down', apoio: 'CRM · últimos 30 dias', icone: 'target' },
            ],
            /* Só o dia de hoje, e sem bloqueio: bloqueio é ausência de
               atendimento, e contá-lo inflaria "atendimentos previstos". */
            agendaDeHoje: copiar(agenda.filter((a) => diaDo(a.inicioEm) === HOJE && a.status !== 'bloqueio')),
            tarefas: copiar(tarefas),
            sugestao: {
                id: 'reativacao',
                texto: '3 pacientes sem retorno há mais de 90 dias. Posso preparar uma campanha de reativação no WhatsApp.',
                moduloDestino: 'marketing',
            },
            onboarding: {
                etapasConcluidas: 4,
                etapasTotais: 5,
                proximaEtapa: 'assinar um documento',
                moduloDaProximaEtapa: 'documentos',
            },
        };
    },

    async agendaDaSemana(diaDeReferencia: string): Promise<AgendaDaSemana> {
        const inicio = segundaDa(diaDeReferencia);
        const fim = new Date(`${inicio}T12:00:00-03:00`);
        fim.setDate(fim.getDate() + 5);
        const ultimo = fim.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });

        const recorte = recorteDaDemonstracao();
        return {
            inicio,
            agendamentos: copiar(agenda.filter((a) => {
                const dia = diaDo(a.inicioEm);
                return dia >= inicio && dia <= ultimo && agendamentoNoRecorte(a, recorte);
            })),
            profissionais: copiar(PROFISSIONAIS),
            /* Sala desativada não recebe agendamento: oferecê-la na grade
               convidaria a marcar onde a clínica já não atende. As salas da
               demonstração não têm unidade: são da principal. */
            salas: recorte?.principal === false ? [] : salas.filter((s) => s.ativa).map((s) => ({ id: s.id, nome: s.nome })),
            horaInicial: 8,
            horaFinal: 18,
        };
    },

    async historicoDoAgendamento(id: string): Promise<RegistroDeHistorico[]> {
        historicos[id] ??= historicoInicial(id);
        return copiar(historicos[id] ?? []);
    },

    async lembretesDoAgendamento(id: string): Promise<Lembrete[]> {
        return ordenarRegua(lembretes).map((l) => {
            const excecao = excecoesDeLembrete.get(`${id}:${l.id}`);
            return excecao === undefined ? l : { ...l, ativo: excecao };
        });
    },

    async atualizarAgendamento(id: string, mudancas: EdicaoDeAgendamento, registros: RegistroDeHistorico[]): Promise<Agendamento> {
        const atual = agenda.find((a) => a.id === id);
        if (!atual) throw new Error(`Agendamento ${id} não existe.`);
        /* O mesmo contrato do servidor: id resolve para o nome do cadastro; nome
           sem id precisa existir; `salaId: null` tira a sala. */
        const { profissionalId, salaId, procedimentoId, observacoes, profissional, sala, procedimento, ...resto } = mudancas;
        const comNomes: Partial<Agendamento> = { ...resto };
        if (profissionalId) comNomes.profissional = PROFISSIONAIS.find((p) => p.id === profissionalId)?.nome ?? naoCadastrado('Profissional');
        else if (profissional) comNomes.profissional = unicoPorNome(PROFISSIONAIS, profissional, 'profissional').nome;
        if (salaId !== undefined) comNomes.sala = salaId === null ? undefined : (salas.find((s) => s.id === salaId)?.nome ?? naoCadastrado('Sala'));
        else if (sala) comNomes.sala = unicoPorNome(salas, sala, 'sala').nome;
        if (procedimentoId) comNomes.procedimento = procedimentos.find((p) => p.id === procedimentoId)?.nome ?? naoCadastrado('Procedimento');
        else if (procedimento) comNomes.procedimento = unicoPorNome(procedimentos, procedimento, 'procedimento').nome;
        if (observacoes !== undefined) comNomes.observacoes = observacoes || undefined;

        Object.assign(atual, comNomes);
        historicos[id] = [...registros, ...(historicos[id] ?? historicoInicial(id))];
        return { ...atual };
    },

    async alternarTarefa(id: string, concluida: boolean): Promise<Tarefa> {
        const tarefa = tarefas.find((t) => t.id === id);
        if (!tarefa) throw new Error(`Tarefa ${id} não existe.`);
        tarefa.concluida = concluida;
        return { ...tarefa };
    },

    async contatos(tipo: TipoDeContato, filtro: FiltroDeContatos): Promise<CargaDeContatos> {
        const busca = filtro.busca?.trim() ?? '';
        const totais = totaisDeContatos();

        if (tipo === 'lead') {
            const encontrados = leads.filter((l) => !busca || combina(l.nome, busca) || combina(l.telefone, busca)
                || l.etiquetas.some((e) => combina(e.nome, busca)));
            return { tipo, ...paginar(copiar(encontrados), filtro), totais };
        }
        if (tipo === 'fornecedor') {
            const encontrados = fornecedores
                .filter((f) => !busca || combina(f.nome, busca) || combina(f.telefone, busca) || combina(f.cnpj, busca))
                .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
                .map(paraFornecedor);
            return { tipo, ...paginar(encontrados, filtro), totais };
        }
        if (tipo === 'prestador') {
            const encontrados = prestadores
                .map(paraPrestador)
                .filter((p) => !busca || combina(p.nome, busca) || combina(p.telefone, busca) || combina(p.tipoServico ?? '', busca))
                .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
            return { tipo, ...paginar(encontrados, filtro), totais };
        }
        if (tipo === 'profissional') {
            const encontrados = membros.filter((m) => !busca || combina(m.nome, busca) || combina(m.telefone, busca) || combina(m.papel, busca));
            return { tipo, ...paginar(copiar(encontrados), filtro), totais };
        }
        /* Acha por etiqueta também: a tela tem caixa de busca e nenhuma barra de filtros,
           e quem digita "VIP" quer os marcados como VIP. Igual ao adaptador do Supabase. */
        const encontrados = pacientes.filter((p) => !arquivados.has(p.id)
            && (!busca || combina(p.nome, busca) || combina(p.telefone, busca) || p.etiquetas.some((e) => combina(e.nome, busca))));
        return { tipo: 'paciente', ...paginar(copiar(encontrados), filtro), totais, convenios: copiar(CONVENIOS) };
    },

    async criarContato(novo: NovoContato): Promise<{ id: string }> {
        const nome = novo.nome.trim();
        if (!nome) throw new Error('O nome é obrigatório.');
        /* Fornecedor e prestador têm cadastro próprio: salvarFornecedor e salvarPrestador. */
        if (novo.tipo === 'fornecedor' || novo.tipo === 'prestador') throw new Error('Fornecedor e prestador se cadastram pelo cadastro próprio.');
        /* O responsável vem por id do cadastro. A lista fixa gravava "Ana Prado"
           num lugar e "Dra. Ana Prado" noutro, e nunca batia com a equipe. */
        const responsavel = novo.profissionalId
            ? (PROFISSIONAIS.find((p) => p.id === novo.profissionalId)?.nome ?? naoCadastrado('Responsável'))
            : undefined;
        if (novo.tipo === 'profissional' && !novo.email?.trim()) {
            throw new Error('O e-mail é obrigatório: é com ele que a pessoa cria a conta e entra.');
        }

        sequencia += 1;
        const id = `${novo.tipo[0]}-novo-${sequencia}`;
        if (novo.tipo === 'lead') {
            /* Lead novo entra na primeira etapa aberta do pipeline padrão, como o
               gatilho da 0100 faz no banco. */
            const padrao = funilPadrao(funis);
            const entrada = padrao ? etapasAtivas(padrao).find((e) => e.tipo === 'aberta') : undefined;
            if (!padrao || !entrada) throw new Error('A clínica não tem pipeline com etapa aberta para receber o lead.');
            leads.unshift({
                /* O contato nasce junto com o lead, como no banco, onde
                   `leads.contato_id` é obrigatório. */
                id, contatoId: `c-novo-${sequencia}`, nome, telefone: novo.telefone,
                origem: novo.origem?.trim() || 'Cadastro manual', interesse: novo.interesse?.trim() || '—',
                responsavel: responsavel ?? '—', criadoEm: diaDeHoje(),
                funilId: padrao.id, etapaId: entrada.id, etapa: entrada.titulo, situacao: 'aberta',
                valorEstimadoCentavos: novo.valorEstimadoCentavos ?? 0,
                etiquetas: [],
            });
        } else if (novo.tipo === 'profissional') {
            membros.unshift({
                id, nome, telefone: novo.telefone, papel: '—', especialidades: novo.especialidades?.trim() || '—',
                ...(novo.registro?.trim() ? { registro: novo.registro.trim() } : {}),
                acesso: 'convite-pendente',
            });
        } else {
            pacientes.unshift({
                id, nome, telefone: novo.telefone, nascimento: novo.nascimento ?? '',
                profissional: responsavel ?? '', situacao: 'ativo', emAbertoCentavos: 0,
                etiquetas: [],
            });
        }
        return { id };
    },

    async atualizarLead(id: string, mudancas: EdicaoDeLead): Promise<Lead> {
        const lead = leads.find((l) => l.id === id);
        if (!lead) throw new Error(`Lead ${id} não existe.`);
        const { responsavelId, etapaId, situacao, campanhaDeAnuncioId, ...resto } = mudancas;
        if (resto.nome !== undefined && !resto.nome.trim()) throw new Error('O nome não pode ficar vazio.');
        if (campanhaDeAnuncioId !== undefined) aplicarCampanhaNoLead(lead, campanhaDeAnuncioId);
        /* A etapa pelo id, ou a final pela situação — as regras do gatilho da
           0100: etapa arquivada não recebe lead. */
        const destino = etapaId
            ? etapaDaDemonstracao(etapaId)
            : situacao ? funis.find((f) => f.id === lead.funilId)?.etapas.find((e) => e.tipo === situacao) : undefined;
        if ((etapaId || situacao) && !destino) throw new Error('Etapa não encontrada nesta clínica.');
        if (destino?.arquivada) throw new Error(`A etapa "${destino.titulo}" está arquivada.`);
        Object.assign(lead, resto);
        if (destino) Object.assign(lead, { funilId: destino.funilId, etapaId: destino.id, etapa: destino.titulo, situacao: destino.tipo });
        if (responsavelId !== undefined) {
            lead.responsavel = responsavelId
                ? (PROFISSIONAIS.find((p) => p.id === responsavelId)?.nome ?? naoCadastrado('Responsável'))
                : '—';
        }
        return { ...lead };
    },

    async avancarEtapaDoLead(id: string): Promise<Lead> {
        const lead = leads.find((l) => l.id === id);
        if (!lead) throw new Error(`Lead ${id} não existe.`);
        const funil = funis.find((f) => f.id === lead.funilId);
        const proxima = funil ? proximaEtapa(funil, lead.etapaId) : undefined;
        /* Lead perdido ou já ganho não avança: a etapa seguinte não existe, e
           inventar uma esconderia o estado real do funil. */
        if (!proxima) throw new Error('Este lead não tem próxima etapa.');
        Object.assign(lead, { etapaId: proxima.id, etapa: proxima.titulo, situacao: proxima.tipo });
        return { ...lead };
    },

    async atualizarMembro(id: string, mudancas: EdicaoDeMembro): Promise<MembroDaEquipe> {
        const membro = membros.find((m) => m.id === id);
        if (!membro) throw new Error(`Membro ${id} não existe.`);
        Object.assign(membro, mudancas);
        return { ...membro };
    },

    async criarTarefa(dados: { titulo: string; venceEm?: string; responsavel?: string; prioridade?: PrioridadeDaTarefa }): Promise<Tarefa> {
        const titulo = dados.titulo.trim();
        if (!titulo) throw new Error('A tarefa precisa de um título.');
        const nova: Tarefa = {
            id: `t-${Date.now()}`,
            titulo,
            venceEm: dados.venceEm ?? diaDeHoje(),
            responsavel: dados.responsavel ?? 'Recepção',
            prioridade: dados.prioridade ?? 'media',
            concluida: false,
        };
        tarefas.unshift(nova);
        return { ...nova };
    },

    async atendimentos(filtro: FiltroDeAtendimentos): Promise<CargaDeAtendimentos> {
        const agora = new Date();
        const hoje = diaDeHoje();

        const recorte = recorteDaDemonstracao();
        const todos: Atendimento[] = agenda
            .filter((a) => a.status !== 'bloqueio' && agendamentoNoRecorte(a, recorte))
            .map((a) => {
                const inicio = new Date(a.inicioEm).getTime();
                const fim = inicio + a.duracaoMin * 60_000;
                const emCurso = agora.getTime() >= inicio && agora.getTime() <= fim;
                /* "Em atendimento" é derivado do relógio, não gravado: um campo
                   de estado que ninguém atualiza fica preso em "em atendimento"
                   para sempre. */
                const situacao: SituacaoDoAtendimento = a.status === 'cancelado' ? 'cancelado'
                    : a.status === 'faltou' ? 'faltou'
                        : a.status === 'concluido' ? 'concluido'
                            : emCurso ? 'em-atendimento' : 'aguardando';
                return {
                    id: `at-${a.id}`,
                    agendamentoId: a.id,
                    pacienteId: a.pacienteId,
                    paciente: a.paciente,
                    procedimento: a.procedimento,
                    profissional: a.profissional,
                    inicioEm: a.inicioEm,
                    duracaoMin: a.duracaoMin,
                    sala: a.sala,
                    situacao,
                    valorCentavos: PRECOS[a.procedimento] ?? 0,
                    fichas: FICHAS_DO_ATENDIMENTO[a.id] ?? [],
                };
            })
            .sort((x, y) => x.inicioEm.localeCompare(y.inicioEm));

        const noPeriodo = (a: Atendimento): boolean => {
            const dia = diaDo(a.inicioEm);
            if (filtro.periodo === 'dia') return dia === filtro.referencia;
            if (filtro.periodo === 'semana') {
                const inicio = segundaDa(filtro.referencia);
                const fim = new Date(`${inicio}T12:00:00-03:00`);
                fim.setDate(fim.getDate() + 6);
                return dia >= inicio && dia <= fim.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
            }
            return dia.slice(0, 7) === filtro.referencia.slice(0, 7);
        };

        const busca = filtro.busca?.trim() ?? '';
        const filtrados = todos.filter((a) => {
            if (!noPeriodo(a)) return false;
            if (filtro.aba === 'em-aberto' && (a.situacao === 'concluido' || a.situacao === 'cancelado')) return false;
            if (filtro.profissional && a.profissional !== filtro.profissional) return false;
            if (busca && !combina(a.paciente, busca) && !combina(a.procedimento, busca)) return false;
            return true;
        });

        const doMes = todos.filter((a) => diaDo(a.inicioEm).slice(0, 7) === hoje.slice(0, 7));
        const concluidos = doMes.filter((a) => a.situacao === 'concluido');
        const faturado = concluidos.reduce((soma, a) => soma + a.valorCentavos, 0);

        return {
            ...paginar(filtrados, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 8 }),
            indicadores: [
                { id: 'realizados', rotulo: 'Realizados no mês', valor: String(concluidos.length), variacao: '+8,1%', tendencia: 'up', icone: 'stethoscope' },
                { id: 'em-aberto', rotulo: 'Em aberto', valor: String(todos.filter((a) => a.situacao === 'aguardando' || a.situacao === 'em-atendimento').length), apoio: 'aguardando registro', icone: 'clipboard-list' },
                {
                    id: 'ticket', rotulo: 'Ticket médio',
                    valor: concluidos.length ? `R$ ${(faturado / concluidos.length / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'R$ 0,00',
                    apoio: 'no mês', icone: 'circle-dollar-sign',
                },
                { id: 'sem-ficha', rotulo: 'Sem ficha preenchida', valor: String(concluidos.filter((a) => a.fichas.length === 0).length), apoio: 'consultas concluídas', icone: 'clipboard-pen' },
            ],
            totais: {
                hoje: todos.filter((a) => diaDo(a.inicioEm) === hoje).length,
                emAberto: todos.filter((a) => a.situacao === 'aguardando' || a.situacao === 'em-atendimento').length,
                todos: todos.length,
            },
        };
    },

    async atendimentoEmCurso(id: string): Promise<AtendimentoEmCurso> {
        /* O id do atendimento derivado é `at-<id do agendamento>`, como a lista
           de Consultas monta. */
        const agendamentoId = id.startsWith('at-') ? id.slice(3) : id;
        const agendamento = agenda.find((a) => a.id === agendamentoId);
        if (!agendamento) throw new Response('Consulta não encontrada.', { status: 404, statusText: 'Consulta não encontrada' });

        const ficha = fichas[agendamento.pacienteId];
        return {
            id,
            agendamentoId: agendamento.id,
            contatoId: agendamento.pacienteId,
            paciente: agendamento.paciente,
            nascimento: ficha?.nascimento,
            procedimento: agendamento.procedimento,
            profissional: agendamento.profissional,
            sala: agendamento.sala,
            previstoPara: agendamento.inicioEm,
            duracaoPrevistaMin: agendamento.duracaoMin,
            situacao: estadoDoAtendimento.get(id) ?? (agendamento.status === 'concluido' ? 'concluido' : 'aguardando'),
            iniciadoEm: iniciosDeAtendimento.get(id),
            encerradoEm: finaisDeAtendimento.get(id),
            alergias: ficha?.alergias || undefined,
            fichasDaConsulta: copiar(fichasClinicas.filter((f) => f.atendimentoId === id)),
            modelos: modelosComUsos(),
            historico: copiar(fichasClinicas.filter((f) => f.contatoId === agendamento.pacienteId && f.atendimentoId !== id)),
        };
    },

    async iniciarAtendimento(id: string): Promise<AtendimentoEmCurso> {
        estadoDoAtendimento.set(id, 'em-atendimento');
        iniciosDeAtendimento.set(id, new Date().toISOString());
        return this.atendimentoEmCurso(id);
    },

    async abrirConsultaPresencial(pacienteId: string): Promise<string> {
        if (!sessaoPode('atendimentos')) throw new Error('Paciente não encontrado, ou sem permissão de atendimento.');
        const pessoa = contatosDaDemonstracao().find((c) => c.id === pacienteId && c.tipo === 'paciente');
        if (!pessoa) throw new Error('Paciente não encontrado, ou sem permissão de atendimento.');
        sequencia += 1;
        const agora = new Date();
        agora.setSeconds(0, 0);
        const criado: Agendamento = {
            id: `ag-${sequencia}`,
            inicioEm: agora.toISOString(),
            duracaoMin: 30,
            pacienteId: pessoa.id,
            paciente: pessoa.nome,
            procedimento: '',
            profissional: sessaoAtual()?.nome ?? '',
            status: 'confirmado',
            vinculo: { tipo: 'paciente', id: pessoa.id, nome: pessoa.nome },
            observacoes: 'Consulta presencial anotada com a VecchIA.',
            lembretePorWhatsapp: false,
        };
        agenda.push(criado);
        const id = `at-${criado.id}`;
        estadoDoAtendimento.set(id, 'em-atendimento');
        iniciosDeAtendimento.set(id, new Date().toISOString());
        return id;
    },

    async encerrarAtendimento(id: string): Promise<AtendimentoEmCurso> {
        if (!iniciosDeAtendimento.has(id)) throw new Error('A consulta ainda não foi aberta.');
        estadoDoAtendimento.set(id, 'concluido');
        finaisDeAtendimento.set(id, new Date().toISOString());

        /* Encerrar a consulta conclui o agendamento: são o mesmo fato visto de
           duas telas, e deixá-los divergir é o defeito que o protótipo tinha. */
        const agendamentoId = id.startsWith('at-') ? id.slice(3) : id;
        const agendamento = agenda.find((a) => a.id === agendamentoId);
        if (agendamento) agendamento.status = 'concluido';

        return this.atendimentoEmCurso(id);
    },

    /* Na demonstração o resumo é escrito à mão: gerar de verdade pede servidor e modelo,
       e fingir uma chamada de IA esconderia o custo que ela tem no ar. */
    async resumoDoPaciente(contatoId: string): Promise<ResumoDoPaciente | null> {
        return resumosDaDemonstracao.get(contatoId) ?? null;
    },

    async gerarResumoDoPaciente(): Promise<ResumoDoPaciente> {
        throw new Error('Na demonstração o resumo não é gerado: isso pede o servidor e uma chave de IA.');
    },

    async prontuario(contatoId: string): Promise<ProntuarioDoPaciente> {
        const doPaciente = agenda
            .filter((a) => a.pacienteId === contatoId && a.status !== 'bloqueio')
            .sort((x, y) => y.inicioEm.localeCompare(x.inicioEm));

        return {
            fichas: copiar(fichasClinicas.filter((f) => f.contatoId === contatoId))
                .sort((a, b) => b.criadoEm.localeCompare(a.criadoEm)),
            modelos: modelosComUsos(),
            atendimentos: doPaciente.map((a) => ({
                id: `at-${a.id}`,
                rotulo: `${new Date(a.inicioEm).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} · ${a.procedimento || 'Consulta'}`,
            })),
        };
    },

    async modelosDeFicha(): Promise<ModeloDeFicha[]> {
        return modelosComUsos();
    },

    async salvarModeloDeFicha(modelo: { id?: string; nome: string; campos: CampoDeFicha[] }): Promise<ModeloDeFicha> {
        if (!modelo.nome.trim()) throw new Error('Dê um nome ao modelo.');
        if (modelo.campos.length === 0) throw new Error('Um modelo sem campo nenhum não coleta nada.');

        const existente = modelo.id ? modelosDeFicha.find((m) => m.id === modelo.id) : undefined;
        if (existente) {
            /* A versão sobe só quando os campos mudam: renomear o modelo não
               invalida as fichas antigas, mexer nas perguntas invalida. */
            const mudouCampos = JSON.stringify(existente.campos) !== JSON.stringify(modelo.campos);
            existente.nome = modelo.nome.trim();
            existente.campos = modelo.campos;
            if (mudouCampos) existente.versao += 1;
            return { ...existente };
        }

        sequencia += 1;
        const criado: ModeloDeFicha = { id: `mod-${sequencia}`, nome: modelo.nome.trim(), campos: modelo.campos, versao: 1 };
        modelosDeFicha.push(criado);
        return { ...criado };
    },

    async arquivarModeloDeFicha(id: string): Promise<void> {
        /* Arquivar tira da lista de escolha; as fichas já preenchidas continuam
           legíveis pela cópia que cada uma guardou — por isso sair da lista não
           é perder registro, e não há motivo para bloquear modelo já usado. */
        modelosDeFicha = modelosDeFicha.filter((m) => m.id !== id);
    },

    async enviarAnexoDaFicha(contatoId: string, uid: string, arquivo: File): Promise<ArquivoDaFicha> {
        /* A demonstração não tem cofre. O anexo existe só enquanto a aba está
           aberta, e o caminho tem a mesma forma do servidor para a tela não
           precisar saber em qual dos dois está. */
        sequencia += 1;
        const caminho = `demonstracao/${contatoId}/${uid}/${String(sequencia)}-${arquivo.name}`;
        anexosDaDemonstracao.set(caminho, arquivo);
        return { nome: arquivo.name, caminho, tipo: arquivo.type, bytes: arquivo.size };
    },

    async enderecoDoAnexo(caminho: string): Promise<string> {
        const arquivo = anexosDaDemonstracao.get(caminho);
        /* `blob:` vale enquanto a aba viver — que é exatamente o tempo de vida
           do anexo na demonstração. */
        return arquivo ? URL.createObjectURL(arquivo) : '';
    },

    async salvarFicha(nova: NovaFicha): Promise<FichaPreenchida> {
        const modelo = modelosDeFicha.find((m) => m.id === nova.modeloId);
        if (!modelo) throw new Error('Modelo de ficha não encontrado.');

        /* Campo obrigatório vazio para aqui: prontuário incompleto que passa
           direto é o que ninguém descobre até precisar dele. */
        const faltando = modelo.campos.filter((c) => c.obrigatorio && !nova.respostas[c.uid]);
        if (faltando.length > 0) {
            throw new Error(`Preencha: ${faltando.map((c) => c.rotulo).join(', ')}.`);
        }

        sequencia += 1;
        const agora = new Date().toISOString();
        const ficha: FichaPreenchida = {
            id: `fic-${sequencia}`,
            atendimentoId: nova.atendimentoId,
            contatoId: nova.contatoId,
            modeloId: nova.modeloId,
            nome: modelo.nome,
            respostas: { ...nova.respostas },
            /* A cópia do modelo vai junto: é por ela que a ficha será lida
               daqui a um ano, mesmo que o modelo mude dez vezes. */
            definicaoUsada: modelo.campos.map((c) => ({ ...c })),
            versaoDoModelo: modelo.versao,
            preenchidaPor: 'Você',
            criadoEm: agora,
            atualizadoEm: agora,
        };
        fichasClinicas.unshift(ficha);
        return { ...ficha };
    },

    async financeiro(filtro: FiltroDoFinanceiro): Promise<CargaDoFinanceiro> {
        const hoje = diaDeHoje();
        const busca = filtro.busca?.trim() ?? '';
        const mes = filtro.mes ?? hoje.slice(0, 7);

        const combinaTitulo = (t: Titulo) => !busca || combina(t.descricao, busca) || combina(t.contraparte, busca);
        const emAberto = (t: Titulo) => t.situacao === 'aberto' || t.situacao === 'parcial';
        /* A semente guarda a categoria pelo nome; o id sai dela, casando também
           a natureza — "Outros" de receita não é "Outros" de despesa. */
        const comCategoria = titulos.map((t): Titulo => {
            const natureza = t.tipo === 'receber' ? 'receita' : 'despesa';
            const id = t.categoriaId ?? categorias.find((c) => c.nome === t.categoria && c.natureza === natureza)?.id;
            return id ? { ...t, categoriaId: id } : { ...t };
        });

        const receber = comCategoria.filter((t) => t.tipo === 'receber' && emAberto(t));
        const pagar = comCategoria.filter((t) => t.tipo === 'pagar' && emAberto(t));
        /* Vencido é conta de data, não campo: título que venceu ontem e ninguém
           tocou continuaria "aberto" para sempre num campo gravado. */
        const vencidos = [...receber, ...pagar].filter((t) => t.vencimento < hoje);

        const lista = filtro.aba === 'pagar' ? pagar.filter(combinaTitulo)
            : filtro.aba === 'receber' ? receber.filter(combinaTitulo)
                : comCategoria.filter(combinaTitulo);
        const doMes = movimentacoes.filter((m) => m.pagoEm.startsWith(mes));

        const recebidoNoMes = doMes.filter((m) => m.tipo === 'receber').reduce((s, m) => s + m.liquidoCentavos, 0);
        const pagoNoMes = doMes.filter((m) => m.tipo === 'pagar').reduce((s, m) => s + m.valorCentavos, 0);

        /* Cada tipo pagina no seu lugar, como em Vendas: uma lista só com os
           dois dentro obrigaria a tela a adivinhar o que recebeu. */
        const paginaDeTitulos = paginar(lista, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });
        const paginaDeMovimentos = paginar(doMes, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });
        const pagina = filtro.aba === 'extrato' ? paginaDeMovimentos : paginaDeTitulos;

        /* Seis meses é o recorte que a clínica olha: o suficiente para ver
           tendência, curto o bastante para caber numa tela sem rolagem. */
        const meses = janelaDeMeses(mes, 6);

        return {
            aba: filtro.aba,
            titulos: filtro.aba === 'extrato' ? [] : paginaDeTitulos.itens,
            movimentacoes: filtro.aba === 'extrato' ? paginaDeMovimentos.itens : copiar(doMes),
            /* As contas da demonstração não têm unidade: são da principal. */
            contas: copiar(recorteDaDemonstracao()?.principal === false ? [] : CONTAS).map((c) => ({
                ...c,
                /* O saldo é o inicial mais o que já foi LIQUIDADO nela: crédito
                   em trinta dias não é dinheiro em caixa hoje. */
                saldoCentavos: c.saldoCentavos + movimentacoes
                    .filter((m) => m.conta === c.nome && m.liquidadoEm && m.liquidadoEm <= hoje)
                    .reduce((s, m) => s + (m.tipo === 'receber' ? m.liquidoCentavos : -m.valorCentavos), 0),
            })),
            categorias: copiar(categorias),
            metodos: copiar(METODOS),
            total: pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'receber', rotulo: 'A receber', valor: `R$ ${(receber.reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, apoio: `${receber.length} títulos`, icone: 'circle-dollar-sign' },
                { id: 'pagar', rotulo: 'A pagar', valor: `R$ ${(pagar.reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, apoio: `${pagar.length} títulos`, icone: 'receipt' },
                { id: 'vencidos', rotulo: 'Vencidos', valor: String(vencidos.length), apoio: `R$ ${(vencidos.reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, tendencia: vencidos.length ? 'down' : 'neutral', icone: 'triangle-alert' },
                { id: 'resultado', rotulo: 'Resultado do mês', valor: `R$ ${((recebidoNoMes - pagoNoMes) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, apoio: 'recebido menos pago', tendencia: recebidoNoMes >= pagoNoMes ? 'up' : 'down', icone: 'chart-no-axes-column' },
            ],
            totais: { receber: receber.length, pagar: pagar.length, vencidos: vencidos.length },
            competencia: relatorioDeCompetencia(titulos, movimentacoes, meses),
            fluxo: filtro.aba === 'fluxo-diario' ? fluxoDiario(movimentacoes, mes) : fluxoMensal(movimentacoes, meses),
            categoriasDeConta: categoriasDeConta(),
            porCategoria: totaisPorCategoria(comCategoria, mes),
        };
    },

    async salvarCategoriaDeConta(categoria: { id?: string; nome: string; natureza: 'receita' | 'despesa'; paiId?: string }): Promise<CategoriaDeConta> {
        const nome = categoria.nome.trim();
        if (!nome) throw new Error('Dê um nome à categoria.');
        /* Duas categorias com o mesmo nome e a mesma natureza fazem o relatório
           partir o valor em duas linhas que ninguém consegue somar. */
        const repetida = categorias.some((c) => c.id !== categoria.id && c.natureza === categoria.natureza && mesmoNome(c.nome, nome));
        if (repetida) throw new Error(`Já existe uma categoria de ${categoria.natureza} chamada ${nome}.`);

        const existente = categoria.id ? categorias.find((c) => c.id === categoria.id) : undefined;
        if (existente) {
            existente.nome = nome;
            existente.natureza = categoria.natureza;
            paisDeCategoria.set(existente.id, categoria.paiId);
            return categoriaDeConta(existente);
        }

        sequencia += 1;
        const criada: CategoriaFinanceira = { id: `cat-${sequencia}`, nome, natureza: categoria.natureza };
        categorias.push(criada);
        if (categoria.paiId) paisDeCategoria.set(criada.id, categoria.paiId);
        return categoriaDeConta(criada);
    },

    async inativarCategoriaDeConta(id: string, ativa: boolean): Promise<void> {
        if (ativa) categoriasInativas.delete(id);
        else categoriasInativas.add(id);
    },

    async registrarPagamento(dados: NovoPagamento): Promise<Titulo> {
        const titulo = titulos.find((t) => t.id === dados.tituloId);
        if (!titulo) throw new Error('Título não encontrado.');

        const falta = titulo.valorCentavos - titulo.pagoCentavos;
        if (dados.valorCentavos <= 0) throw new Error('Informe um valor maior que zero.');
        /* Receber mais do que se deve não é baixa, é erro de digitação — e um
           título "pago" com valor maior some do relatório sem explicação. */
        if (dados.valorCentavos > falta) throw new Error(`O valor não pode passar de ${(falta / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.`);

        const metodo = METODOS.find((m) => m.id === dados.metodoId);
        const conta = CONTAS.find((c) => c.id === dados.contaId);
        const taxa = dados.taxaCentavos ?? Math.round(dados.valorCentavos * ((metodo?.taxaPercentual ?? 0) / 100));

        sequencia += 1;
        movimentacoes.unshift({
            id: `mov-${sequencia}`,
            tituloId: titulo.id,
            tipo: titulo.tipo,
            descricao: titulo.descricao,
            contraparte: titulo.contraparte,
            valorCentavos: dados.valorCentavos,
            taxaCentavos: taxa,
            liquidoCentavos: dados.valorCentavos - taxa,
            pagoEm: dados.pagoEm,
            liquidadoEm: metodo ? somaDiasIso(dados.pagoEm, metodo.prazoDias) : dados.pagoEm,
            conta: conta?.nome,
            metodo: metodo?.nome,
        });

        titulo.pagoCentavos += dados.valorCentavos;
        titulo.situacao = titulo.pagoCentavos >= titulo.valorCentavos ? 'pago' : 'parcial';
        return { ...titulo };
    },

    async pipeline(filtro: { busca?: string; responsavel?: string; funilId?: string }): Promise<CargaDoPipeline> {
        const funil = funis.find((f) => f.id === filtro.funilId) ?? funilPadrao(funis);
        if (!funil) throw new Error('A clínica não tem pipeline ativo.');
        const busca = filtro.busca?.trim() ?? '';
        const visiveis = leads.filter((l) => {
            if (l.funilId !== funil.id) return false;
            if (filtro.responsavel && l.responsavel !== filtro.responsavel) return false;
            if (busca && !combina(l.nome, busca) && !combina(l.interesse, busca)) return false;
            return true;
        });
        return montarPipeline(copiar(funis), funil, copiar(visiveis), [...new Set(leads.map((l) => l.responsavel))].sort());
    },

    async moverLeadDeEtapa(id: string, etapaId: string): Promise<Lead> {
        return this.atualizarLead(id, { etapaId });
    },

    async vendas(filtro: FiltroDeVendas): Promise<CargaDeVendas> {
        const busca = filtro.busca?.trim() ?? '';
        const combinaBusca = (o: Orcamento) => !busca || combina(o.paciente, busca) || combina(o.numero, busca) || combina(o.resumo, busca);

        const daAba = filtro.aba === 'aguardando' ? orcamentos.filter((o) => o.status === 'enviado')
            : filtro.aba === 'aprovados' ? orcamentos.filter((o) => o.status === 'aprovado')
                : orcamentos;
        const filtrados = daAba.filter(combinaBusca);
        const pacotesVisiveis = pacotes.filter((p) => !busca || combina(p.paciente, busca) || combina(p.nome, busca));

        /* Paginar cada tipo no seu lugar: uma lista só com os dois tipos
           dentro obrigaria a tela a adivinhar o que recebeu. */
        const paginaDeOrcamentos = paginar(filtrados, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 8 });
        const paginaDePacotes = paginar(pacotesVisiveis, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 8 });
        const pagina = filtro.aba === 'pacotes' ? paginaDePacotes : paginaDeOrcamentos;

        const aprovadosCentavos = orcamentos.filter((o) => o.status === 'aprovado').reduce((s, o) => s + o.totalCentavos, 0);
        const enviados = orcamentos.filter((o) => o.status === 'enviado');
        const decididos = orcamentos.filter((o) => o.status === 'aprovado' || o.status === 'recusado');

        return {
            aba: filtro.aba,
            orcamentos: filtro.aba === 'pacotes' ? [] : paginaDeOrcamentos.itens,
            pacotes: filtro.aba === 'pacotes' ? paginaDePacotes.itens : [],
            total: pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'aprovado', rotulo: 'Aprovado no período', valor: `R$ ${(aprovadosCentavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, icone: 'circle-check' },
                { id: 'aguardando', rotulo: 'Aguardando resposta', valor: String(enviados.length), apoio: `R$ ${(enviados.reduce((s, o) => s + o.totalCentavos, 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, icone: 'clock' },
                {
                    id: 'conversao', rotulo: 'Taxa de aprovação',
                    valor: decididos.length ? `${Math.round((decididos.filter((o) => o.status === 'aprovado').length / decididos.length) * 100)}%` : '—',
                    apoio: 'dos decididos', icone: 'target',
                },
                { id: 'pacotes', rotulo: 'Pacotes ativos', valor: String(pacotes.filter((p) => p.status === 'ativo').length), apoio: 'em uso', icone: 'layers' },
            ],
            totais: {
                orcamentos: orcamentos.length,
                aguardando: orcamentos.filter((o) => o.status === 'enviado').length,
                aprovados: orcamentos.filter((o) => o.status === 'aprovado').length,
                pacotes: pacotes.length,
            },
        };
    },

    async criarOrcamento(novo: NovoOrcamento): Promise<Orcamento> {
        sequencia += 1;
        const total = novo.itens.reduce((s, i) => s + i.valorUnitarioCentavos * i.quantidade - i.descontoCentavos, 0);
        const criado: Orcamento = {
            id: `orc-${sequencia}`,
            numero: `#2026-${String(184 + sequencia).padStart(4, '0')}`,
            contatoId: novo.contatoId ?? '',
            paciente: novo.pacienteNome,
            responsavel: novo.responsavelId
                ? (PROFISSIONAIS.find((p) => p.id === novo.responsavelId)?.nome ?? naoCadastrado('Responsável'))
                : novo.responsavel,
            status: 'rascunho',
            totalCentavos: total,
            validade: novo.validade,
            criadoEm: diaDeHoje(),
            itens: novo.itens.map((i, indice) => ({ id: `i-${sequencia}-${indice}`, ...i })),
            resumo: novo.itens.map((i) => i.descricao).join(' · '),
        };
        orcamentos.unshift(criado);
        return { ...criado };
    },

    async decidirOrcamento(id: string, status: StatusDoOrcamento, cobranca?: CondicaoDeCobranca): Promise<Orcamento> {
        const orcamento = orcamentos.find((o) => o.id === id);
        if (!orcamento) throw new Error('Orçamento não encontrado.');

        /* Saindo de aprovado: as parcelas sem pagamento são canceladas, e com
           pagamento a mudança é recusada — a mesma regra do banco (0175). */
        const parcelasDoOrcamento = titulos.filter((t) => orcamentoDoTitulo.get(t.id) === id);
        if (orcamento.status === 'aprovado' && status !== 'aprovado') {
            if (parcelasDoOrcamento.some((t) => movimentacoes.some((m) => m.tituloId === t.id))) {
                throw new Error('Este orçamento já tem pagamento recebido. Estorne os pagamentos no Financeiro antes de mudar a situação.');
            }
            for (const t of parcelasDoOrcamento) t.situacao = 'cancelado';
        }
        const categoriaDaVenda = cobranca?.categoriaId
            ? categorias.find((c) => c.id === cobranca.categoriaId && c.natureza === 'receita')
            : undefined;
        if (status === 'aprovado' && cobranca?.categoriaId && !categoriaDaVenda) {
            throw new Error('A categoria escolhida não é uma receita desta clínica.');
        }
        orcamento.status = status;

        /* Aprovar cria um pacote por item COM procedimento e quantidade ≥ 2 —
           a mesma regra do banco (`0062`). A descrição é texto livre: procurar
           "pacote" e "10 sess" nela criava pacote que ninguém vendeu e deixava
           de criar o que foi vendido. */
        if (status === 'aprovado') {
            for (const item of orcamento.itens) {
                const id = `pac-${orcamento.id}-${item.procedimentoId ?? ''}`;
                if (!item.procedimentoId || item.quantidade < 2 || pacotes.some((p) => p.id === id)) continue;
                pacotes.push({
                    id,
                    contatoId: orcamento.contatoId,
                    paciente: orcamento.paciente,
                    nome: item.descricao,
                    sessoesTotais: item.quantidade,
                    sessoesUsadas: 0,
                    valorCentavos: Math.max(0, item.quantidade * item.valorUnitarioCentavos - item.descontoCentavos),
                    status: 'ativo',
                });
            }

            const jaLancado = parcelasDoOrcamento.some((t) => t.situacao !== 'cancelado');
            const parcelas = cobranca?.parcelas ?? 1;
            if ((cobranca?.gerar ?? true) && !jaLancado && orcamento.totalCentavos > 0) {
                if (!Number.isInteger(parcelas) || parcelas < 1 || parcelas > 48) throw new Error('O número de parcelas vai de 1 a 48.');
                const hoje = diaDeHoje();
                const primeiro = cobranca?.primeiroVencimento ?? hoje;
                valoresDasParcelas(orcamento.totalCentavos, parcelas).forEach((valor, i) => {
                    sequencia += 1;
                    const titulo: Titulo = {
                        id: `tit-${sequencia}`,
                        tipo: 'receber',
                        descricao: `Orçamento ${orcamento.numero}${parcelas > 1 ? ` · parcela ${i + 1}/${parcelas}` : ''}`,
                        contraparte: orcamento.paciente,
                        contatoId: orcamento.contatoId,
                        ...(categoriaDaVenda ? { categoria: categoriaDaVenda.nome, categoriaId: categoriaDaVenda.id } : {}),
                        valorCentavos: valor,
                        pagoCentavos: 0,
                        vencimento: vencimentoDaParcela(primeiro, i + 1),
                        competencia: `${hoje.slice(0, 7)}-01`,
                        parcela: i + 1,
                        parcelasTotal: parcelas,
                        situacao: 'aberto',
                    };
                    titulos.push(titulo);
                    orcamentoDoTitulo.set(titulo.id, orcamento.id);
                });
            }
        }
        return { ...orcamento };
    },

    async caixaDeEntrada(filtro: FiltroDeConversas): Promise<CargaDaCaixa> {
        const busca = filtro.busca?.trim() ?? '';
        const visiveis = conversas.filter((c) => {
            if (filtro.aba === 'nao-lidas' && c.naoLidas === 0) return false;
            if (filtro.aba === 'leads' && !c.vinculos.includes('Lead')) return false;
            if (filtro.canalId && c.canalId !== filtro.canalId) return false;
            if (filtro.fila && c.fila !== filtro.fila) return false;
            if (busca && !combina(c.nome, busca) && !combina(c.ultimaMensagem ?? '', busca)) return false;
            return true;
        });

        return {
            conversas: copiar(visiveis).sort((a, b) => (b.ultimaMensagemEm ?? '').localeCompare(a.ultimaMensagemEm ?? '')),
            canais: copiar(CANAIS),
            filas: [...FILAS],
            totais: {
                todas: conversas.length,
                naoLidas: conversas.filter((c) => c.naoLidas > 0).length,
                leads: conversas.filter((c) => c.vinculos.includes('Lead')).length,
            },
        };
    },

    async mensagensDaConversa(conversaId: string): Promise<Mensagem[]> {
        return copiar(mensagens.filter((m) => m.conversaId === conversaId))
            .sort((a, b) => a.ocorridoEm.localeCompare(b.ocorridoEm));
    },

    async enviarMensagem(conversaId: string, texto: string): Promise<Mensagem> {
        const conversa = conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        /* A mesma recusa do servidor: canal de provedor com que a VecchIA não
           fala não recebe mensagem que nunca sairia. */
        const canalDaConversa = canais.find((c) => c.id === conversa.canalId);
        const indisponivel = canalDaConversa ? indisponibilidadeDoProvedor(canalDaConversa.provedor) : null;
        if (indisponivel) throw new Error(indisponivel);
        sequencia += 1;
        const nova: Mensagem = {
            id: `msg-${sequencia}`,
            conversaId,
            direcao: 'enviada',
            tipo: 'texto',
            texto,
            autor: 'Você',
            status: 'enviada',
            ocorridoEm: new Date().toISOString(),
        };
        mensagens.push(nova);
        conversa.ultimaMensagem = texto;
        conversa.ultimaMensagemEm = nova.ocorridoEm;
        /* Responder é ler: deixar o contador aceso depois da resposta faz a
           caixa mentir sobre o que falta atender. */
        conversa.naoLidas = 0;
        return { ...nova };
    },

    async abrirConversaCom(contatoId: string, canalId: string): Promise<string> {
        const canal = canais.find((c) => c.id === canalId);
        if (!canal) throw new Error('Canal não encontrado.');

        /* A mesma regra do banco: uma conversa por (contato, canal). */
        const existente = [...conversas]
            .filter((c) => c.contatoId === contatoId && c.canalId === canalId)
            .sort((a, b) => (b.ultimaMensagemEm ?? '').localeCompare(a.ultimaMensagemEm ?? ''))[0];

        if (existente) {
            existente.estado = 'em-atendimento';
            existente.conduz = 'equipe';
            existente.atendente = sessaoAtual()?.nome ?? 'Você';
            existente.naoLidas = 0;
            return existente.id;
        }

        sequencia += 1;
        const id = `conv-nova-${sequencia}`;
        const nome = pacientes.find((p) => p.id === contatoId)?.nome
            ?? leads.find((l) => l.contatoId === contatoId)?.nome
            ?? 'Contato';

        conversas.unshift({
            id,
            contatoId,
            nome,
            canalId,
            canalNome: canal.nome,
            canalTipo: canal.tipo,
            estado: 'em-atendimento',
            conduz: 'equipe',
            atendente: sessaoAtual()?.nome ?? 'Você',
            fila: 'Recepção',
            naoLidas: 0,
            ultimaMensagem: '',
            ultimaMensagemEm: new Date().toISOString(),
            vinculos: [],
        });
        return id;
    },

    async assumirConversa(conversaId: string): Promise<Conversa> {
        const conversa = conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        conversa.conduz = 'equipe';
        conversa.estado = 'em-atendimento';
        conversa.atendente = sessaoAtual()?.nome ?? 'Você';
        conversa.naoLidas = 0;
        delete conversa.reservadaPara;
        delete conversa.reservadaParaNome;
        return { ...conversa };
    },

    async resolverConversa(conversaId: string, motivo?: string): Promise<Conversa> {
        const conversa = conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        conversa.estado = 'resolvida';
        conversa.naoLidas = 0;
        conversa.resolvidaEm = new Date().toISOString();
        delete conversa.reservadaPara;
        delete conversa.reservadaParaNome;
        if (motivo?.trim()) conversa.motivoResolucao = motivo.trim();
        else delete conversa.motivoResolucao;
        return { ...conversa };
    },

    async reabrirConversa(conversaId: string): Promise<Conversa> {
        const conversa = conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        if (conversa.estado !== 'resolvida') throw new Error('Só conversa resolvida se reabre.');
        conversa.estado = 'em-atendimento';
        conversa.conduz = 'equipe';
        conversa.atendente = sessaoAtual()?.nome ?? 'Você';
        delete conversa.resolvidaEm;
        delete conversa.motivoResolucao;
        return { ...conversa };
    },

    async devolverConversa(conversaId: string): Promise<void> {
        const conversa = conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        if (conversa.estado === 'resolvida' || situacaoDaConversa(conversa) !== 'aberto') {
            throw new Error('Só conversa em Abertos volta para Pendentes.');
        }
        conversa.estado = 'aberta';
        delete conversa.atendente;
        delete conversa.atendenteId;
        delete conversa.reservadaPara;
        delete conversa.reservadaParaNome;
    },

    async paciente(id: string): Promise<CargaDoPaciente> {
        const ficha = fichas[id];
        /* Id que não existe é 404, não ficha em branco: mandar a tela desenhar
           um paciente vazio esconde link quebrado e cadastro apagado. */
        if (!ficha) throw new Response(`Paciente ${id} não encontrado.`, { status: 404, statusText: 'Paciente não encontrado' });
        const doPaciente = titulos.filter((t) => t.contatoId === id);
        const pago = doPaciente.reduce((s, t) => s + t.pagoCentavos, 0);
        const emAberto = doPaciente.reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0);
        const vencido = doPaciente
            .filter((t) => t.vencimento < diaDeHoje() && t.situacao !== 'pago')
            .reduce((s, t) => s + (t.valorCentavos - t.pagoCentavos), 0);
        const pacotesDele = pacotes.filter((p) => p.contatoId === id);

        return {
            ficha: { ...ficha },
            /* A etiqueta é da PESSOA: a mesma que a lista de contatos mostra. */
            etiquetas: copiar(pacientes.find((p) => p.id === id)?.etiquetas ?? []),
            convenios: copiar(CONVENIOS),
            /* O resumo vem dos títulos do próprio paciente, não de número
               guardado à parte: dois lugares para o mesmo dinheiro divergem. */
            financeiro: {
                ...(FINANCEIRO[id] ?? FINANCEIRO_VAZIO),
                totalInvestidoCentavos: pago,
                emAbertoCentavos: emAberto,
                vencidoCentavos: vencido,
                ticketMedioCentavos: doPaciente.length ? Math.round(pago / doPaciente.length) : 0,
                pacote: pacotesDele[0]
                    ? { nome: pacotesDele[0].nome, sessoesConcluidas: pacotesDele[0].sessoesUsadas, sessoesTotais: pacotesDele[0].sessoesTotais }
                    : undefined,
            },
            linhaDoTempo: copiar(LINHA_DO_TEMPO[id] ?? []),
            /* A ficha do paciente lê o MESMO acervo da tela de Documentos: duas
               listas do mesmo fato discordariam na primeira emissão. */
            documentos: documentos.filter((d) => d.contatoId === id).map(paraDocumentoDoPaciente),
            pacotes: copiar(pacotesDele),
            orcamentos: copiar(orcamentos.filter((o) => o.contatoId === id)),
            titulos: copiar(doPaciente),
            prontuario: await this.prontuario(id),
        };
    },

    async atualizarCampoDaFicha<C extends keyof FichaDoPaciente>(id: string, campo: C, valor: FichaDoPaciente[C]): Promise<FichaDoPaciente> {
        const ficha = fichas[id];
        if (!ficha) throw new Error(`Paciente ${id} não existe.`);
        /* O profissional chega por id e fica por nome, como a ficha o mostra —
           o mesmo contrato do servidor. */
        let final: FichaDoPaciente[C] = valor;
        if (campo === 'profissional') {
            const bruto = String(valor ?? '').trim();
            const nome = bruto ? (PROFISSIONAIS.find((p) => p.id === bruto) ?? unicoPorNome(PROFISSIONAIS, bruto, 'profissional')).nome : '';
            final = nome as FichaDoPaciente[C];
            const naLista = pacientes.find((p) => p.id === id);
            if (naLista) naLista.profissional = nome;
        }
        const atualizada: FichaDoPaciente = {
            ...ficha,
            [campo]: final,
            atualizadoEm: new Date().toISOString(),
        };
        fichas[id] = atualizada;

        /* O nome vive em dois lugares enquanto a listagem não vier do mesmo
           registro: manter só na ficha faria a lista de contatos mostrar o nome
           antigo depois de uma correção de cadastro. */
        if (campo === 'nome') {
            const naLista = pacientes.find((p) => p.id === id);
            if (naLista) naLista.nome = String(valor);
        }
        return { ...atualizada };
    },

    async criarAgendamento(novo: NovoAgendamento): Promise<Agendamento> {
        /* Tudo por id; nome só na falta dele (proposta do agente), e aí tem de
           bater com UM cadastro — como no servidor. Antes, qualquer nome digitado
           virava um paciente novo com id inventado. */
        const contatos = contatosDaDemonstracao();
        const pessoa = novo.pacienteId
            ? contatos.find((c) => c.id === novo.pacienteId)
            : unicoPorNome(contatos, novo.pacienteNome, 'paciente');
        if (!pessoa) throw new Error('Escolha o paciente na lista.');

        const procedimento = novo.procedimentoId
            ? (procedimentos.find((p) => p.id === novo.procedimentoId) ?? naoCadastrado('Procedimento'))
            : novo.procedimento ? unicoPorNome(procedimentos, novo.procedimento, 'procedimento') : undefined;
        const profissional = novo.profissionalId
            ? (PROFISSIONAIS.find((p) => p.id === novo.profissionalId) ?? naoCadastrado('Profissional'))
            : novo.profissional ? unicoPorNome(PROFISSIONAIS, novo.profissional, 'profissional') : undefined;
        const sala = novo.teleconsulta
            ? undefined
            : novo.salaId
                ? (salas.find((s) => s.id === novo.salaId) ?? naoCadastrado('Sala'))
                : novo.sala ? unicoPorNome(salas, novo.sala, 'sala') : undefined;
        const lead = pessoa.tipo === 'lead' ? leads.find((l) => l.contatoId === pessoa.id) : undefined;
        /* Nasce na unidade escolhida; com "Todas", na principal — como a 0101 faz no banco. */
        const unidade = recorteDaDemonstracao()
            ? unidades.find((u) => u.id === unidadeAtiva())
            : unidadesAtivasDaDemonstracao()[0];

        const sessoes = novo.repetirSemanalmente ? 4 : 1;
        const criados: Agendamento[] = [];
        for (let i = 0; i < sessoes; i += 1) {
            sequencia += 1;
            const criado: Agendamento = {
                id: `ag-${sequencia}`,
                inicioEm: `${somaDiasIso(novo.data, i * 7)}T${novo.hora}:00-03:00`,
                duracaoMin: novo.duracaoMin,
                pacienteId: pessoa.id,
                paciente: pessoa.nome,
                procedimento: procedimento?.nome ?? '',
                profissional: profissional?.nome ?? '',
                sala: sala?.nome,
                ...(unidade ? { unidadeId: unidade.id, unidade: unidade.nome } : {}),
                status: novo.status ?? 'confirmado',
                vinculo: lead
                    ? { tipo: 'lead', id: lead.id, nome: pessoa.nome, oportunidade: lead.interesse, etapa: lead.etapa }
                    : { tipo: 'paciente', id: pessoa.id, nome: pessoa.nome },
                teleconsulta: novo.teleconsulta,
                ...(novo.teleconsulta ? { linkTeleconsulta: gerarLinkDeTeleconsulta() } : {}),
                observacoes: novo.observacoes,
                lembretePorWhatsapp: novo.lembretePorWhatsapp,
            };
            agenda.push(criado);
            criados.push(criado);
        }
        const [primeiro] = criados;
        if (!primeiro) throw new Error('Nenhuma sessão foi criada.');
        return { ...primeiro };
    },

    async estoque(filtro: FiltroDoEstoque): Promise<CargaDoEstoque> {
        const busca = filtro.busca?.trim() ?? '';
        const todos = produtos.map(paraProduto);

        const combinaProduto = (p: Produto) => !busca || combina(p.nome, busca) || combina(p.lote, busca);
        const abaixoDoMinimo = todos.filter((p) => p.situacao !== 'normal');
        const criticos = todos.filter((p) => p.situacao === 'critico');
        const reservasAbertas = reservas.filter((r) => r.status !== 'consumida');

        const listaDeProdutos = (filtro.aba === 'baixo' ? abaixoDoMinimo : todos).filter(combinaProduto);
        const listaDeMovimentos = [...movimentosDeEstoque]
            .sort((a, b) => b.ocorridoEm.localeCompare(a.ocorridoEm))
            .map(paraMovimentacao)
            .filter((m) => !busca || combina(m.produto, busca) || combina(m.origem, busca));
        const listaDeReservas = reservas
            .map(paraReserva)
            .filter((r) => !busca || combina(r.procedimento, busca) || combina(r.paciente ?? '', busca));
        const listaDeFornecedores = fornecedores.map(paraFornecedor).filter((f) => !busca || combina(f.nome, busca));
        const listaDeMarcas = marcas.map(paraMarca).filter((m) => !busca || combina(m.nome, busca));
        const listaDeCategorias = categoriasDeProduto.map(paraCategoriaDeProduto).filter((c) => !busca || combina(c.nome, busca));

        /* Pagina só a lista que a aba mostra. Paginar as sete de uma vez daria
           números de página que não correspondem a nada na tela. */
        const visivel = filtro.aba === 'movimentacoes' ? listaDeMovimentos.length
            : filtro.aba === 'reservas' ? listaDeReservas.length
                : filtro.aba === 'fornecedores' ? listaDeFornecedores.length
                    : filtro.aba === 'marcas' ? listaDeMarcas.length
                        : filtro.aba === 'categorias' ? listaDeCategorias.length
                            : listaDeProdutos.length;
        const porPagina = filtro.porPagina ?? 10;
        const paginas = Math.max(1, Math.ceil(visivel / porPagina));
        const pagina = Math.min(Math.max(filtro.pagina ?? 1, 1), paginas);
        const recorte = <T>(lista: T[], ativa: boolean) =>
            (ativa ? lista.slice((pagina - 1) * porPagina, pagina * porPagina) : lista);

        const valorEmEstoqueCentavos = todos.reduce((s, p) => s + p.custoCentavos * p.quantidade, 0);

        return {
            aba: filtro.aba,
            produtos: recorte(listaDeProdutos, filtro.aba === 'produtos' || filtro.aba === 'baixo'),
            categorias: recorte(listaDeCategorias, filtro.aba === 'categorias'),
            fornecedores: recorte(listaDeFornecedores, filtro.aba === 'fornecedores'),
            marcas: recorte(listaDeMarcas, filtro.aba === 'marcas'),
            reservas: recorte(listaDeReservas, filtro.aba === 'reservas'),
            movimentacoes: recorte(listaDeMovimentos, filtro.aba === 'movimentacoes'),
            total: visivel,
            pagina,
            paginas,
            indicadores: [
                { id: 'itens', rotulo: 'Itens cadastrados', valor: String(todos.length), apoio: `${marcas.length} marcas · ${fornecedores.length} fornecedores`, icone: 'package' },
                { id: 'valor', rotulo: 'Valor em estoque', valor: `R$ ${(valorEmEstoqueCentavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, apoio: 'custo de reposição', icone: 'circle-dollar-sign' },
                /* Contagem de reservas, não soma de quantidades: somar 2 frascos
                   com 4 agulhas dá 6 de coisa nenhuma. */
                { id: 'reservado', rotulo: 'Reservado por propostas', valor: String(reservasAbertas.length), apoio: `${reservasAbertas.reduce((s, r) => s + r.itens.length, 0)} insumos comprometidos`, icone: 'package-check' },
                { id: 'baixo', rotulo: 'Abaixo do mínimo', valor: String(abaixoDoMinimo.length), apoio: criticos.length ? 'ação necessária' : 'estoque coberto', tendencia: criticos.length ? 'down' : 'neutral', icone: 'triangle-alert' },
            ],
            totais: {
                produtos: todos.length,
                categorias: categoriasDeProduto.length,
                abaixoDoMinimo: abaixoDoMinimo.length,
                reservasAbertas: reservasAbertas.length,
                fornecedores: fornecedores.length,
                marcas: marcas.length,
                movimentacoes: movimentosDeEstoque.length,
            },
            criticos,
            valorEmEstoqueCentavos,
        };
    },

    async criarProduto(dados: NovoProduto): Promise<Produto> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao produto.');
        if (dados.conteudo <= 0) throw new Error('A quantidade por embalagem precisa ser maior que zero.');
        const lote = dados.lote?.trim() ?? '';
        if (produtos.some((p) => mesmoNome(p.nome, nome) && p.lote === lote)) {
            throw new Error(`Já existe ${nome}${lote ? ` no lote ${lote}` : ''}.`);
        }

        sequencia += 1;
        const criado: ProdutoSemente = {
            id: `prd-${sequencia}`,
            nome,
            categoriaId: dados.categoriaId ?? '',
            marcaId: dados.marcaId ?? '',
            fornecedorId: dados.fornecedorId ?? '',
            lote,
            validade: dados.validade ?? '',
            minimo: dados.minimo,
            unidade: dados.unidade,
            conteudo: dados.conteudo,
            unidadeConteudo: dados.unidadeConteudo,
            unidadeConsumo: dados.unidadeConsumo,
            custoCentavos: dados.custoCentavos,
            ativo: true,
        };
        produtos.push(criado);

        /* O saldo inicial entra como movimentação, e não como campo: assim a
           primeira linha do extrato explica de onde veio o que já está lá. */
        if (dados.quantidadeInicial > 0) {
            sequencia += 1;
            movimentosDeEstoque.push({
                id: `mve-${sequencia}`,
                produtoId: criado.id,
                tipo: 'entrada',
                quantidade: dados.quantidadeInicial,
                origem: 'Saldo inicial',
                ocorridoEm: new Date().toISOString(),
            });
        }
        return paraProduto(criado);
    },

    async atualizarProduto(id: string, dados: DadosDoProduto): Promise<Produto> {
        const alvo = produtoOuErro(id);
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao produto.');
        if (dados.conteudo <= 0) throw new Error('A quantidade por embalagem precisa ser maior que zero.');

        Object.assign(alvo, {
            nome,
            categoriaId: dados.categoriaId ?? '',
            marcaId: dados.marcaId ?? '',
            fornecedorId: dados.fornecedorId ?? '',
            lote: dados.lote?.trim() ?? '',
            validade: dados.validade ?? '',
            minimo: dados.minimo,
            unidade: dados.unidade,
            conteudo: dados.conteudo,
            unidadeConteudo: dados.unidadeConteudo,
            unidadeConsumo: dados.unidadeConsumo,
            custoCentavos: dados.custoCentavos,
        });
        return paraProduto(alvo);
    },

    async registrarMovimentacao(dados: NovaMovimentacao): Promise<Produto> {
        const alvo = produtoOuErro(dados.produtoId);
        if (dados.quantidade <= 0) throw new Error('Informe uma quantidade maior que zero.');
        /* Saída maior que o saldo é erro de digitação, não estoque negativo:
           deixar passar faz o inventário mentir para sempre. */
        if (dados.tipo === 'saida' && dados.quantidade > saldoDe(alvo.id)) {
            throw new Error(`Há apenas ${saldoDe(alvo.id).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} em estoque.`);
        }

        sequencia += 1;
        movimentosDeEstoque.push({
            id: `mve-${sequencia}`,
            produtoId: alvo.id,
            tipo: dados.tipo,
            quantidade: dados.quantidade,
            origem: dados.origem,
            ocorridoEm: dados.ocorridoEm ? `${dados.ocorridoEm}T12:00:00-03:00` : new Date().toISOString(),
        });
        return paraProduto(alvo);
    },

    async salvarFornecedor(dados: DadosDoFornecedor): Promise<Fornecedor> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao fornecedor.');
        if (fornecedores.some((f) => f.id !== dados.id && mesmoNome(f.nome, nome))) {
            throw new Error(`Já existe um fornecedor chamado ${nome}.`);
        }

        conferirCategoriaDeCadastro(dados.categoriaId);
        const existente = dados.id ? fornecedores.find((f) => f.id === dados.id) : undefined;
        if (existente) {
            if (dados.categoriaId !== undefined) existente.categoriaId = dados.categoriaId ?? undefined;
            Object.assign(existente, {
                nome,
                cnpj: dados.cnpj ?? '',
                contato: dados.contato ?? '',
                telefone: dados.telefone ?? '',
                email: dados.email ?? '',
                prazoEntrega: dados.prazoEntrega ?? '',
                ativo: dados.ativo ?? existente.ativo,
            });
            return paraFornecedor(existente);
        }

        sequencia += 1;
        const criado = {
            id: `for-${sequencia}`,
            nome,
            cnpj: dados.cnpj ?? '',
            contato: dados.contato ?? '',
            telefone: dados.telefone ?? '',
            email: dados.email ?? '',
            prazoEntrega: dados.prazoEntrega ?? '',
            ativo: dados.ativo ?? true,
            ...(dados.categoriaId ? { categoriaId: dados.categoriaId } : {}),
        };
        fornecedores.push(criado);
        return paraFornecedor(criado);
    },

    async salvarPrestador(dados: DadosDoPrestador): Promise<PrestadorDeServico> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao prestador de serviços.');
        conferirCategoriaDeCadastro(dados.categoriaId);
        let tipoServicoId = dados.tipoServicoId;
        const novoTipo = dados.novoTipoServico?.trim();
        if (novoTipo) tipoServicoId = (await this.salvarTipoDeServico({ nome: novoTipo })).id;
        if (tipoServicoId && !tiposDeServico.some((t) => t.id === tipoServicoId)) throw new Error('Tipo de serviço não encontrado.');

        const campos = {
            nome,
            documento: dados.documento?.trim() || undefined,
            telefone: dados.telefone?.trim() ?? '',
            email: dados.email?.trim() || undefined,
            observacoes: dados.observacoes?.trim() || undefined,
        };
        const existente = dados.id ? prestadores.find((p) => p.id === dados.id) : undefined;
        if (dados.id && !existente) throw new Error('Prestador de serviços não encontrado.');
        const alvo: PrestadorSemente = existente ?? { id: `psv-${(sequencia += 1)}`, ativo: true, ...campos };
        Object.assign(alvo, campos);
        if (tipoServicoId !== undefined) alvo.tipoServicoId = tipoServicoId ?? undefined;
        if (dados.categoriaId !== undefined) alvo.categoriaId = dados.categoriaId ?? undefined;
        if (dados.ativo !== undefined) alvo.ativo = dados.ativo;
        if (!existente) prestadores.push(alvo);
        return paraPrestador(alvo);
    },

    async tiposDeServico(): Promise<TipoDeServico[]> {
        return [...tiposDeServico]
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
            .map((t) => ({ ...t, prestadores: prestadores.filter((p) => p.tipoServicoId === t.id).length }));
    },

    async salvarTipoDeServico(dados: { id?: string; nome: string; ativo?: boolean }): Promise<TipoDeServico> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao tipo de serviço.');
        if (tiposDeServico.some((t) => t.id !== dados.id && mesmoNome(t.nome, nome))) throw new Error(`Já existe o tipo de serviço ${nome}.`);
        const existente = dados.id ? tiposDeServico.find((t) => t.id === dados.id) : undefined;
        if (dados.id && !existente) throw new Error('Tipo de serviço não encontrado.');
        const alvo = existente ?? { id: `tsv-${(sequencia += 1)}`, nome, ativo: true };
        alvo.nome = nome;
        if (dados.ativo !== undefined) alvo.ativo = dados.ativo;
        if (!existente) tiposDeServico.push(alvo);
        return { ...alvo, prestadores: prestadores.filter((p) => p.tipoServicoId === alvo.id).length };
    },

    async categoriasParaCadastro(): Promise<CategoriaParaEscolha[]> {
        /* Como a RLS da 0037: sem o financeiro, a lista vem vazia. */
        if (!sessaoPode('financeiro')) return [];
        return categorias
            .filter((c) => c.natureza === 'despesa' && !categoriasInativas.has(c.id))
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
            .map((c) => ({ id: c.id, nome: c.nome }));
    },

    async salvarMarca(dados: DadosDaMarca): Promise<Marca> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome à marca.');
        if (marcas.some((m) => m.id !== dados.id && mesmoNome(m.nome, nome))) {
            throw new Error(`Já existe uma marca chamada ${nome}.`);
        }

        const existente = dados.id ? marcas.find((m) => m.id === dados.id) : undefined;
        if (existente) {
            Object.assign(existente, {
                nome,
                fabricante: dados.fabricante ?? '',
                fornecedorId: dados.fornecedorId ?? '',
                categoria: dados.categoria ?? '',
                registro: dados.registro,
                ativa: dados.ativa ?? existente.ativa,
            });
            return paraMarca(existente);
        }

        sequencia += 1;
        const criada = {
            id: `mar-${sequencia}`,
            nome,
            fabricante: dados.fabricante ?? '',
            fornecedorId: dados.fornecedorId ?? '',
            categoria: dados.categoria ?? '',
            registro: dados.registro,
            ativa: dados.ativa ?? true,
        };
        marcas.push(criada);
        return paraMarca(criada);
    },

    async salvarCategoriaDeProduto(dados: DadosDaCategoriaDeProduto): Promise<CategoriaDeProduto> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome à categoria.');
        if (categoriasDeProduto.some((c) => c.id !== dados.id && mesmoNome(c.nome, nome))) {
            throw new Error(`Já existe uma categoria chamada ${nome}.`);
        }

        const existente = dados.id ? categoriasDeProduto.find((c) => c.id === dados.id) : undefined;
        if (existente) {
            Object.assign(existente, { nome, descricao: dados.descricao ?? '', ativa: dados.ativa });
            return paraCategoriaDeProduto(existente);
        }

        sequencia += 1;
        const criada = { id: `cpr-${sequencia}`, nome, descricao: dados.descricao ?? '', ativa: dados.ativa };
        categoriasDeProduto.push(criada);
        return paraCategoriaDeProduto(criada);
    },

    async baixarReserva(id: string): Promise<void> {
        const reserva = reservas.find((r) => r.id === id);
        if (!reserva) throw new Error('Reserva não encontrada.');
        if (reserva.status === 'consumida') return;

        for (const item of reserva.itens) {
            const p = produtos.find((x) => x.id === item.produtoId);
            if (!p) continue;
            sequencia += 1;
            /* A saída vai em embalagens, porque é a unidade do saldo, mas leva
               junto o que foi USADO: "0,5 frasco" não conta a história que
               "50 UI" conta. */
            movimentosDeEstoque.push({
                id: `mve-${sequencia}`,
                produtoId: p.id,
                tipo: 'saida',
                quantidade: embalagensDe(p, item.quantidadeConsumo),
                quantidadeConsumo: item.quantidadeConsumo,
                unidadeConsumo: item.unidadeConsumo,
                origem: `Procedimento — ${reserva.procedimento}`,
                ocorridoEm: new Date().toISOString(),
                reservaId: reserva.id,
            });
        }
        reserva.status = 'consumida';
    },

    async liberarReserva(id: string): Promise<void> {
        /* Liberar não gera movimentação: nada saiu do armário. O disponível
           volta sozinho porque o reservado é derivado das reservas abertas. */
        reservas = reservas.filter((r) => r.id !== id);
    },

    async procedimentos(filtro: FiltroDeProcedimentos): Promise<CargaDeProcedimentos> {
        const busca = filtro.busca?.trim() ?? '';
        const todos = procedimentos.map(paraProcedimento);

        const visiveis = todos.filter((p) => (!busca || combina(p.nome, busca))
            && (!filtro.categoria || p.categoria === filtro.categoria));
        const pagina = paginar(visiveis, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });

        const ativos = todos.filter((p) => p.ativo);
        const comPreco = ativos.filter((p) => p.precoCentavos > 0);
        const bloqueados = todos.filter((p) => p.execucoesPossiveis === 0);
        const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

        const categorias = [...new Set(todos.map((p) => p.categoria).filter((c): c is string => !!c))]
            .map((nome) => ({ nome, total: todos.filter((p) => p.categoria === nome).length }));

        return {
            itens: pagina.itens,
            categorias,
            produtos: produtos.map(paraProduto),
            profissionais: membros.filter((m) => m.acesso === 'ativo').map((m) => ({ id: m.id, nome: m.nome })),
            total: pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'ativos', rotulo: 'Procedimentos ativos', valor: String(ativos.length), apoio: `${categorias.length} categorias`, icone: 'clipboard-list' },
                /* Ticket do catálogo é média dos que TÊM preço: incluir o
                   retorno gratuito puxaria a média para baixo e o número
                   deixaria de dizer o que a clínica cobra. */
                { id: 'ticket', rotulo: 'Ticket do catálogo', valor: comPreco.length ? reais(comPreco.reduce((s, p) => s + p.precoCentavos, 0) / comPreco.length) : reais(0), apoio: 'média dos que têm preço', icone: 'circle-dollar-sign' },
                { id: 'custo', rotulo: 'Custo médio', valor: ativos.length ? reais(ativos.reduce((s, p) => s + p.custoDeInsumosCentavos + p.custoExtraCentavos, 0) / ativos.length) : reais(0), apoio: 'insumos e custos extras', icone: 'package' },
                { id: 'bloqueados', rotulo: 'Bloqueados por estoque', valor: String(bloqueados.length), apoio: bloqueados.length ? 'repor insumo' : 'estoque cobre o catálogo', tendencia: bloqueados.length ? 'down' : 'neutral', icone: 'triangle-alert' },
            ],
            bloqueados,
            totais: { procedimentos: todos.length, ativos: ativos.length },
        };
    },

    async salvarProcedimento(dados: DadosDoProcedimento): Promise<Procedimento> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao procedimento.');
        if (dados.duracaoMin <= 0) throw new Error('A duração precisa ser maior que zero.');
        /* Preço zero é legítimo — o retorno é gratuito —, mas negativo não é
           desconto: é erro de digitação que desce para o orçamento. */
        if (dados.precoCentavos < 0) throw new Error('O valor não pode ser negativo.');
        if (procedimentos.some((p) => p.id !== dados.id && mesmoNome(p.nome, nome))) {
            throw new Error(`Já existe um procedimento chamado ${nome}.`);
        }

        const campos = {
            nome,
            categoria: dados.categoria ?? '',
            duracaoMin: dados.duracaoMin,
            precoCentavos: dados.precoCentavos,
            tipoValor: dados.tipoValor,
            reconsultaDias: dados.reconsultaDias,
            cor: dados.cor,
            descricao: dados.descricao,
            ativo: dados.ativo,
            profissionaisIds: [...dados.profissionaisIds],
            custosExtras: dados.custosExtras.filter((c) => c.nome.trim()).map((c) => ({ ...c })),
            camposPersonalizados: dados.camposPersonalizados.filter((c) => c.rotulo.trim()).map((c) => ({ ...c })),
        };

        const existente = dados.id ? procedimentos.find((p) => p.id === dados.id) : undefined;
        const alvo = existente ?? (() => {
            sequencia += 1;
            const criado: ProcedimentoSemente = { id: `prc-${sequencia}`, exigeSala: true, ...campos };
            procedimentos.push(criado);
            return criado;
        })();
        if (existente) Object.assign(existente, campos);

        /* Os insumos são substituídos inteiros: o formulário manda a lista que
           vale agora, e casar linha a linha só criaria insumo órfão. */
        insumosDosProcedimentos = insumosDosProcedimentos.filter((i) => i.procedimentoId !== alvo.id);
        for (const insumo of dados.insumos) {
            if (insumo.quantidade > 0) {
                insumosDosProcedimentos.push({ procedimentoId: alvo.id, produtoId: insumo.produtoId, quantidade: insumo.quantidade });
            }
        }
        return paraProcedimento(alvo);
    },

    async reservarInsumos(procedimentoId: string, contexto: ContextoDaReserva): Promise<ReservaDeEstoque> {
        const semente = procedimentos.find((p) => p.id === procedimentoId);
        if (!semente) throw new Error('Procedimento não encontrado.');
        const procedimento = paraProcedimento(semente);
        if (procedimento.insumos.length === 0) throw new Error('Este procedimento não consome insumos.');

        /* A reserva nasce marcada quando o estoque não cobre: recusar
           esconderia o problema, e reservar em silêncio deixaria a recepção
           marcar um procedimento que não dá para executar. */
        const faltando = procedimento.insumos.filter((i) => i.disponivel < i.embalagens);

        sequencia += 1;
        const nova = {
            id: `res-${sequencia}`,
            procedimentoId: procedimento.id,
            procedimento: procedimento.nome,
            contatoId: contexto.contatoId,
            paciente: contexto.paciente ?? '—',
            origem: contexto.origem ?? 'Proposta aceita',
            status: (faltando.length ? 'insuficiente' : 'reservada') as ReservaDeEstoque['status'],
            criadoEm: new Date().toISOString(),
            itens: procedimento.insumos.map((i) => ({
                produtoId: i.produtoId,
                quantidadeConsumo: i.quantidade,
                unidadeConsumo: i.unidadeConsumo,
            })),
        };
        reservas.unshift(nova);
        return paraReserva(nova);
    },

    async compras(filtro: FiltroDeCompras): Promise<CargaDeCompras> {
        const busca = filtro.busca?.trim() ?? '';
        const todos = pedidos.map(paraPedido);
        const emAberto = todos.filter((p) => p.status === 'rascunho' || p.status === 'enviado' || p.status === 'parcial');
        const recebidos = todos.filter((p) => p.status === 'recebido');

        const combina2 = (p: PedidoDeCompra) => !busca || combina(p.numero, busca) || combina(p.fornecedor, busca)
            || p.itens.some((i) => combina(i.produto, busca));
        const lista = (filtro.aba === 'abertos' ? emAberto : filtro.aba === 'recebidos' ? recebidos : todos).filter(combina2);
        const pagina = paginar(lista, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });

        const sugestoes = sugestoesDeCompra();
        const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
        const aReceber = emAberto.reduce((s, p) => s + (p.totalCentavos - p.recebidoCentavos), 0);
        const itensSugeridos = sugestoes.reduce((s, g) => s + g.itens.length, 0);

        return {
            aba: filtro.aba,
            pedidos: filtro.aba === 'sugestao' ? [] : pagina.itens,
            sugestoes,
            fornecedores: fornecedores.map(paraFornecedor),
            produtos: produtos.map(paraProduto),
            total: filtro.aba === 'sugestao' ? itensSugeridos : pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'abertos', rotulo: 'Pedidos em aberto', valor: String(emAberto.length), apoio: `${reais(aReceber)} por chegar`, icone: 'truck' },
                { id: 'repor', rotulo: 'Itens a repor', valor: String(itensSugeridos), apoio: `${sugestoes.length} ${sugestoes.length === 1 ? 'fornecedor' : 'fornecedores'}`, tendencia: itensSugeridos ? 'down' : 'neutral', icone: 'package-search' },
                { id: 'sugerido', rotulo: 'Compra sugerida', valor: reais(sugestoes.reduce((s, g) => s + g.totalCentavos, 0)), apoio: 'para voltar ao dobro do mínimo', icone: 'circle-dollar-sign' },
                { id: 'recebidos', rotulo: 'Recebidos', valor: String(recebidos.length), apoio: 'no histórico', icone: 'package-check' },
            ],
            totais: { abertos: emAberto.length, recebidos: recebidos.length, todos: todos.length, sugeridos: itensSugeridos },
        };
    },

    async salvarPedido(dados: DadosDoPedido): Promise<PedidoDeCompra> {
        const itens = dados.itens.filter((i) => i.quantidade > 0);
        /* Pedido sem item não é pedido: seria um número de protocolo que nunca
           vira entrada no estoque. */
        if (itens.length === 0) throw new Error('Escolha ao menos um produto para o pedido.');
        if (!dados.fornecedorId) throw new Error('Escolha o fornecedor.');
        const repetido = itens.map((i) => i.produtoId).some((id, indice, lista) => lista.indexOf(id) !== indice);
        if (repetido) throw new Error('O mesmo produto aparece duas vezes no pedido.');

        const existente = dados.id ? pedidos.find((p) => p.id === dados.id) : undefined;
        if (existente && (existente.status === 'recebido' || existente.status === 'parcial')) {
            throw new Error('Este pedido já recebeu mercadoria e não pode mais ser alterado.');
        }

        const agora = new Date().toISOString();
        if (existente) {
            existente.fornecedorId = dados.fornecedorId;
            existente.previsaoEntrega = dados.previsaoEntrega;
            existente.observacoes = dados.observacoes;
            if (dados.enviar && existente.status === 'rascunho') {
                existente.status = 'enviado';
                existente.enviadoEm = agora;
            }
            /* Os itens são trocados inteiros: casar linha a linha com o que já
               estava lá deixaria item órfão no primeiro produto removido. */
            existente.itens = itens.map((i, indice) => ({
                id: `pdi-${sequencia + indice + 1}`,
                produtoId: i.produtoId,
                quantidade: i.quantidade,
                quantidadeRecebida: 0,
                custoUnitarioCentavos: i.custoUnitarioCentavos,
            }));
            sequencia += itens.length;
            return paraPedido(existente);
        }

        sequencia += 1;
        const ano = new Date().getFullYear();
        const criado: PedidoSemente = {
            id: `pdc-${sequencia}`,
            numero: `#${ano}-${String(pedidos.length + 1).padStart(4, '0')}`,
            fornecedorId: dados.fornecedorId,
            status: dados.enviar ? 'enviado' : 'rascunho',
            previsaoEntrega: dados.previsaoEntrega,
            observacoes: dados.observacoes,
            criadoEm: agora,
            criadoPor: sessaoAtual()?.nome,
            enviadoEm: dados.enviar ? agora : undefined,
            itens: itens.map((i, indice) => ({
                id: `pdi-${sequencia}-${indice}`,
                produtoId: i.produtoId,
                quantidade: i.quantidade,
                quantidadeRecebida: 0,
                custoUnitarioCentavos: i.custoUnitarioCentavos,
            })),
        };
        pedidos.unshift(criado);
        return paraPedido(criado);
    },

    async receberPedido(dados: Recebimento): Promise<PedidoDeCompra> {
        const pedido = pedidos.find((p) => p.id === dados.pedidoId);
        if (!pedido) throw new Error('Pedido não encontrado.');
        if (pedido.status === 'cancelado') throw new Error('Pedido cancelado não recebe mercadoria.');

        const chegando = dados.itens.filter((i) => i.quantidade > 0);
        if (chegando.length === 0) throw new Error('Informe o que chegou.');

        const dia = dados.recebidoEm ? `${dados.recebidoEm}T12:00:00-03:00` : new Date().toISOString();
        let valorRecebido = 0;

        for (const recebido of chegando) {
            const item = pedido.itens.find((i) => i.id === recebido.itemId);
            if (!item) continue;
            const falta = item.quantidade - item.quantidadeRecebida;
            /* Receber mais do que se pediu é erro de digitação, não sorte: o
               saldo subiria sem nota que o explique. */
            if (recebido.quantidade > falta) {
                const produto = produtos.find((p) => p.id === item.produtoId);
                throw new Error(`Faltavam ${falta.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} de ${produto?.nome ?? 'um item'}.`);
            }

            item.quantidadeRecebida += recebido.quantidade;
            valorRecebido += recebido.quantidade * item.custoUnitarioCentavos;

            const produto = produtos.find((p) => p.id === item.produtoId);
            if (produto) {
                sequencia += 1;
                movimentosDeEstoque.push({
                    id: `mve-${sequencia}`,
                    produtoId: produto.id,
                    tipo: 'entrada',
                    quantidade: recebido.quantidade,
                    origem: `${fornecedores.find((f) => f.id === pedido.fornecedorId)?.nome ?? 'Fornecedor'} · pedido ${pedido.numero}`,
                    ocorridoEm: dia,
                });
                /* O custo do cadastro passa a ser o da última compra: é ele que
                   alimenta o custo dos procedimentos, e manter o preço velho
                   faria a margem mentir a favor da clínica. */
                produto.custoCentavos = item.custoUnitarioCentavos;
            }
        }

        /* Status é consequência do que chegou, como no banco. */
        const pedidoTotal = pedido.itens.reduce((s, i) => s + i.quantidade, 0);
        const recebidoTotal = pedido.itens.reduce((s, i) => s + i.quantidadeRecebida, 0);
        if (pedido.status !== 'rascunho') {
            pedido.status = recebidoTotal >= pedidoTotal ? 'recebido' : recebidoTotal > 0 ? 'parcial' : 'enviado';
            if (pedido.status === 'recebido') pedido.recebidoEm = pedido.recebidoEm ?? dia;
        }

        if (dados.lancarContaAPagar && valorRecebido > 0) {
            sequencia += 1;
            const fornecedor = fornecedores.find((f) => f.id === pedido.fornecedorId);
            titulos.push({
                id: `tit-${sequencia}`,
                tipo: 'pagar',
                descricao: `Pedido de compra ${pedido.numero}`,
                contraparte: fornecedor?.nome ?? 'Fornecedor',
                categoria: 'Produtos e insumos',
                valorCentavos: valorRecebido,
                pagoCentavos: 0,
                vencimento: diaDo(dia),
                competencia: `${diaDo(dia).slice(0, 7)}-01`,
                parcela: 1,
                parcelasTotal: 1,
                situacao: 'aberto',
            });
        }

        return paraPedido(pedido);
    },

    async cancelarPedido(id: string): Promise<void> {
        const pedido = pedidos.find((p) => p.id === id);
        if (!pedido) throw new Error('Pedido não encontrado.');
        if (pedido.status === 'recebido') throw new Error('Pedido já recebido não se cancela.');
        /* Cancelar não desfaz entrada: o que chegou está no estoque e no
           extrato, e apagar isso seria reescrever o inventário. */
        pedido.status = 'cancelado';
    },

    async documentos(filtro: FiltroDeDocumentos): Promise<CargaDeDocumentos> {
        const busca = filtro.busca?.trim() ?? '';
        const agora = Date.now();

        const emitidos = documentos.map(paraDocumento);
        const aguardando = emitidos.filter((d) => d.status === 'aguardando');
        const assinados = emitidos.filter((d) => d.status === 'assinado');
        const recusados = emitidos.filter((d) => d.status === 'rejeitado');

        const visiveis = emitidos.filter((d) => (!busca || combina(d.titulo, busca) || combina(d.paciente, busca))
            && (filtro.aba !== 'assinaturas' || d.status === 'aguardando')
            && (!filtro.tipos?.length || filtro.tipos.includes(d.tipo))
            && (!filtro.status?.length || filtro.status.includes(d.status)));

        const modelos = modelosDeDocumento.map(paraModeloDeDocumento)
            .filter((m) => (!busca || combina(m.nome, busca)) && (!filtro.favoritos || m.favorito));

        /* Pagina só a lista que a aba mostra — documento e modelo são listas
           diferentes, com contagens diferentes. */
        const visivel: unknown[] = filtro.aba === 'modelos' ? modelos : visiveis;
        const pagina = paginar(visivel, { pagina: filtro.pagina, porPagina: filtro.porPagina ?? 10 });

        return {
            aba: filtro.aba,
            itens: filtro.aba === 'modelos' ? [] : (pagina.itens as Documento[]),
            modelos: filtro.aba === 'modelos' ? (pagina.itens as ModeloDeDocumento[]) : modelos,
            pacientes: pacientes.map((p) => ({ id: p.id, nome: p.nome })),
            procedimentos: procedimentos.filter((p) => p.ativo).map((p) => ({ id: p.id, nome: p.nome })),
            profissionais: membros.filter((m) => m.acesso === 'ativo').map((m) => ({ id: m.id, nome: m.nome })),
            total: pagina.total,
            pagina: pagina.pagina,
            paginas: pagina.paginas,
            indicadores: [
                { id: 'total', rotulo: 'Documentos emitidos', valor: String(emitidos.length), apoio: `${modelosDeDocumento.length} modelos`, icone: 'file-text' },
                { id: 'assinados', rotulo: 'Assinados', valor: String(assinados.length), apoio: emitidos.length ? `${Math.round((assinados.length / emitidos.length) * 100)}% do acervo` : 'nenhum ainda', tendencia: 'up', icone: 'file-check' },
                { id: 'aguardando', rotulo: 'Aguardando assinatura', valor: String(aguardando.length), apoio: 'com a paciente', icone: 'clock' },
                { id: 'recusados', rotulo: 'Recusados', valor: String(recusados.length), apoio: recusados.length ? 'revisar' : 'nenhum', tendencia: recusados.length ? 'down' : 'neutral', icone: 'file-x' },
            ],
            /* Os tipos saem do acervo: lista fixa ofereceria filtro que não
               casa com nada. */
            tipos: [...new Set(emitidos.map((d) => d.tipo))].sort(),
            totais: { todos: emitidos.length, aguardando: aguardando.length, modelos: modelosDeDocumento.length },
            parados: paradosNaAssinatura(agora).map(paraDocumento),
        };
    },

    async salvarModeloDeDocumento(dados: DadosDoModeloDeDocumento): Promise<ModeloDeDocumento> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao modelo.');
        if (modelosDeDocumento.some((m) => m.id !== dados.id && mesmoNome(m.nome, nome))) {
            throw new Error(`Já existe um modelo chamado ${nome}.`);
        }
        /* Publicar sem corpo entregaria à paciente uma folha em branco para
           assinar. */
        if (dados.publicado && !dados.corpo.trim()) throw new Error('Escreva o corpo antes de publicar o modelo.');

        const agora = new Date().toISOString();
        const existente = dados.id ? modelosDeDocumento.find((m) => m.id === dados.id) : undefined;
        if (existente) {
            Object.assign(existente, {
                nome,
                categoria: dados.categoria,
                assinantes: dados.assinantes,
                corpo: dados.corpo,
                publicado: dados.publicado,
                favorito: dados.favorito ?? existente.favorito,
                atualizadoEm: agora,
            });
            return paraModeloDeDocumento(existente);
        }

        sequencia += 1;
        const criado: ModeloSemente = {
            id: `mdd-${sequencia}`,
            nome,
            categoria: dados.categoria,
            assinantes: dados.assinantes,
            corpo: dados.corpo,
            publicado: dados.publicado,
            favorito: dados.favorito ?? false,
            atualizadoEm: agora,
        };
        modelosDeDocumento.unshift(criado);
        return paraModeloDeDocumento(criado);
    },

    async duplicarModeloDeDocumento(id: string): Promise<ModeloDeDocumento> {
        const original = modelosDeDocumento.find((m) => m.id === id);
        if (!original) throw new Error('Modelo não encontrado.');

        sequencia += 1;
        /* A cópia nasce em rascunho: duplicar é para alterar, e o texto que
           ainda vai mudar não pode ser oferecido para assinatura. */
        const copia: ModeloSemente = {
            ...original,
            id: `mdd-${sequencia}`,
            nome: `${original.nome} (cópia)`,
            publicado: false,
            favorito: false,
            atualizadoEm: new Date().toISOString(),
        };
        modelosDeDocumento.unshift(copia);
        return paraModeloDeDocumento(copia);
    },

    async removerModeloDeDocumento(id: string): Promise<{ removido: boolean }> {
        const modelo = modelosDeDocumento.find((m) => m.id === id);
        if (!modelo) throw new Error('Modelo não encontrado.');

        /* Modelo já usado não se apaga: os documentos emitidos apontam para
           ele, e o termo assinado ficaria sem origem. Sai de circulação. */
        if (documentos.some((d) => d.modeloId === id)) {
            modelo.publicado = false;
            modelo.atualizadoEm = new Date().toISOString();
            return { removido: false };
        }
        modelosDeDocumento = modelosDeDocumento.filter((m) => m.id !== id);
        return { removido: true };
    },

    async emitirDocumento(novo: NovoDocumento): Promise<Documento> {
        const modelo = modelosDeDocumento.find((m) => m.id === novo.modeloId);
        if (!modelo) throw new Error('Modelo não encontrado.');
        if (!modelo.publicado) throw new Error('Este modelo ainda é rascunho: publique antes de emitir.');

        const paciente = pacientes.find((p) => p.id === novo.contatoId);
        if (!paciente) throw new Error('Escolha o paciente do documento.');

        const procedimento = novo.procedimentoId ? procedimentos.find((p) => p.id === novo.procedimentoId) : undefined;
        const profissional = novo.profissionalId ? membros.find((m) => m.id === novo.profissionalId) : undefined;
        const agora = new Date().toISOString();
        const ficha = fichas[paciente.id];

        const corpo = preencherCorpo(modelo.corpo, {
            paciente: paciente.nome,
            cpf: ficha?.cpf,
            procedimento: procedimento?.nome,
            valorCentavos: procedimento?.precoCentavos,
            data: diaDo(agora).split('-').reverse().join('/'),
            profissional: profissional?.nome,
            clinica: 'Vecchia — Jardins',
        });

        sequencia += 1;
        const documento: DocumentoSemente = {
            id: `doc-${sequencia}`,
            titulo: modelo.nome,
            tipo: modelo.categoria === 'Formulário' ? 'Anamnese' : modelo.categoria === 'Contrato' ? 'Contrato' : 'Termo de consentimento',
            contatoId: paciente.id,
            paciente: paciente.nome,
            modeloId: modelo.id,
            corpo,
            assinantes: modelo.assinantes,
            canal: novo.enviar ? novo.canal : undefined,
            /* Modelo que não pede assinatura nasce concluído: ficar
               "aguardando" para sempre é fila que ninguém limpa. */
            status: !novo.enviar ? 'rascunho' : modelo.assinantes === 'Não requer assinatura' ? 'assinado' : 'aguardando',
            paginas: Math.max(1, Math.ceil(corpo.length / 1800)),
            criadoEm: agora,
            atualizadoEm: agora,
            enviadoEm: novo.enviar ? agora : undefined,
            assinadoEm: novo.enviar && modelo.assinantes === 'Não requer assinatura' ? agora : undefined,
            profissional: profissional?.nome,
            procedimento: procedimento?.nome,
        };
        documentos.unshift(documento);
        return paraDocumento(documento);
    },

    async decidirDocumento(id: string, status: StatusDoDocumento, motivo?: string): Promise<Documento> {
        const documento = documentos.find((d) => d.id === id);
        if (!documento) throw new Error('Documento não encontrado.');
        if (status === 'rejeitado' && !motivo?.trim()) {
            /* Recusa sem motivo é status vermelho que não explica nada — e
               quem for cobrar a paciente amanhã precisa saber o porquê. */
            throw new Error('Diga por que o documento foi recusado.');
        }

        const agora = new Date().toISOString();
        documento.status = status;
        documento.atualizadoEm = agora;
        if (status === 'assinado') documento.assinadoEm = agora;
        if (status === 'rejeitado') {
            documento.recusadoEm = agora;
            documento.motivoDaRecusa = motivo?.trim();
        }
        return paraDocumento(documento);
    },

    async relatorios(filtro: FiltroDeRelatorios): Promise<CargaDeRelatorios> {
        /* O relatório lê a MESMA agenda das outras telas, com o estado do
           atendimento por cima: consulta aberta e encerrada entra com a duração
           que teve, não com a que estava prevista. */
        const linhas: AgendamentoDoRelatorio[] = agenda.map((a) => {
            const inicio = iniciosDeAtendimento.get(a.id);
            const fim = finaisDeAtendimento.get(a.id);
            const duracaoRealMin = inicio && fim
                ? Math.max(1, Math.round((new Date(fim).getTime() - new Date(inicio).getTime()) / 60000))
                : undefined;
            return {
                inicioEm: a.inicioEm,
                duracaoMin: a.duracaoMin,
                status: estadoDoAtendimento.get(a.id) === 'concluido' ? 'concluido' : a.status,
                procedimento: a.procedimento,
                profissional: a.profissional,
                duracaoRealMin,
                comFicha: (FICHAS_DO_ATENDIMENTO[a.id]?.length ?? 0) > 0
                    || fichasClinicas.some((f) => f.atendimentoId === a.id),
            };
        });

        const carga = montarRelatorio(
            { agendamentos: linhas, precos: PRECOS, movimentacoes, leads, orcamentos, jornada: horarios },
            filtro,
            diaDeHoje(),
        );

        return {
            ...carga,
            profissionais: [...new Set(agenda.map((a) => a.profissional).filter(Boolean))].sort(),
        };
    },

    async configuracoes(aba: AbaDeConfiguracoes): Promise<CargaDeConfiguracoes> {
        return {
            aba,
            clinica: { ...clinica },
            unidades: copiar(unidadesAtivasDaDemonstracao()),
            horarios: horarios.map((d) => ({ ...d, faixas: copiar(d.faixas) })),
            horasSemanais: horasSemanais(horarios),
            salas: copiar(salas),
            equipe: copiar(membros),
            lembretes: ordenarRegua(lembretes),
            canais: canais.map(paraCanalDeIntegracao),
            pareamentoDisponivel: provedoresDeCanal.some((c) => c.provedor === 'uazapi' && c.ativo),
            vagas: { limite: limiteDeMembrosDaDemo('clinica-vecchia', clinica.plano), ocupadas: vagasOcupadasDaDemo() },
        };
    },

    async salvarDadosDaClinica(mudancas: EdicaoDaClinica): Promise<DadosDaClinica> {
        clinica = { ...clinica, ...mudancas };
        return { ...clinica };
    },

    async salvarUnidade(id: string, mudancas: EdicaoDaUnidade): Promise<UnidadeDaClinica> {
        const unidade = unidades.find((u) => u.id === id);
        if (!unidade) throw new Error(`Unidade ${id} não existe.`);
        Object.assign(unidade, mudancas);
        return { ...unidade };
    },

    async salvarHorarios(dias: DiaDeFuncionamento[]): Promise<DiaDeFuncionamento[]> {
        const normalizados = normalizarDias(dias);
        /* A recusa é aqui e no banco: faixas cruzadas contariam a mesma hora
           duas vezes na ocupação, e o total sairia maior que o expediente. */
        for (const dia of normalizados) {
            const cruzada = faixaSobreposta(dia.faixas);
            if (cruzada) throw new Error(`O horário ${cruzada.inicio} se sobrepõe ao anterior.`);
        }
        horarios = normalizados;
        return horarios.map((d) => ({ ...d, faixas: copiar(d.faixas) }));
    },

    async automacoes(aba: AbaDeAutomacoes): Promise<CargaDeAutomacoes> {
        const lista = automacoes.map((a) => montarAutomacao(a, alcanceDaAutomacao(a.gatilho), canalConectado()));
        const daAba = lista.filter((a) => aba === 'todas' || (aba === 'ativas' ? a.ativa : !a.ativa));
        return {
            aba,
            automacoes: daAba,
            indicadores: indicadoresDeAutomacoes(lista),
            canalConectado: canalConectado(),
            totais: {
                ativas: lista.filter((a) => a.ativa).length,
                pausadas: lista.filter((a) => !a.ativa).length,
                todas: lista.length,
            },
        };
    },

    async alternarAutomacao(id: string, ativa: boolean): Promise<Automacao> {
        const automacao = automacoes.find((a) => a.id === id);
        if (!automacao) throw new Error(`Automação ${id} não existe.`);
        automacao.ativa = ativa;
        return montarAutomacao(automacao, alcanceDaAutomacao(automacao.gatilho), canalConectado());
    },

    async executarAutomacao(id: string): Promise<ResultadoDaExecucao> {
        const automacao = automacoes.find((a) => a.id === id);
        if (!automacao) throw new Error(`Automação ${id} não existe.`);
        const montada = montarAutomacao(automacao, alcanceDaAutomacao(automacao.gatilho), canalConectado());
        if (!montada.executavel) throw new Error(montada.bloqueio ?? 'Esta automação não pode ser executada agora.');

        let criadas = 0;
        let repetidas = 0;
        for (const titulo of titulosDeTarefa(automacao.gatilho)) {
            /* Executar de novo no mesmo dia não duplica: a tarefa aberta que a
               execução anterior criou já cobre o caso. */
            if (tarefas.some((t) => t.titulo === titulo && !t.concluida)) {
                repetidas += 1;
                continue;
            }
            tarefas.unshift({
                id: `t-auto-${automacao.id}-${criadas}-${Date.now()}`,
                titulo,
                venceEm: diaDeHoje(),
                responsavel: 'Recepção',
                prioridade: 'media',
                concluida: false,
            });
            criadas += 1;
        }

        automacao.execucoes += 1;
        automacao.ultimaExecucaoEm = new Date().toISOString();
        return {
            criadas,
            repetidas,
            descricao: criadas
                ? `${criadas} ${criadas === 1 ? 'tarefa criada' : 'tarefas criadas'} para a equipe`
                : 'Nada a fazer: as tarefas desta regra já estão abertas',
        };
    },

    async marketing(aba: AbaDeMarketing): Promise<CargaDeMarketing> {
        const lista = campanhas.map(paraCampanha);
        const origens = origensDoMes();
        const publicos = publicosDisponiveis({
            'sem-retorno': tamanhoDoPublico('sem-retorno', { dias: 90 }),
            'aniversariantes': tamanhoDoPublico('aniversariantes', {}),
            'por-origem': tamanhoDoPublico('por-origem', { origem: origens[0]?.origem }),
            'todos-pacientes': tamanhoDoPublico('todos-pacientes', {}),
        });

        return {
            aba,
            campanhas: aba === 'ativas' ? lista.filter((c) => c.status === 'ativa') : lista,
            publicos,
            origens,
            indicadores: indicadoresDeMarketing(lista, origens),
            canalConectado: canalConectado(),
            totais: {
                campanhas: lista.length,
                ativas: lista.filter((c) => c.status === 'ativa').length,
                publicos: publicos.length,
            },
        };
    },

    async plataforma(): Promise<CargaDaPlataforma> {
        const clinicas = copiar(CLINICAS_DA_PLATAFORMA);
        return {
            clinicas,
            usuarios: [...usuariosDaPlataforma(), ...[...proprietariasDasClinicasNovas.values()].map((u) => ({ ...u }))],
            auditoria: auditoriaDaPlataforma.map((a) => ({ ...a })),
            /* Nenhuma impersonação aberta: a lista existe para provar que o
               poder de entrar deixa rastro, e vazia é o estado saudável. */
            impersonacoes: [],
            indicadores: indicadoresDaPlataforma(clinicas),
        };
    },

    async clinicaDaPlataforma(id: string): Promise<CargaDaClinicaNaPlataforma> {
        const clinica = CLINICAS_DA_PLATAFORMA.find((c) => c.id === id);
        if (!clinica) throw new Response('Clínica não encontrada', { status: 404 });
        const proprietaria = proprietariasDasClinicasNovas.get(id);
        const daClinica = [
            ...usuariosDaPlataforma().filter((u) => u.clinica === clinica.nome),
            ...(proprietaria ? [{ ...proprietaria }] : []),
        ];
        const cobranca = await this.faturamento();
        const assinatura = cobranca.assinaturas.find((a) => a.clinicaId === id);
        return {
            clinica: { ...clinica },
            /* Só a clínica de demonstração tem unidade na semente; a aberta pelo
               painel traz a dela. As salas são as da demonstração inteira. */
            unidades: clinica.id === 'clinica-vecchia'
                ? unidades.map((u) => ({ ...u, salas: salas.filter((s) => s.ativa).length }))
                : (unidadesDasClinicasNovas.get(id) ?? []).map((u) => ({ ...u })),
            usuarios: daClinica,
            auditoria: auditoriaDaPlataforma.filter((a) => (a.clinicaId ? a.clinicaId === id : a.clinica === clinica.nome)).map((a) => ({ ...a })),
            impersonacoes: [],
            planos: cobranca.planos.filter((p) => p.ativo),
            ...(assinatura ? { assinatura } : {}),
            faturas: cobranca.faturas.filter((f) => f.clinicaId === id),
            creditos: creditosDaDemonstracao.filter((c) => c.clinicaId === id).map(paraCreditoDaDemo).reverse(),
            descontos: descontosDaDemonstracao.filter((d) => d.clinicaId === id).map(paraDescontoDaDemo),
            /* Sem chave de IA na demonstração, ninguém perguntou nada. */
            consumoDeIa: [],
            vagas: {
                limite: limiteDeMembrosDaDemo(id, clinica.plano),
                ocupadas: id === 'clinica-vecchia' ? vagasOcupadasDaDemo() : clinica.membrosAtivos,
                folgaDoCadastro: folgasDeMembros.get(id) ?? null,
                limiteDoPlano: PLANOS_SEMENTE.find((p) => p.codigo === clinica.plano)?.limiteMembros ?? null,
            },
        };
    },

    async faturamento(): Promise<CargaDoFaturamento> {
        const assinaturas = assinaturasDaBase();
        const lista = faturas.map(paraFatura);
        const comContagem: Plano[] = planos.map((p) => ({
            ...p,
            clinicas: assinaturas.filter((a) => a.planoCodigo === p.codigo && !a.canceladaEm).length,
        }));
        return {
            planos: comContagem,
            assinaturas,
            faturas: lista,
            indicadores: indicadoresDoFaturamento(comContagem, assinaturas, lista),
            temPreco: planos.some((p) => p.precoCentavos !== undefined),
        };
    },

    async salvarPlano(id: string | null, dados: DadosDoPlano): Promise<Plano> {
        exigirOperador('planos.editar', 'Só quem edita o catálogo de planos muda um plano.');
        const codigo = dados.codigo.trim();
        if (!codigo) throw new Error('O plano precisa de um código.');
        if (planos.some((p) => p.id !== id && p.codigo === codigo)) {
            throw new Error(`Já existe um plano com o código ${codigo}.`);
        }
        const base: PlanoSemente = {
            id: id ?? `plano-${Date.now()}`,
            codigo,
            nome: dados.nome.trim() || codigo,
            ciclo: dados.ciclo,
            ...(dados.precoCentavos === null || dados.precoCentavos === undefined
                ? {}
                : { precoCentavos: dados.precoCentavos }),
            ...(dados.limiteUnidades === undefined ? {} : { limiteUnidades: dados.limiteUnidades }),
            ...(dados.limiteMembros === undefined ? {} : { limiteMembros: dados.limiteMembros }),
            ativo: dados.ativo,
        };
        if (!id) planos.push(base);
        else {
            const indice = planos.findIndex((p) => p.id === id);
            if (indice < 0) throw new Error(`Plano ${id} não existe.`);
            planos[indice] = base;
        }
        const assinaturas = assinaturasDaBase();
        return { ...base, clinicas: assinaturas.filter((a) => a.planoCodigo === base.codigo && !a.canceladaEm).length };
    },

    async gerarFaturas(competencia: string): Promise<{ criadas: number }> {
        exigirOperador('faturamento.gerenciar', 'Só quem gerencia o faturamento gera faturas.');
        if (!/^\d{4}-\d{2}$/.test(competencia)) throw new Error('Competência deve ser YYYY-MM.');
        let criadas = 0;
        for (const a of assinaturasDaBase()) {
            /* Trial não paga ainda, cancelada não paga mais, e plano sem preço
               não vira fatura: cobrança de valor que ninguém decidiu. */
            if (a.canceladaEm || a.precoCentavos === undefined) continue;
            if (a.situacao !== 'ativa' && a.situacao !== 'inadimplente') continue;
            if (faturas.some((f) => f.clinicaId === a.clinicaId && f.competencia === competencia && (f.tipo ?? 'assinatura') === 'assinatura')) continue;
            /* A conta é a de `gerar_faturas` (0092): preço → desconto → créditos,
               do mais antigo ao mais novo. */
            const creditos = creditosDaDemonstracao.filter((c) => c.clinicaId === a.clinicaId && !c.excluidoEm && c.saldoCentavos > 0);
            const conta = valorDaFatura(
                a.precoCentavos,
                descontoVigenteNa(a.clinicaId, competencia)?.percentual ?? null,
                creditos.map((c) => c.saldoCentavos),
            );
            creditos.forEach((c, i) => { c.saldoCentavos -= conta.consumos[i] ?? 0; });
            faturas.push({
                id: `fat-${a.clinicaId}-${competencia}`,
                clinicaId: a.clinicaId,
                competencia,
                vencimento: `${competencia}-10`,
                valorCentavos: conta.valorCentavos,
                tipo: 'assinatura',
                descontoCentavos: conta.descontoCentavos,
                creditoCentavos: conta.creditoCentavos,
            });
            criadas += 1;
        }
        return { criadas };
    },

    async baixarFatura(id: string, pagoEm: string | null): Promise<Fatura> {
        exigirOperador('financeiro.operar', 'Só quem registra pagamento dá baixa em fatura.');
        const fatura = faturas.find((f) => f.id === id);
        if (!fatura) throw new Error(`Fatura ${id} não existe.`);
        if (pagoEm) fatura.pagoEm = pagoEm;
        else delete fatura.pagoEm;
        return paraFatura(fatura);
    },

    async onboarding(): Promise<CargaDoOnboarding> {
        /* Na demonstração, só a clínica de exemplo tem operação — e é por isso
           que ela é a única com etapas concluídas. O canal nunca está pareado,
           então nenhuma fecha as sete. */
        const lista: OnboardingDaClinica[] = CLINICAS_DA_PLATAFORMA.map((c) => {
            const daCasa = c.id === 'clinica-vecchia';
            const etapas = etapasDe({
                temCadastro: daCasa,
                temEndereco: daCasa,
                temJornada: daCasa && horarios.some((d) => d.faixas.length > 0),
                temLogin: c.membrosAtivos > 0,
                temProcedimentos: daCasa,
                temCanal: canalConectado(),
                temAtendimento: daCasa && agenda.some((a) => a.status === 'concluido'),
            });
            return {
                clinicaId: c.id,
                clinica: c.nome,
                situacao: c.situacao,
                criadoEm: c.criadoEm,
                etapas,
                concluidas: etapas.filter((e) => e.concluida).length,
                total: etapas.length,
            };
        });
        return { clinicas: lista, indicadores: indicadoresDoOnboarding(lista) };
    },

    async suporte(): Promise<CargaDoSuporte> {
        const lista = chamados.map(paraChamado);
        return {
            chamados: lista,
            indicadores: indicadoresDoSuporte(lista),
            clinicas: CLINICAS_DA_PLATAFORMA.map((c) => ({ id: c.id, nome: c.nome })),
            /* A demonstração não abre impersonação no banco: nenhuma sessão a listar. */
            impersonacoes: [],
        };
    },

    async salvarChamado(id: string | null, dados: DadosDoChamado): Promise<Chamado> {
        exigirOperador('suporte.atender', 'Só quem atende o suporte registra chamado.');
        const assunto = dados.assunto.trim();
        if (!assunto) throw new Error('O chamado precisa de um assunto.');
        if (!dados.clinicaId) throw new Error('O chamado precisa de uma clínica.');

        const agora = new Date().toISOString();
        const base: ChamadoSemente = {
            id: id ?? `ch-${Date.now()}`,
            clinicaId: dados.clinicaId,
            assunto,
            descricao: dados.descricao,
            canal: dados.canal,
            prioridade: dados.prioridade,
            situacao: dados.situacao,
            ...(dados.responsavelNome ? { responsavelNome: dados.responsavelNome } : {}),
            abertoEm: id ? (chamados.find((c) => c.id === id)?.abertoEm ?? agora) : agora,
            /* Resolvido sem data é chamado que ninguém sabe quando fechou — e o
               tempo de resposta sai dessa diferença. A data entra aqui, no
               momento em que a situação vira resolvido. */
            ...(dados.situacao === 'resolvido'
                ? { resolvidoEm: chamados.find((c) => c.id === id)?.resolvidoEm ?? agora }
                : {}),
        };

        if (!id) chamados.unshift(base);
        else {
            const indice = chamados.findIndex((c) => c.id === id);
            if (indice < 0) throw new Error(`Chamado ${id} não existe.`);
            chamados[indice] = base;
        }
        return paraChamado(base);
    },

    async flags(): Promise<CargaDasFlags> {
        return {
            flags: flags.map((f) => ({
                ...f,
                excecoes: copiar(f.excecoes),
                ligadas: CLINICAS_DA_PLATAFORMA.filter((c) => flagLigadaPara(f, c.id)).length,
                total: CLINICAS_DA_PLATAFORMA.length,
            })),
            clinicas: CLINICAS_DA_PLATAFORMA.map((c) => ({ id: c.id, nome: c.nome })),
        };
    },

    async definirPadraoDaFlag(id: string, ativo: boolean): Promise<FlagDeModulo> {
        exigirOperador('flags.editar', 'Só quem edita as flags muda o padrão.');
        const flag = flags.find((f) => f.id === id);
        if (!flag) throw new Error(`Flag ${id} não existe.`);
        flag.ativoPadrao = ativo;
        return { ...flag, excecoes: copiar(flag.excecoes) };
    },

    async definirExcecaoDaFlag(id: string, clinicaId: string, ativo: boolean | null): Promise<FlagDeModulo> {
        exigirOperador('flags.editar', 'Só quem edita as flags muda a exceção de uma clínica.');
        const flag = flags.find((f) => f.id === id);
        if (!flag) throw new Error(`Flag ${id} não existe.`);
        /* Nulo REMOVE a exceção: voltar ao padrão é diferente de gravar uma
           exceção que por acaso coincide com ele — a segunda não acompanha o
           padrão quando ele muda. */
        if (ativo === null) {
            flag.excecoes = flag.excecoes.filter((e) => e.clinicaId !== clinicaId);
            return { ...flag, excecoes: copiar(flag.excecoes) };
        }
        const clinica = CLINICAS_DA_PLATAFORMA.find((c) => c.id === clinicaId);
        if (!clinica) throw new Error(`Clínica ${clinicaId} não existe.`);
        const atual = flag.excecoes.find((e) => e.clinicaId === clinicaId);
        if (atual) atual.ativo = ativo;
        else flag.excecoes.push({ clinicaId, clinica: clinica.nome, ativo });
        return { ...flag, excecoes: copiar(flag.excecoes) };
    },

    async canaisDaClinica(): Promise<CanalDeIntegracao[]> {
        return canais.map(paraCanalDeIntegracao);
    },

    async salvarCanal(id: string | null, dados: DadosDoCanal): Promise<CanalDeIntegracao> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O canal precisa de um nome.');
        const destino = conferirDestinoDoCanal(dados);

        if (!id) {
            const novo: Canal = {
                id: `canal-${Date.now()}`,
                nome,
                tipo: dados.tipo,
                provedor: dados.provedor,
                ...(dados.numero ? { numero: dados.numero } : {}),
                status: 'desconectado',
                conversasAbertas: 0,
                naoLidas: 0,
            };
            canais.push(novo);
            destinoDoCanal.set(novo.id, destino);
            return paraCanalDeIntegracao(novo);
        }
        const canal = canais.find((c) => c.id === id);
        if (!canal) throw new Error(`Canal ${id} não existe.`);
        canal.nome = nome;
        canal.tipo = dados.tipo;
        canal.provedor = dados.provedor;
        if (dados.numero) canal.numero = dados.numero;
        destinoDoCanal.set(canal.id, destino);
        return paraCanalDeIntegracao(canal);
    },

    async salvarCredencialDoCanal(canalId: string, credenciais: Record<string, string>): Promise<CanalDeIntegracao> {
        const canal = canais.find((c) => c.id === canalId);
        if (!canal) throw new Error(`Canal ${canalId} não existe.`);
        if (Object.values(credenciais).every((v) => !v?.trim())) {
            throw new Error('Informe as credenciais do canal.');
        }
        const anterior = credenciaisDeCanal.get(canalId);
        const agora = new Date().toISOString();
        if (credenciais.senha === 'errada') senhaDeEmailRecusada.add(canalId);
        else senhaDeEmailRecusada.delete(canalId);
        credenciaisDeCanal.set(canalId, anterior
            ? { criadoEm: anterior.criadoEm, rotacionadoEm: agora }
            : { criadoEm: agora });
        /* "Conectando", e não "conectado": ter credencial não é ter sessão. E
           só onde há quem confirme — nos provedores com que a VecchIA ainda não
           fala, o canal esperaria para sempre (a mesma regra da `0070`). */
        if (canal.status !== 'conectado' && !indisponibilidadeDoProvedor(canal.provedor)) canal.status = 'conectando';
        delete canal.ultimoErro;
        return paraCanalDeIntegracao(canal);
    },

    async removerCredencialDoCanal(canalId: string): Promise<CanalDeIntegracao> {
        const canal = canais.find((c) => c.id === canalId);
        if (!canal) throw new Error(`Canal ${canalId} não existe.`);
        credenciaisDeCanal.delete(canalId);
        canal.status = 'desconectado';
        delete canal.ultimaConexaoEm;
        return paraCanalDeIntegracao(canal);
    },

    async validarCanalDeEmail(canalId: string): Promise<{ status: StatusDoCanal; motivo?: string }> {
        const canal = canais.find((c) => c.id === canalId);
        if (!canal) throw new Error('Canal não encontrado, ou fora do seu acesso.');
        if (canal.provedor !== 'smtp') throw new Error(`Só o canal de e-mail (SMTP) se valida assim. Este canal é ${canal.provedor}.`);
        if (!credenciaisDeCanal.has(canalId)) throw new Error('Este canal ainda não tem os dados do e-mail. Preencha e salve antes de testar.');
        if (senhaDeEmailRecusada.has(canalId)) {
            canal.status = 'erro';
            canal.ultimoErro = 'O servidor recusou o usuário ou a senha.';
            return { status: 'erro', motivo: canal.ultimoErro };
        }
        canal.status = 'conectado';
        canal.ultimaConexaoEm = new Date().toISOString();
        delete canal.ultimoErro;
        return { status: 'conectado' };
    },

    async pareamentoDisponivel(): Promise<boolean> {
        /* A mesma regra do banco: depende de a plataforma ter ligado o
           servidor, e não de estar em demonstração. */
        return provedoresDeCanal.some((c) => c.provedor === 'uazapi' && c.ativo);
    },

    async parearCanal(canalId: string, acao: AcaoDePareamento): Promise<PareamentoDoCanal> {
        const canal = canais.find((c) => c.id === canalId);
        if (!canal) throw new Error(`Canal ${canalId} não existe.`);
        if (canal.provedor !== 'uazapi') {
            throw new Error(`O pareamento por QR é da UAZAPI. Este canal é ${canal.provedor}.`);
        }

        const instancia = canal.instanciaExternaId ?? `vecchia-${canalId.slice(0, 8)}`;

        if (acao === 'desconectar') {
            canal.status = 'desconectado';
            delete canal.ultimaConexaoEm;
            leiturasDoPareamento.delete(canalId);
            return { instancia, status: 'desconectado' };
        }

        if (acao === 'criar' || acao === 'conectar') {
            canal.instanciaExternaId = instancia;
            canal.status = 'conectando';
            delete canal.ultimoErro;
            credenciaisDeCanal.set(canalId, { criadoEm: new Date().toISOString() });
            leiturasDoPareamento.set(canalId, 0);
            return { instancia, status: 'conectando', qrcode: QR_DE_DEMONSTRACAO };
        }

        if (!credenciaisDeCanal.has(canalId)) {
            throw new Error('Este canal ainda não tem instância. Gere o QR Code para criar uma.');
        }

        /* A demonstração não tem um WhatsApp do outro lado, então o pareamento
           não pode vir de uma leitura de verdade. Ele acontece na terceira
           consulta — tempo suficiente para a tela mostrar o QR, a contagem e a
           virada, que é o que se quer conferir aqui. */
        const lidas = (leiturasDoPareamento.get(canalId) ?? 0) + 1;
        leiturasDoPareamento.set(canalId, lidas);

        if (lidas < 3) {
            return { instancia, status: 'conectando', qrcode: QR_DE_DEMONSTRACAO };
        }

        canal.status = 'conectado';
        canal.ultimaConexaoEm = new Date().toISOString();
        canal.numero = canal.numero ?? '5511988887777';
        return {
            instancia,
            status: 'conectado',
            numero: canal.numero,
            perfil: 'Clínica Vecchia',
        };
    },

    async provedoresDeCanalDaPlataforma(): Promise<ProvedorDeCanalDaPlataforma[]> {
        return copiar(provedoresDeCanal);
    },

    async salvarProvedorDeCanal(provedor: string, baseUrl: string, token: string): Promise<ProvedorDeCanalDaPlataforma[]> {
        /* As mesmas recusas do banco, para a demonstração errar igual. */
        if (!/^https:\/\//.test(baseUrl.trim())) {
            throw new Error('O endereço do servidor precisa começar com https://');
        }
        if (token.trim().length < 16) {
            throw new Error('Este token é curto demais para ser um token de administração.');
        }
        const registro: ProvedorDeCanalDaPlataforma = {
            provedor,
            baseUrl: baseUrl.trim().replace(/\/+$/, ''),
            temToken: true,
            dica: token.trim().slice(-4),
            ativo: true,
            atualizadoEm: new Date().toISOString(),
        };
        const atual = provedoresDeCanal.findIndex((c) => c.provedor === provedor);
        if (atual >= 0) provedoresDeCanal[atual] = registro;
        else provedoresDeCanal.push(registro);
        return copiar(provedoresDeCanal);
    },

    async removerProvedorDeCanal(provedor: string): Promise<ProvedorDeCanalDaPlataforma[]> {
        provedoresDeCanal = provedoresDeCanal.filter((c) => c.provedor !== provedor);
        return copiar(provedoresDeCanal);
    },

    async chavesDeIa(): Promise<ChaveDeIa[]> {
        return copiar(chavesDeIa);
    },

    async salvarChaveDeIa(provedor: ProvedorDeIa, chave: string, modelo?: string): Promise<ChaveDeIa[]> {
        /* A mesma recusa do banco. */
        if (chave.trim().length < 16) throw new Error('Esta chave é curta demais para ser uma chave de API.');
        const atual = chavesDeIa.find((c) => c.provedor === provedor);
        const registro: ChaveDeIa = {
            provedor,
            temChave: true,
            /* Só os quatro últimos: com eles se reconhece a chave, não se usa. */
            dica: chave.trim().slice(-4),
            ...(modelo ? { modelo } : atual?.modelo ? { modelo: atual.modelo } : {}),
            ativo: true,
            atualizadoEm: new Date().toISOString(),
        };
        chavesDeIa = [...chavesDeIa.filter((c) => c.provedor !== provedor), registro]
            .sort((a, b) => a.provedor.localeCompare(b.provedor));
        return copiar(chavesDeIa);
    },

    async removerChaveDeIa(provedor: ProvedorDeIa): Promise<ChaveDeIa[]> {
        chavesDeIa = chavesDeIa.filter((c) => c.provedor !== provedor);
        return copiar(chavesDeIa);
    },

    /* A demonstração tem tetos para a tela ter o que mostrar — inclusive um
       estourado, porque a linha vermelha é justamente a que precisa ser vista
       antes de existir de verdade. */
    async baseDaClinica(): Promise<DocumentoDaBase[]> {
        return copiar(baseDaDemonstracao);
    },

    async adicionarNaBase({ titulo, texto, arquivo }: { titulo: string; texto?: string; arquivo?: File }): Promise<DocumentoDaBase> {
        sequencia += 1;
        /* A demonstração não vetoriza nada — não há chave nem servidor. O
           documento nasce PRONTO com uma contagem plausível para a tela ter o
           que mostrar, e a contagem sai do tamanho do texto porque um número
           redondo inventado pareceria enfeite. */
        const conteudo = texto ?? '';
        const documento: DocumentoDaBase = {
            id: `base-${String(sequencia)}`,
            titulo: titulo.trim(),
            tipo: arquivo ? (arquivo.type || 'application/octet-stream') : 'texto',
            bytes: arquivo ? arquivo.size : conteudo.length,
            situacao: 'pronto',
            trechos: Math.max(1, Math.ceil((arquivo ? arquivo.size : conteudo.length) / 1200)),
            criadoEm: new Date().toISOString(),
        };
        baseDaDemonstracao.unshift(documento);
        return { ...documento };
    },

    async prepararDocumentoDaBase(id: string): Promise<DocumentoDaBase> {
        const doc = baseDaDemonstracao.find((d) => d.id === id);
        if (!doc) throw new Error('Documento não encontrado.');
        doc.situacao = 'pronto';
        delete doc.erro;
        return { ...doc };
    },

    async removerDaBase(id: string): Promise<void> {
        baseDaDemonstracao = baseDaDemonstracao.filter((d) => d.id !== id);
    },

    async orcamentosDeIa(): Promise<OrcamentoDeIa[]> {
        return orcamentosDaDemonstracao.map((o) => ({
            ...o,
            estourado: o.limiteTokensMes !== null && o.tokensDoMes >= o.limiteTokensMes,
        }));
    },

    async salvarOrcamentoDeIa(clinicaId: string, limite: number | null): Promise<void> {
        exigirOperador('ia.tetos', 'Só quem define teto de IA muda o teto de uma clínica.');
        const alvo = orcamentosDaDemonstracao.find((o) => o.clinicaId === clinicaId);
        if (alvo) alvo.limiteTokensMes = limite;
    },

    async meuOrcamentoDeIa(): Promise<MeuOrcamentoDeIa | null> {
        const meu = orcamentosDaDemonstracao[0];
        if (!meu) return null;
        return {
            limiteTokensMes: meu.limiteTokensMes,
            tokensDoMes: meu.tokensDoMes,
            estourado: meu.limiteTokensMes !== null && meu.tokensDoMes >= meu.limiteTokensMes,
        };
    },

    async iaDisponivel(): Promise<boolean> {
        /* Na demonstração não há chave nem servidor para falar com provedor
           nenhum — e responder "sim" faria a tela oferecer um teste que só sabe
           errar. */
        return false;
    },

    async retornosEmAberto(dias = 90): Promise<RetornoEmAberto[]> {
        const hoje = diaDeHoje();
        return semRetorno(dias).map((nome) => {
            const paciente = pacientes.find((p) => p.nome === nome);
            const dele = agenda
                .filter((a) => a.paciente === nome && a.status !== 'bloqueio' && a.status !== 'cancelado'
                    && diaDo(a.inicioEm) <= hoje)
                .sort((a, b) => a.inicioEm.localeCompare(b.inicioEm));
            const ultimo = dele[dele.length - 1];
            const quando = ultimo ? diaDo(ultimo.inicioEm) : hoje;
            return {
                contatoId: paciente?.id ?? nome,
                nome,
                telefone: paciente?.telefone ?? '',
                ultimoAtendimentoEm: quando,
                diasSemRetorno: Math.round(
                    (new Date(`${hoje}T12:00:00-03:00`).getTime() - new Date(`${quando}T12:00:00-03:00`).getTime()) / 86400000,
                ),
                ultimoProcedimento: ultimo?.procedimento ?? '—',
            };
        }).sort((a, b) => b.diasSemRetorno - a.diasSemRetorno);
    },

    async agentes(): Promise<Agente[]> {
        return agentes
            .filter((a) => a.tipo !== 'copiloto')
            .map((a) => ({ ...a, ferramentas: [...a.ferramentas], limites: { ...a.limites } }));
    },

    async salvarAgente(id: string | null, dados: DadosDoAgente): Promise<Agente> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O agente precisa de um nome.');
        if (agentes.some((a) => a.id !== id && a.nome.toLowerCase() === nome.toLowerCase())) {
            throw new Error(`Já existe um agente chamado ${nome}.`);
        }

        const base: Agente = {
            id: id ?? `agente-${Date.now()}`,
            tipo: dados.tipo,
            nome,
            ativo: dados.ativo,
            tom: dados.tom,
            missao: dados.missao,
            instrucoes: dados.instrucoes,
            temperatura: dados.temperatura,
            ...(dados.modelo ? { modelo: dados.modelo } : {}),
            ferramentas: [...dados.ferramentas],
            ...(dados.saudacao ? { saudacao: dados.saudacao } : {}),
            limites: dados.limites ?? {},
            atualizadoEm: new Date().toISOString(),
        };

        if (!id) agentes.push(base);
        else {
            const indice = agentes.findIndex((a) => a.id === id);
            if (indice < 0) throw new Error(`Agente ${id} não existe.`);
            agentes[indice] = base;
        }
        return { ...base, ferramentas: [...base.ferramentas], limites: { ...base.limites } };
    },

    async removerAgente(id: string): Promise<{ removido: boolean }> {
        const antes = agentes.length;
        agentes = agentes.filter((a) => a.id !== id);
        return { removido: agentes.length < antes };
    },

    async conversarComAgente(): Promise<RespostaDoAgente> {
        throw new Error('O agente precisa de uma chave de IA configurada na plataforma.');
    },

    async confirmarPropostaDoAgente(proposta: PropostaDoAgente): Promise<{ descricao: string }> {
        /* A confirmação NÃO depende do provedor: ela executa pelos mesmos
           caminhos da tela, e por isso funciona igual na demonstração. */
        return executarProposta(this, proposta);
    },

    async ferramentasDaBorda(): Promise<{ id: string; escreve: boolean }[]> {
        return [];
    },

    async fluxos(): Promise<Fluxo[]> {
        return fluxosDaClinica.map((f) => ({ ...f, passos: JSON.parse(JSON.stringify(f.passos)) }));
    },

    async salvarFluxo(id: string | null, dados: DadosDoFluxo): Promise<Fluxo> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O fluxo precisa de um nome.');
        if (fluxosDaClinica.some((f) => f.id !== id && f.nome.toLowerCase() === nome.toLowerCase())) {
            throw new Error(`Já existe um fluxo chamado ${nome}.`);
        }

        const base: Fluxo = {
            id: id ?? `fluxo-${Date.now()}`,
            nome,
            descricao: dados.descricao,
            gatilho: dados.gatilho,
            passos: dados.passos,
            ativo: dados.ativo,
            execucoes: fluxosDaClinica.find((f) => f.id === id)?.execucoes ?? 0,
            ...(fluxosDaClinica.find((f) => f.id === id)?.ultimaExecucaoEm
                ? { ultimaExecucaoEm: fluxosDaClinica.find((f) => f.id === id)?.ultimaExecucaoEm as string }
                : {}),
            atualizadoEm: new Date().toISOString(),
        };

        if (!id) fluxosDaClinica.push(base);
        else {
            const indice = fluxosDaClinica.findIndex((f) => f.id === id);
            if (indice < 0) throw new Error(`Fluxo ${id} não existe.`);
            fluxosDaClinica[indice] = base;
        }
        return { ...base };
    },

    async removerFluxo(id: string): Promise<{ removido: boolean }> {
        const antes = fluxosDaClinica.length;
        fluxosDaClinica = fluxosDaClinica.filter((f) => f.id !== id);
        return { removido: fluxosDaClinica.length < antes };
    },

    async simularFluxo(id: string): Promise<SimulacaoDoFluxo> {
        const fluxo = fluxosDaClinica.find((f) => f.id === id);
        if (!fluxo) throw new Error(`Fluxo ${id} não existe.`);
        return simular(this, fluxo);
    },

    async executarFluxo(id: string): Promise<ResultadoDoFluxo> {
        const fluxo = fluxosDaClinica.find((f) => f.id === id);
        if (!fluxo) throw new Error(`Fluxo ${id} não existe.`);
        const resultado = await executar(this, fluxo);
        fluxo.execucoes += 1;
        fluxo.ultimaExecucaoEm = new Date().toISOString();
        return resultado;
    },

    async gerarFluxoComIa(): Promise<DadosDoFluxo> {
        throw new Error('Gerar fluxo com IA precisa de uma chave configurada na plataforma.');
    },

    async caminhosDaApi(): Promise<string[]> {
        /* Sem servidor não há o que conferir — e inventar a lista faria a tela
           dizer "confere" sem ter conferido. */
        return [];
    },

    async consumoDeIa(): Promise<LinhaDeConsumoDeIa[]> {
        /* Nenhuma pergunta foi feita porque não há chave: zero é o número
           certo, e não um vazio por falta de dado. */
        return [];
    },

    async salvarLembrete(id: string | null, dados: DadosDoLembrete): Promise<Lembrete> {
        const quando = dados.quando.trim();
        if (!quando) throw new Error('O lembrete precisa de um rótulo de quando.');
        if (dados.offsetMin < 0) throw new Error('A antecedência não pode ser negativa.');

        if (!id) {
            const novo: Lembrete = { id: `r-${Date.now()}`, ...dados, quando };
            lembretes.push(novo);
            return { ...novo };
        }
        const lembrete = lembretes.find((l) => l.id === id);
        if (!lembrete) throw new Error(`Lembrete ${id} não existe.`);
        Object.assign(lembrete, dados, { quando });
        return { ...lembrete };
    },

    async removerLembrete(id: string): Promise<{ removido: boolean }> {
        const antes = lembretes.length;
        lembretes = lembretes.filter((l) => l.id !== id);
        return { removido: lembretes.length < antes };
    },

    async salvarCampanha(id: string | null, dados: DadosDaCampanha): Promise<Campanha> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A campanha precisa de um nome.');
        if (campanhas.some((c) => c.id !== id && c.nome.toLowerCase() === nome.toLowerCase())) {
            throw new Error(`Já existe uma campanha chamada ${nome}.`);
        }
        /* Mesma recusa do banco: campanha ativa sem início não entraria em
           relatório de período nenhum. */
        if (dados.status !== 'rascunho' && !dados.inicio) {
            throw new Error('Campanha fora do rascunho precisa de data de início.');
        }

        const base: CampanhaSemente = {
            id: id ?? `camp-${Date.now()}`,
            nome,
            canal: dados.canal,
            publicoTipo: dados.publicoTipo,
            publicoParam: dados.publicoParam ?? {},
            ...(dados.origemLead ? { origemLead: dados.origemLead } : {}),
            status: dados.status,
            ...(dados.inicio ? { inicio: dados.inicio } : {}),
            ...(dados.fim ? { fim: dados.fim } : {}),
            investimentoCentavos: dados.investimentoCentavos,
        };

        if (!id) campanhas.unshift(base);
        else {
            const indice = campanhas.findIndex((c) => c.id === id);
            if (indice < 0) throw new Error(`Campanha ${id} não existe.`);
            campanhas[indice] = base;
        }
        return paraCampanha(base);
    },

    async salvarSala(id: string | null, dados: { nome: string; ativa: boolean }): Promise<SalaDaClinica> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A sala precisa de um nome.');
        if (salas.some((s) => s.id !== id && s.nome.toLowerCase() === nome.toLowerCase())) {
            throw new Error(`Já existe uma sala chamada ${nome}.`);
        }
        if (!id) {
            const nova: SalaDaClinica = { id: `s${salas.length + 1}-${Date.now()}`, nome, ativa: dados.ativa };
            salas.push(nova);
            return { ...nova };
        }
        const sala = salas.find((s) => s.id === id);
        if (!sala) throw new Error(`Sala ${id} não existe.`);
        sala.nome = nome;
        sala.ativa = dados.ativa;
        return { ...sala };
    },

    /* ---- trilha T5a ---- */

    async preferencias(): Promise<PreferenciaSalva[]> {
        return preferenciasSalvas.map((p) => ({ chave: p.chave, valor: copiarValorDePreferencia(p.valor) }));
    },

    async salvarPreferencias(mudancas: { chave: string; valor: ValorDePreferencia | null }[]): Promise<PreferenciaSalva[]> {
        const desconhecida = mudancas.find((m) => !preferenciaPorChave(m.chave));
        if (desconhecida) throw new Error(`Preferência desconhecida: ${desconhecida.chave}.`);
        exigirPermissaoDeConfigurar();
        /* A guarda da 0156: quem vê o resumo da IA é decisão da proprietária. */
        if (mudancas.some((m) => preferenciaPorChave(m.chave)?.soProprietaria) && !sessaoPode('*')) {
            throw new Error('Só a proprietária muda quem vê o resumo da IA.');
        }
        for (const m of mudancas) {
            /* Restaurar apaga, como no banco: o padrão é do catálogo. */
            preferenciasSalvas = preferenciasSalvas.filter((p) => p.chave !== m.chave);
            if (m.valor !== null) preferenciasSalvas.push({ chave: m.chave, valor: copiarValorDePreferencia(m.valor) });
        }
        return this.preferencias();
    },

    async atendimentoECrm(): Promise<CargaDeAtendimentoECrm> {
        const eu = membroDaSessao();
        const nomeDe = (id: string) => membros.find((m) => m.id === id)?.nome ?? '—';
        const pt = (a: { nome: string }, b: { nome: string }) => a.nome.localeCompare(b.nome, 'pt-BR');

        const filas = filasDeAtendimento.map((f) => ({
            id: f.id, nome: f.nome, departamentoId: f.departamentoId, cor: f.cor, slaMin: f.slaMin, ativa: f.ativa,
            distribuicao: f.distribuicao ?? 'manual',
            membros: f.membrosIds.map((id) => ({ id, nome: nomeDe(id) })).sort(pt),
        })).sort(pt);

        return {
            departamentos: departamentosDaClinica.map((d) => {
                const dele = filas.filter((f) => f.departamentoId === d.id);
                return { ...d, filas: dele.length, usuarios: new Set(dele.flatMap((f) => f.membros.map((m) => m.id))).size };
            }).sort(pt),
            filas,
            etiquetas: copiar(etiquetasDaClinica).sort(pt),
            mensagensRapidas: mensagensRapidasDaClinica
                .filter((m) => podeVerMensagemRapida(m, eu))
                .map((m) => ({ id: m.id, gatilho: m.gatilho, titulo: m.titulo, texto: m.texto, escopo: m.escopo, usos: m.usos, ativa: m.ativa }))
                .sort((a, b) => a.gatilho.localeCompare(b.gatilho)),
            membros: membros.filter((m) => m.acesso === 'ativo').map((m) => ({ id: m.id, nome: m.nome })).sort(pt),
            podeGestao: sessaoPode('configuracoes'),
        };
    },

    async salvarDepartamento(id: string | null, dados: DadosDoDepartamento): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O departamento precisa de um nome.');
        exigirPermissaoDeConfigurar();
        if (departamentosDaClinica.some((d) => d.id !== id && mesmoNome(d.nome, nome))) {
            throw new Error(`Já existe um departamento chamado ${nome}.`);
        }
        const linha = { nome, descricao: dados.descricao.trim(), ativo: dados.ativo };
        if (!id) {
            const novo: DepartamentoSemente = { id: `dep-${Date.now()}-${departamentosDaClinica.length}`, ...linha };
            departamentosDaClinica.push(novo);
            return { id: novo.id };
        }
        const departamento = departamentosDaClinica.find((d) => d.id === id);
        if (!departamento) throw new Error(`Departamento ${id} não existe.`);
        Object.assign(departamento, linha);
        return { id };
    },

    async salvarFila(id: string | null, dados: DadosDaFila): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A fila precisa de um nome.');
        if (!dados.departamentoId) throw new Error('Escolha o departamento: fila sem departamento ninguém sabe quem atende.');
        exigirPermissaoDeConfigurar();
        if (!departamentosDaClinica.some((d) => d.id === dados.departamentoId)) {
            throw new Error('O departamento escolhido não existe nesta clínica.');
        }
        if (filasDeAtendimento.some((f) => f.id !== id && mesmoNome(f.nome, nome))) throw new Error(`Já existe uma fila chamada ${nome}.`);
        const desconhecido = dados.membrosIds.find((m) => !membros.some((x) => x.id === m));
        if (desconhecido) throw new Error(`Membro ${desconhecido} não existe nesta clínica.`);

        const linha = {
            nome, departamentoId: dados.departamentoId, cor: dados.cor, slaMin: dados.slaMin, ativa: dados.ativa,
            distribuicao: dados.distribuicao,
            membrosIds: [...new Set(dados.membrosIds)],
        };
        if (!id) {
            const nova: FilaSemente = { id: `fila-${Date.now()}-${filasDeAtendimento.length}`, ...linha };
            filasDeAtendimento.push(nova);
            return { id: nova.id };
        }
        const fila = filasDeAtendimento.find((f) => f.id === id);
        if (!fila) throw new Error(`Fila ${id} não existe.`);
        Object.assign(fila, linha);
        return { id };
    },

    async salvarEtiqueta(id: string | null, dados: DadosDaEtiqueta): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A etiqueta precisa de um nome.');
        if (dados.filaId && !dados.departamentoId) throw new Error('Escolha o departamento antes da fila.');
        exigirPermissaoDeConfigurar();
        if (dados.filaId && !filasDeAtendimento.some((f) => f.id === dados.filaId && f.departamentoId === dados.departamentoId)) {
            throw new Error('A fila escolhida não pertence a esse departamento.');
        }
        if (etiquetasDaClinica.some((e) => e.id !== id && mesmoNome(e.nome, nome))) throw new Error(`Já existe uma etiqueta chamada ${nome}.`);

        const linha: Omit<EtiquetaSemente, 'id'> = {
            nome, cor: dados.cor, ativa: dados.ativa,
            gatilhos: [...new Set(dados.gatilhos.map((g) => g.trim()).filter(Boolean))],
            ...(dados.departamentoId ? { departamentoId: dados.departamentoId } : {}),
            ...(dados.filaId ? { filaId: dados.filaId } : {}),
        };
        if (!id) {
            const nova: EtiquetaSemente = { id: `etq-${Date.now()}-${etiquetasDaClinica.length}`, ...linha };
            etiquetasDaClinica.push(nova);
            return { id: nova.id };
        }
        const indice = etiquetasDaClinica.findIndex((e) => e.id === id);
        if (indice < 0) throw new Error(`Etiqueta ${id} não existe.`);
        etiquetasDaClinica[indice] = { id, ...linha };
        return { id };
    },

    async salvarMensagemRapida(id: string | null, dados: DadosDaMensagemRapida): Promise<{ id: string }> {
        const gatilho = normalizarGatilho(dados.gatilho);
        if (!gatilho) throw new Error('A mensagem rápida precisa de um gatilho.');
        if (!dados.titulo.trim()) throw new Error('A mensagem rápida precisa de um título.');
        if (!dados.texto.trim()) throw new Error('Escreva o texto da mensagem.');

        const eu = membroDaSessao();
        const existente = id ? mensagensRapidasDaClinica.find((m) => m.id === id) : undefined;
        if (id && (!existente || !podeVerMensagemRapida(existente, eu))) throw new Error('Mensagem rápida não encontrada.');
        /* As mesmas recusas da política do banco. */
        if ((dados.escopo === 'gestao' || existente?.escopo === 'gestao') && !sessaoPode('configuracoes')) {
            throw new Error('Só a gestão da clínica cria ou altera mensagem restrita.');
        }
        const autorId = existente ? existente.autorId : eu;
        if ((dados.escopo === 'privada' || existente?.escopo === 'privada') && (!autorId || autorId !== eu)) {
            throw new Error('Mensagem privada só pode ser de quem a escreveu.');
        }
        if (mensagensRapidasDaClinica.some((m) => m.id !== id && m.gatilho === gatilho)) {
            throw new Error(`Já existe a mensagem rápida /${gatilho}.`);
        }

        const linha = { gatilho, titulo: dados.titulo.trim(), texto: dados.texto, escopo: dados.escopo, ativa: dados.ativa };
        if (!existente) {
            const nova: MensagemRapidaSemente = {
                id: `mr-${Date.now()}-${mensagensRapidasDaClinica.length}`, ...linha, usos: 0,
                ...(autorId ? { autorId } : {}),
            };
            mensagensRapidasDaClinica.push(nova);
            return { id: nova.id };
        }
        Object.assign(existente, linha);
        return { id: existente.id };
    },

    async removerMensagemRapida(id: string): Promise<void> {
        const eu = membroDaSessao();
        const mensagem = mensagensRapidasDaClinica.find((m) => m.id === id);
        if (!mensagem || !podeVerMensagemRapida(mensagem, eu)) throw new Error('Mensagem rápida não encontrada.');
        if (mensagem.escopo === 'gestao' && !sessaoPode('configuracoes')) throw new Error('Só a gestão da clínica remove mensagem restrita.');
        mensagensRapidasDaClinica = mensagensRapidasDaClinica.filter((m) => m.id !== id);
    },

    async usarMensagemRapida(id: string): Promise<number> {
        const mensagem = mensagensRapidasDaClinica.find((m) => m.id === id);
        if (!mensagem) throw new Error('Mensagem rápida não encontrada.');
        mensagem.usos += 1;
        return mensagem.usos;
    },

    async categoriasDeProcedimento(): Promise<CategoriaDeProcedimento[]> {
        garantirCategoriasDosProcedimentos();
        return categoriasDeProcedimentoDaClinica
            .map((c) => ({ ...c, procedimentos: procedimentos.filter((p) => p.categoria.trim() === c.nome).length }))
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    },

    async salvarCategoriaDeProcedimento(id: string | null, dados: { nome: string; ativa: boolean }): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A categoria precisa de um nome.');
        exigirPermissaoDeConfigurar();
        if (categoriasDeProcedimentoDaClinica.some((c) => c.id !== id && c.nome === nome)) {
            throw new Error(`Já existe uma categoria chamada ${nome}.`);
        }
        if (!id) {
            const nova = { id: `catp-${Date.now()}-${categoriasDeProcedimentoDaClinica.length}`, nome, ativa: dados.ativa };
            categoriasDeProcedimentoDaClinica.push(nova);
            return { id: nova.id };
        }
        const categoria = categoriasDeProcedimentoDaClinica.find((c) => c.id === id);
        if (!categoria) throw new Error('Categoria não encontrada.');
        /* Renomear leva os procedimentos junto, como a função do banco. */
        if (categoria.nome !== nome) {
            for (const p of procedimentos) if (p.categoria.trim() === categoria.nome) p.categoria = nome;
        }
        categoria.nome = nome;
        categoria.ativa = dados.ativa;
        return { id };
    },

    async removerCategoriaDeProcedimento(id: string): Promise<{ removida: boolean }> {
        const categoria = categoriasDeProcedimentoDaClinica.find((c) => c.id === id);
        if (!categoria) throw new Error('Categoria não encontrada.');
        exigirPermissaoDeConfigurar();
        if (procedimentos.some((p) => p.categoria.trim() === categoria.nome)) return { removida: false };
        categoriasDeProcedimentoDaClinica = categoriasDeProcedimentoDaClinica.filter((c) => c.id !== id);
        return { removida: true };
    },

    async pacotesDoCatalogo(): Promise<PacoteDoCatalogo[]> {
        return copiar(pacotesDoCatalogoDaClinica).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    },

    async salvarPacoteDoCatalogo(id: string | null, dados: DadosDoPacoteDoCatalogo): Promise<{ id: string }> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O pacote precisa de um nome.');
        if (!Number.isInteger(dados.precoCentavos) || dados.precoCentavos < 0) throw new Error('Informe o preço do pacote.');
        if (dados.sessoes !== undefined && (!Number.isInteger(dados.sessoes) || dados.sessoes <= 0)) {
            throw new Error('Sessões precisa ser um número inteiro maior que zero.');
        }
        exigirPermissaoDeConfigurar();
        if (pacotesDoCatalogoDaClinica.some((p) => p.id !== id && mesmoNome(p.nome, nome))) throw new Error(`Já existe um pacote chamado ${nome}.`);

        const linha: Omit<PacoteDoCatalogo, 'id'> = {
            nome, precoCentavos: dados.precoCentavos, ativo: dados.ativo,
            ...(dados.procedimentoId ? { procedimentoId: dados.procedimentoId } : {}),
            ...(dados.sessoes === undefined ? {} : { sessoes: dados.sessoes }),
            ...(dados.validadeDias === undefined ? {} : { validadeDias: dados.validadeDias }),
        };
        if (!id) {
            const novo: PacoteDoCatalogo = { id: `pct-${Date.now()}-${pacotesDoCatalogoDaClinica.length}`, ...linha };
            pacotesDoCatalogoDaClinica.push(novo);
            return { id: novo.id };
        }
        const indice = pacotesDoCatalogoDaClinica.findIndex((p) => p.id === id);
        if (indice < 0) throw new Error(`Pacote ${id} não existe.`);
        pacotesDoCatalogoDaClinica[indice] = { id, ...linha };
        return { id };
    },

    async assinaturaDaClinica(): Promise<AssinaturaVistaPelaClinica> {
        const daSessao = CLINICAS_DA_PLATAFORMA.find((c) => c.nome === sessaoAtual()?.clinica) ?? CLINICAS_DA_PLATAFORMA[0];
        if (!daSessao) throw new Error('Clínica não encontrada.');
        const assinatura = assinaturasDaBase().find((a) => a.clinicaId === daSessao.id);
        const plano = planos.find((p) => p.codigo === (assinatura?.planoCodigo ?? daSessao.plano));
        return {
            plano: plano?.nome ?? daSessao.plano,
            ciclo: plano?.ciclo ?? 'mensal',
            ...(plano?.precoCentavos === undefined ? {} : { precoCentavos: plano.precoCentavos }),
            situacao: daSessao.situacao,
            ...(assinatura?.inicio ? { inicio: assinatura.inicio } : {}),
            ...(assinatura?.proximaCobranca ? { proximaCobranca: assinatura.proximaCobranca } : {}),
            ...(assinatura?.canceladaEm ? { canceladaEm: assinatura.canceladaEm } : {}),
            ...(plano?.limiteMembros === undefined ? {} : { limiteMembros: plano.limiteMembros }),
            ...(plano?.limiteUnidades === undefined ? {} : { limiteUnidades: plano.limiteUnidades }),
            faturas: faturas.filter((f) => f.clinicaId === daSessao.id).map(paraFatura)
                .sort((a, b) => b.competencia.localeCompare(a.competencia)),
        };
    },

    async sinaisDaCaixa(): Promise<Record<string, SinaisDaConversa>> {
        const hoje = diaDeHoje();
        const agora = Date.now();
        const resultado: Record<string, SinaisDaConversa> = {};
        for (const c of conversas) {
            const daPessoa = c.contatoId
                ? agenda.filter((a) => a.pacienteId === c.contatoId && a.status !== 'cancelado' && a.status !== 'bloqueio')
                    .sort((a, b) => a.inicioEm.localeCompare(b.inicioEm))
                : [];
            const proximo = daPessoa.find((a) => new Date(a.inicioEm).getTime() >= agora);
            const deHoje = daPessoa.find((a) => diaDo(a.inicioEm) === hoje);
            const filaId = filaDasConversas.get(c.id);
            resultado[c.id] = {
                conversaId: c.id,
                ...(filaId ? { filaId } : {}),
                etiquetas: etiquetasDasConversas.filter((m) => m.conversaId === c.id).flatMap((m) => {
                    const e = etiquetasDaClinica.find((x) => x.id === m.etiquetaId);
                    return e ? [{ id: e.id, nome: e.nome, cor: e.cor, origem: m.origem }] : [];
                }),
                procedimentos: [...new Set(daPessoa.map((a) => a.procedimento).filter(Boolean))],
                ...(proximo ? { proximoAgendamento: { inicioEm: proximo.inicioEm, procedimento: proximo.procedimento, profissional: proximo.profissional } } : {}),
                ...(deHoje ? { atendimentoDeHojeId: `at-${deHoje.id}` } : {}),
                temOportunidade: Boolean(c.contatoId) && leads.some((l) => l.contatoId === c.contatoId && l.situacao === 'aberta'),
                temProposta: Boolean(c.contatoId) && orcamentos.some((o) => o.contatoId === c.contatoId && o.status === 'enviado'),
            };
        }
        return resultado;
    },

    async etiquetarContato(contatoId: string, etiquetaId: string): Promise<void> {
        const etiqueta = etiquetasDaClinica.find((e) => e.id === etiquetaId);
        if (!etiqueta) throw new Error('Etiqueta não encontrada.');
        const aplicar = (lista: { id: string; etiquetas: EtiquetaAplicada[]; contatoId?: string }[], chave: 'id' | 'contatoId') => {
            for (const item of lista) {
                if (item[chave] !== contatoId || item.etiquetas.some((e) => e.id === etiquetaId)) continue;
                item.etiquetas = [...item.etiquetas, { id: etiqueta.id, nome: etiqueta.nome, cor: etiqueta.cor, origem: 'manual' as const }]
                    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
            }
        };
        aplicar(pacientes, 'id');
        aplicar(leads, 'contatoId');
    },

    async desetiquetarContato(contatoId: string, etiquetaId: string): Promise<void> {
        for (const p of pacientes) if (p.id === contatoId) p.etiquetas = p.etiquetas.filter((e) => e.id !== etiquetaId);
        for (const l of leads) if (l.contatoId === contatoId) l.etiquetas = l.etiquetas.filter((e) => e.id !== etiquetaId);
    },

    async etiquetarConversa(conversaId: string, etiquetaId: string): Promise<void> {
        /* A mesma permissão que a RLS de `conversa_etiquetas` cobra. */
        if (!sessaoPode('comunicacao')) throw new Error('Etiquetar conversa exige a permissão de comunicação.');
        if (!conversas.some((c) => c.id === conversaId)) throw new Error('Conversa não encontrada.');
        if (!etiquetasDaClinica.some((e) => e.id === etiquetaId)) throw new Error('Etiqueta não encontrada.');
        if (etiquetasDasConversas.some((m) => m.conversaId === conversaId && m.etiquetaId === etiquetaId)) return;
        etiquetasDasConversas.push({ conversaId, etiquetaId, origem: 'manual' });
    },

    async desetiquetarConversa(conversaId: string, etiquetaId: string): Promise<void> {
        if (!sessaoPode('comunicacao')) throw new Error('Tirar etiqueta de conversa exige a permissão de comunicação.');
        etiquetasDasConversas = etiquetasDasConversas.filter((m) => !(m.conversaId === conversaId && m.etiquetaId === etiquetaId));
    },

    async transferirConversa(conversaId: string, filaId: string, atendenteId?: string): Promise<void> {
        const conversa = conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        /* As mesmas recusas de `transferir_conversa`. */
        const fila = filasDeAtendimento.find((f) => f.id === filaId);
        if (!fila) throw new Error('A fila escolhida não é desta clínica.');
        if (!fila.ativa) throw new Error('Esta fila está desativada: escolha outra.');
        if (atendenteId && !fila.membrosIds.includes(atendenteId)) throw new Error('Essa pessoa não atende esta fila.');

        filaDasConversas.set(conversaId, filaId);
        /* A demonstração não distribui: sem pessoa, a conversa fica livre na fila. */
        delete conversa.reservadaPara;
        delete conversa.reservadaParaNome;
        const departamento = departamentosDaClinica.find((d) => d.id === fila.departamentoId);
        if (departamento) conversa.fila = departamento.nome;
        if (atendenteId) {
            const pessoa = membros.find((m) => m.id === atendenteId);
            conversa.atendenteId = atendenteId;
            if (pessoa) conversa.atendente = pessoa.nome;
            conversa.conduz = 'equipe';
            if (conversa.estado !== 'resolvida') conversa.estado = 'em-atendimento';
        } else {
            delete conversa.atendente;
            delete conversa.atendenteId;
        }
    },

    async cadastrosFinanceiros(): Promise<CadastrosFinanceiros> {
        const pt = (a: { nome: string }, b: { nome: string }) => a.nome.localeCompare(b.nome, 'pt-BR');
        return {
            contas: CONTAS.map((c) => {
                const extra = extrasDasContas.get(c.id);
                return {
                    id: c.id, nome: c.nome, tipo: c.tipo,
                    ...(c.banco ? { banco: c.banco } : {}),
                    ...(extra?.agencia ? { agencia: extra.agencia } : {}),
                    ...(extra?.numero ? { numero: extra.numero } : {}),
                    saldoInicialCentavos: c.saldoCentavos,
                    ativa: !contasDesativadas.has(c.id),
                };
            }).sort(pt),
            metodos: METODOS.map((m) => {
                const conta = CONTAS.find((c) => c.nome === m.conta);
                return {
                    id: m.id, nome: m.nome, tipo: m.tipo as TipoDeMetodo, taxaPercentual: m.taxaPercentual, prazoDias: m.prazoDias,
                    ...(conta ? { contaId: conta.id } : {}),
                    ativo: !metodosDesativados.has(m.id),
                };
            }).sort(pt),
        };
    },

    async salvarConta(id: string | null, dados: DadosDaConta): Promise<{ id: string }> {
        exigirPermissaoDoFinanceiro();
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A conta precisa de um nome.');
        if (!Number.isInteger(dados.saldoInicialCentavos)) throw new Error('Saldo inicial inválido.');
        if (CONTAS.some((c) => c.id !== id && mesmoNome(c.nome, nome))) throw new Error(`Já existe uma conta chamada ${nome}.`);
        const extra = {
            ...(dados.agencia?.trim() ? { agencia: dados.agencia.trim() } : {}),
            ...(dados.numero?.trim() ? { numero: dados.numero.trim() } : {}),
        };
        if (!id) {
            sequencia += 1;
            const nova: ContaFinanceira = {
                id: `cta-${sequencia}`, nome, tipo: dados.tipo, saldoCentavos: dados.saldoInicialCentavos,
                ...(dados.banco?.trim() ? { banco: dados.banco.trim() } : {}),
            };
            CONTAS.push(nova);
            extrasDasContas.set(nova.id, extra);
            if (!dados.ativa) contasDesativadas.add(nova.id);
            return { id: nova.id };
        }
        const conta = CONTAS.find((c) => c.id === id);
        if (!conta) throw new Error('Conta não encontrada.');
        const nomeAntigo = conta.nome;
        conta.nome = nome;
        conta.tipo = dados.tipo;
        conta.saldoCentavos = dados.saldoInicialCentavos;
        if (dados.banco?.trim()) conta.banco = dados.banco.trim();
        else delete conta.banco;
        extrasDasContas.set(id, extra);
        if (dados.ativa) contasDesativadas.delete(id);
        else contasDesativadas.add(id);
        /* A demonstração liga pagamento e forma de pagamento à conta pelo NOME:
           renomear sem levar os dois deixaria o saldo sem o que já entrou. */
        if (nomeAntigo !== nome) {
            for (const m of movimentacoes) if (m.conta === nomeAntigo) m.conta = nome;
            for (const m of METODOS) if (m.conta === nomeAntigo) m.conta = nome;
        }
        return { id };
    },

    async salvarMetodo(id: string | null, dados: DadosDoMetodo): Promise<{ id: string }> {
        exigirPermissaoDoFinanceiro();
        const nome = dados.nome.trim();
        if (!nome) throw new Error('A forma de pagamento precisa de um nome.');
        if (!(dados.taxaPercentual >= 0 && dados.taxaPercentual < 100)) throw new Error('A taxa vai de 0 a 99,99%.');
        if (!Number.isInteger(dados.prazoDias) || dados.prazoDias < 0) throw new Error('O prazo de repasse é um número de dias, zero ou mais.');
        if (METODOS.some((m) => m.id !== id && mesmoNome(m.nome, nome))) throw new Error(`Já existe uma forma de pagamento chamada ${nome}.`);
        const conta = dados.contaId ? CONTAS.find((c) => c.id === dados.contaId) : undefined;
        if (dados.contaId && !conta) throw new Error('Conta de destino não encontrada.');
        const linha = { nome, tipo: dados.tipo, taxaPercentual: dados.taxaPercentual, prazoDias: dados.prazoDias };
        if (!id) {
            sequencia += 1;
            const novo: MetodoDePagamento = { id: `met-${sequencia}`, ...linha, ...(conta ? { conta: conta.nome } : {}) };
            METODOS.push(novo);
            if (!dados.ativo) metodosDesativados.add(novo.id);
            return { id: novo.id };
        }
        const metodo = METODOS.find((m) => m.id === id);
        if (!metodo) throw new Error('Forma de pagamento não encontrada.');
        Object.assign(metodo, linha);
        if (conta) metodo.conta = conta.nome;
        else delete metodo.conta;
        if (dados.ativo) metodosDesativados.delete(id);
        else metodosDesativados.add(id);
        return { id };
    },

    async criarTitulo(novo: NovoTitulo): Promise<{ id: string }> {
        exigirPermissaoDoFinanceiro();
        validarTituloDaDemonstracao(novo);
        if (novo.tipo === 'receber' && !novo.contatoId) throw new Error('Escolha o paciente do título a receber.');
        if (novo.tipo === 'pagar' && !novo.fornecedor?.trim()) throw new Error('Informe o fornecedor da conta a pagar.');
        const categoria = novo.categoriaId ? categorias.find((c) => c.id === novo.categoriaId) : undefined;
        if (novo.categoriaId && !categoria) throw new Error('Categoria não encontrada.');

        sequencia += 1;
        const titulo: Titulo = {
            id: `tit-${sequencia}`,
            tipo: novo.tipo,
            descricao: novo.descricao.trim(),
            contraparte: novo.tipo === 'receber' ? nomeDoContato(novo.contatoId) : (novo.fornecedor ?? '').trim(),
            ...(novo.tipo === 'receber' && novo.contatoId ? { contatoId: novo.contatoId } : {}),
            ...(categoria ? { categoria: categoria.nome } : {}),
            valorCentavos: novo.valorCentavos,
            pagoCentavos: 0,
            vencimento: novo.vencimento,
            competencia: `${novo.competencia ?? competenciaDoDia(novo.vencimento)}-01`,
            parcela: 1,
            parcelasTotal: 1,
            situacao: 'aberto',
        };
        titulos.push(titulo);
        if (novo.observacoes?.trim()) observacoesDosTitulos.set(titulo.id, novo.observacoes.trim());
        return { id: titulo.id };
    },

    async detalheDoTitulo(id: string): Promise<DetalheDoTitulo> {
        const titulo = titulos.find((t) => t.id === id);
        if (!titulo) throw new Error('Título não encontrado.');
        const categoria = categorias.find((c) => c.nome === titulo.categoria);
        const observacoes = observacoesDosTitulos.get(id);
        return {
            titulo: { ...titulo },
            ...(categoria ? { categoriaId: categoria.id } : {}),
            ...(titulo.tipo === 'pagar' ? { fornecedor: titulo.contraparte } : {}),
            ...(observacoes ? { observacoes } : {}),
            pagamentos: copiar(movimentacoes.filter((m) => m.tituloId === id)).sort((a, b) => b.pagoEm.localeCompare(a.pagoEm)),
            estornos: copiar(estornosDaDemonstracao.get(id) ?? []),
        };
    },

    async editarTitulo(id: string, edicao: EdicaoDeTitulo): Promise<void> {
        exigirPermissaoDoFinanceiro();
        validarTituloDaDemonstracao(edicao);
        const titulo = titulos.find((t) => t.id === id);
        if (!titulo) throw new Error('Título não encontrado.');
        if (titulo.situacao === 'cancelado') throw new Error('Título cancelado não se edita.');
        if (edicao.valorCentavos < titulo.pagoCentavos) {
            throw new Error(`O valor não pode ficar abaixo do que já foi pago (${(titulo.pagoCentavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}).`);
        }
        const categoria = edicao.categoriaId ? categorias.find((c) => c.id === edicao.categoriaId) : undefined;
        if (edicao.categoriaId && !categoria) throw new Error('Categoria não encontrada.');

        titulo.descricao = edicao.descricao.trim();
        titulo.contraparte = titulo.tipo === 'receber' ? nomeDoContato(edicao.contatoId) : (edicao.fornecedor ?? '').trim();
        if (titulo.tipo === 'receber' && edicao.contatoId) titulo.contatoId = edicao.contatoId;
        if (categoria) titulo.categoria = categoria.nome;
        else delete titulo.categoria;
        titulo.valorCentavos = edicao.valorCentavos;
        titulo.vencimento = edicao.vencimento;
        titulo.competencia = `${edicao.competencia ?? competenciaDoDia(edicao.vencimento)}-01`;
        /* A situação acompanha o valor novo: baixar o valor até o que já foi
           pago quita o título. */
        titulo.situacao = titulo.pagoCentavos === 0 ? 'aberto' : titulo.pagoCentavos >= titulo.valorCentavos ? 'pago' : 'parcial';
        if (edicao.observacoes?.trim()) observacoesDosTitulos.set(id, edicao.observacoes.trim());
        else observacoesDosTitulos.delete(id);
    },

    async cancelarTitulo(id: string): Promise<void> {
        exigirPermissaoDoFinanceiro();
        const titulo = titulos.find((t) => t.id === id);
        if (!titulo) throw new Error('Título não encontrado.');
        if (movimentacoes.some((m) => m.tituloId === id)) {
            throw new Error('Este título já tem pagamento registrado. Cancelar apagaria do histórico um dinheiro que andou.');
        }
        titulo.situacao = 'cancelado';
    },

    async estornarPagamento(pagamentoId: string, motivo: string): Promise<void> {
        exigirPermissaoDoFinanceiro();
        if (!motivo.trim()) throw new Error('Diga o motivo do estorno.');
        const pagamento = movimentacoes.find((m) => m.id === pagamentoId);
        if (!pagamento) throw new Error('Pagamento não encontrado.');
        const titulo = titulos.find((t) => t.id === pagamento.tituloId);
        movimentacoes = movimentacoes.filter((m) => m.id !== pagamentoId);
        if (titulo) {
            titulo.pagoCentavos -= pagamento.valorCentavos;
            if (titulo.situacao !== 'cancelado') {
                titulo.situacao = titulo.pagoCentavos <= 0 ? 'aberto' : titulo.pagoCentavos >= titulo.valorCentavos ? 'pago' : 'parcial';
            }
            estornosDaDemonstracao.set(titulo.id, [
                { id: `est-${pagamentoId}`, valorCentavos: pagamento.valorCentavos, pagoEm: pagamento.pagoEm, motivo: motivo.trim(), estornadoEm: new Date().toISOString() },
                ...(estornosDaDemonstracao.get(titulo.id) ?? []),
            ]);
        }
        /* A linha do extrato que virou este pagamento volta à fila. */
        for (const linha of extratoDaDemonstracao) {
            if (linha.situacao === 'conciliada' && linha.tituloId === pagamento.tituloId && linha.data === pagamento.pagoEm
                && Math.abs(linha.valorCentavos) === pagamento.valorCentavos) {
                linha.situacao = 'pendente';
                delete linha.tituloId;
                delete linha.tituloDescricao;
                break;
            }
        }
    },

    async transferencias(mes?: string): Promise<Transferencia[]> {
        return copiar(transferenciasDaDemonstracao.filter((t) => !mes || t.data < primeiroDiaDoMesSeguinte(mes)))
            .sort((a, b) => b.data.localeCompare(a.data));
    },

    async transferirEntreContas(nova: NovaTransferencia): Promise<{ id: string }> {
        exigirPermissaoDoFinanceiro();
        if (nova.origemId === nova.destinoId) throw new Error('Escolha duas contas diferentes.');
        if (!Number.isInteger(nova.valorCentavos) || nova.valorCentavos <= 0) throw new Error('Informe um valor maior que zero.');
        if (!/^\d{4}-\d{2}-\d{2}$/.test(nova.data)) throw new Error('Informe a data da transferência.');
        const origem = CONTAS.find((c) => c.id === nova.origemId);
        const destino = CONTAS.find((c) => c.id === nova.destinoId);
        if (!origem || !destino) throw new Error('Uma das contas não é desta clínica.');
        sequencia += 1;
        const transferencia: Transferencia = {
            id: `trf-${sequencia}`, origemId: origem.id, origem: origem.nome, destinoId: destino.id, destino: destino.nome,
            valorCentavos: nova.valorCentavos, data: nova.data,
            ...(nova.observacao?.trim() ? { observacao: nova.observacao.trim() } : {}),
        };
        transferenciasDaDemonstracao.push(transferencia);
        return { id: transferencia.id };
    },

    async extratoImportado(contaId: string): Promise<LinhaDoExtratoImportada[]> {
        return copiar(extratoDaDemonstracao.filter((l) => l.contaId === contaId)).sort((a, b) => b.data.localeCompare(a.data));
    },

    async importarExtrato(contaId: string, lidas: LinhaDoOfx[]): Promise<{ novas: number; repetidas: number }> {
        exigirPermissaoDoFinanceiro();
        if (!CONTAS.some((c) => c.id === contaId)) throw new Error('Conta não encontrada.');
        let novas = 0;
        for (const l of lidas) {
            if (extratoDaDemonstracao.some((x) => x.contaId === contaId && x.fitid === l.fitid)) continue;
            sequencia += 1;
            extratoDaDemonstracao.push({ ...l, id: `ext-${sequencia}`, contaId, situacao: 'pendente' });
            novas += 1;
        }
        return { novas, repetidas: lidas.length - novas };
    },

    async conciliarLinhaDoExtrato(linhaId: string, tituloId: string): Promise<void> {
        exigirPermissaoDoFinanceiro();
        const linha = extratoDaDemonstracao.find((l) => l.id === linhaId);
        if (!linha) throw new Error('Linha do extrato não encontrada.');
        if (linha.situacao !== 'pendente') throw new Error(`Esta linha do extrato já foi ${linha.situacao}.`);
        const titulo = titulos.find((t) => t.id === tituloId);
        if (!titulo) throw new Error('O título escolhido não é desta clínica.');
        if (titulo.situacao === 'pago' || titulo.situacao === 'cancelado') throw new Error('Este título já está quitado ou cancelado.');
        if ((linha.valorCentavos > 0 && titulo.tipo !== 'receber') || (linha.valorCentavos < 0 && titulo.tipo !== 'pagar')) {
            throw new Error('Entrada no extrato só concilia com título a receber, e saída com título a pagar.');
        }
        /* O mesmo caminho da baixa manual — é ele que acerta a situação. */
        await this.registrarPagamento({ tituloId, valorCentavos: Math.abs(linha.valorCentavos), pagoEm: linha.data, contaId: linha.contaId });
        linha.situacao = 'conciliada';
        linha.tituloId = titulo.id;
        linha.tituloDescricao = titulo.descricao;
    },

    async ignorarLinhaDoExtrato(linhaId: string): Promise<void> {
        exigirPermissaoDoFinanceiro();
        const linha = extratoDaDemonstracao.find((l) => l.id === linhaId);
        if (!linha) throw new Error('Linha do extrato não encontrada.');
        if (linha.situacao === 'pendente') linha.situacao = 'ignorada';
    },

    /* ---- trilha T5a: editor de atendimento ---- */
    async pausasDoAtendimento(atendimentoId: string): Promise<PausaDoAtendimento[]> {
        return pausasFechadasNoEncerramento(atendimentoId).map((p) => ({ ...p }));
    },

    async pausarAtendimento(atendimentoId: string): Promise<void> {
        exigirPermissaoDeAtender();
        if (estadoDoAtendimento.get(atendimentoId) !== 'em-atendimento') throw new Error('Só consulta aberta pode ser pausada.');
        const pausas = pausasDosAtendimentos.get(atendimentoId) ?? [];
        if (estaPausado(pausas)) throw new Error('A consulta já está pausada.');
        sequencia += 1;
        pausasDosAtendimentos.set(atendimentoId, [...pausas, { id: `pausa-${sequencia}`, de: new Date().toISOString() }]);
    },

    async retomarAtendimento(atendimentoId: string): Promise<void> {
        exigirPermissaoDeAtender();
        const pausas = pausasDosAtendimentos.get(atendimentoId) ?? [];
        const aberta = pausas[pausas.length - 1];
        if (!aberta || aberta.ate) throw new Error('A consulta não está pausada.');
        aberta.ate = new Date().toISOString();
    },

    async reabrirAtendimento(atendimentoId: string, motivo: string): Promise<void> {
        exigirPermissaoDeAtender();
        if (motivo.trim().length < MOTIVO_MINIMO_PARA_REABRIR) throw new Error('Diga por que a consulta está sendo reaberta.');
        const fim = finaisDeAtendimento.get(atendimentoId);
        if (estadoDoAtendimento.get(atendimentoId) !== 'concluido' || !fim) throw new Error('Só consulta encerrada pode ser reaberta.');
        if (diaEmSaoPaulo(fim) !== diaEmSaoPaulo(Date.now())) {
            throw new Error('Só a consulta encerrada hoje pode ser reaberta. A de outro dia pede um adendo, não uma reabertura.');
        }
        /* O tempo em que ficou encerrada não é consulta: entra como pausa, como na `0083`. */
        const pausas = pausasFechadasNoEncerramento(atendimentoId);
        sequencia += 1;
        const agora = new Date().toISOString();
        pausasDosAtendimentos.set(atendimentoId, [...pausas, { id: `pausa-${sequencia}`, de: fim, ate: agora < fim ? fim : agora }]);
        estadoDoAtendimento.set(atendimentoId, 'em-atendimento');
        finaisDeAtendimento.delete(atendimentoId);
        const agendamento = agenda.find((a) => `at-${a.id}` === atendimentoId);
        if (agendamento?.status === 'concluido') agendamento.status = 'confirmado';
    },

    async atendimentoEmAndamento(): Promise<AtendimentoEmAndamento | null> {
        const nome = sessaoAtual()?.nome;
        if (!nome || !sessaoPode('atendimentos')) return null;
        /* A pílula é da pessoa que atende, como na leitura do Supabase. */
        const abertas = agenda.flatMap((a) => {
            const id = `at-${a.id}`;
            const iniciadoEm = iniciosDeAtendimento.get(id);
            return semTitulo(a.profissional) === semTitulo(nome) && iniciadoEm && estadoDoAtendimento.get(id) === 'em-atendimento'
                ? [{ id, paciente: a.paciente, iniciadoEm }]
                : [];
        }).sort((x, y) => y.iniciadoEm.localeCompare(x.iniciadoEm));
        const primeira = abertas[0];
        if (!primeira) return null;
        return { ...primeira, pausado: estaPausado(pausasDosAtendimentos.get(primeira.id) ?? []) };
    },

    async rascunhoDaFicha(atendimentoId: string, modeloId: string): Promise<RascunhoDaFicha | null> {
        const rascunho = rascunhosDasFichas.get(chaveDoRascunho(atendimentoId, modeloId));
        return rascunho ? { respostas: { ...rascunho.respostas }, atualizadoEm: rascunho.atualizadoEm } : null;
    },

    async salvarRascunhoDaFicha(atendimentoId: string, modeloId: string, respostas: NovaFicha['respostas']): Promise<RascunhoDaFicha> {
        exigirPermissaoDeAtender();
        if (estadoDoAtendimento.get(atendimentoId) !== 'em-atendimento') throw new Error('O rascunho só se guarda enquanto a consulta está aberta.');
        if (!modelosDeFicha.some((m) => m.id === modeloId)) throw new Error('Modelo de ficha não encontrado.');
        const rascunho = { respostas: { ...respostas }, atualizadoEm: new Date().toISOString() };
        rascunhosDasFichas.set(chaveDoRascunho(atendimentoId, modeloId), rascunho);
        return { respostas: { ...rascunho.respostas }, atualizadoEm: rascunho.atualizadoEm };
    },

    async descartarRascunhoDaFicha(atendimentoId: string, modeloId: string): Promise<void> {
        rascunhosDasFichas.delete(chaveDoRascunho(atendimentoId, modeloId));
    },

    /* ---- trilha T5a: orçamento ---- */
    async orcamento(id: string): Promise<Orcamento> {
        const achado = orcamentos.find((o) => o.id === id);
        if (!achado) throw new Error('Orçamento não encontrado.');
        return { ...achado, itens: achado.itens.map((i) => ({ ...i })) };
    },

    async editarOrcamento(id: string, dados: NovoOrcamento): Promise<Orcamento> {
        if (!sessaoPode('vendas')) throw new Error('Editar orçamento exige a permissão de vendas.');
        const orcamento = orcamentos.find((o) => o.id === id);
        if (!orcamento) throw new Error('Orçamento não encontrado.');
        if (orcamento.status !== 'rascunho' && orcamento.status !== 'enviado') {
            throw new Error('Orçamento aprovado, recusado ou expirado não se edita: duplique para fazer outra proposta.');
        }
        const itens = dados.itens.filter((i) => i.descricao.trim());
        if (itens.length === 0) throw new Error('Inclua ao menos um item.');
        if (itens.some((i) => !Number.isInteger(i.quantidade) || i.quantidade < 1 || i.valorUnitarioCentavos < 0 || i.descontoCentavos < 0)) {
            throw new Error('Cada item precisa de descrição, quantidade a partir de 1 e valores sem sinal negativo.');
        }
        sequencia += 1;
        orcamento.responsavel = dados.responsavelId
            ? (PROFISSIONAIS.find((p) => p.id === dados.responsavelId)?.nome ?? naoCadastrado('Responsável'))
            : dados.responsavel;
        orcamento.validade = dados.validade;
        orcamento.itens = itens.map((i, indice) => ({ id: `i-${sequencia}-${indice}`, ...i, descricao: i.descricao.trim() }));
        orcamento.totalCentavos = totalDoOrcamento(orcamento.itens);
        orcamento.resumo = orcamento.itens.map((i) => i.descricao).join(' · ');
        /* Enviado volta a rascunho, como na `0084`. */
        orcamento.status = 'rascunho';
        return this.orcamento(id);
    },

    /* ---- trilha T5a: planos de saúde ---- */
    async planosDeSaude(): Promise<PlanoDeSaude[]> {
        const contagem = new Map<string, number>();
        for (const p of pacientes) if (p.convenioId) contagem.set(p.convenioId, (contagem.get(p.convenioId) ?? 0) + 1);
        return planosDaDemonstracao
            .map((p) => ({ ...p, pacientes: contagem.get(p.id) ?? 0 }))
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    },

    async salvarPlanoDeSaude(id: string | null, dados: DadosDoPlanoDeSaude): Promise<{ id: string }> {
        if (!sessaoPode('configuracoes')) throw new Error('Só quem administra a clínica cadastra planos de saúde.');
        const problema = problemaDoPlano(dados);
        if (problema) throw new Error(problema);
        const limpo = normalizarPlano(dados);
        if (planosDaDemonstracao.some((p) => p.id !== id && mesmoNome(p.nome, limpo.nome))) throw new Error(`Já existe um plano chamado ${limpo.nome}.`);

        let alvoId = id;
        if (!alvoId) {
            sequencia += 1;
            alvoId = `plano-${sequencia}`;
            planosDaDemonstracao.push({ id: alvoId, ...limpo });
        } else {
            const indice = planosDaDemonstracao.findIndex((p) => p.id === alvoId);
            if (indice < 0) throw new Error('Plano não encontrado.');
            planosDaDemonstracao[indice] = { id: alvoId, ...limpo };
        }

        /* `CONVENIOS` é a lista de escolha da ficha do paciente: só os ativos,
           como a leitura do Supabase. */
        const naLista = CONVENIOS.findIndex((c) => c.id === alvoId);
        if (limpo.ativo) {
            const item = { id: alvoId, nome: limpo.nome, tipo: limpo.tipo, cor: limpo.cor };
            if (naLista >= 0) CONVENIOS[naLista] = item;
            else CONVENIOS.push(item);
        } else if (naLista >= 0) {
            CONVENIOS.splice(naLista, 1);
        }
        return { id: alvoId };
    },

    /* ---- trilha T5a: ficha preenchida ---- */
    async alterarRespostasDaFicha(fichaId: string, respostas: NovaFicha['respostas'], motivo: string): Promise<FichaPreenchida> {
        if (!sessaoPode('atendimentos')) throw new Error('Corrigir ficha exige a permissão de atendimento.');
        const ficha = fichasClinicas.find((f) => f.id === fichaId);
        if (!ficha) throw new Error('Ficha não encontrada.');
        if (!ficha.definicaoUsada) throw new Error('Ficha anterior à cópia do modelo não se corrige: os rótulos do dia não foram guardados.');
        const problemas = problemasDaAlteracao(ficha.definicaoUsada, ficha.respostas, respostas, motivo);
        if (problemas.length > 0) throw new Error(problemas.join(' '));
        ficha.respostas = { ...respostas };
        ficha.atualizadoEm = new Date().toISOString();
        return { ...ficha, respostas: { ...ficha.respostas } };
    },

    async cabecalhoDaClinica(): Promise<CabecalhoDaClinica> {
        return cabecalhoDe(clinica, unidades);
    },

    /* ---- trilha T2 ---- */

    async opcoesDoAgendamento(): Promise<OpcoesDoAgendamento> {
        return {
            profissionais: copiar(PROFISSIONAIS),
            salas: recorteDaDemonstracao()?.principal === false ? [] : salas.filter((s) => s.ativa).map((s) => ({ id: s.id, nome: s.nome })),
            procedimentos: procedimentos.filter((p) => p.ativo).map((p) => ({ id: p.id, nome: p.nome, duracaoMin: p.duracaoMin })),
            horarios: horarios.map((d) => ({ ...d, faixas: copiar(d.faixas) })),
        };
    },

    async buscarContatos(busca: string): Promise<ContatoParaEscolha[]> {
        const termo = busca.trim();
        if (termo.length < 2) return [];
        const digitos = termo.replace(/\D/g, '');
        return contatosDaDemonstracao()
            .filter((c) => nomeComparavel(c.nome).includes(nomeComparavel(termo))
                || (digitos.length >= 4 && c.telefone.replace(/\D/g, '').includes(digitos)))
            .slice(0, 8);
    },

    async contatoPorId(id: string): Promise<ContatoParaEscolha | null> {
        return contatosDaDemonstracao().find((c) => c.id === id) ?? null;
    },

    async definirLembreteDoAgendamento(agendamentoId: string, lembreteId: string, ativo: boolean): Promise<Lembrete[]> {
        if (!agenda.some((a) => a.id === agendamentoId)) throw new Error(`Agendamento ${agendamentoId} não existe.`);
        if (!lembretes.some((l) => l.id === lembreteId)) throw new Error(`Lembrete ${lembreteId} não existe.`);
        excecoesDeLembrete.set(`${agendamentoId}:${lembreteId}`, ativo);
        return this.lembretesDoAgendamento(agendamentoId);
    },

    async minhaConta(): Promise<MinhaConta> {
        return { ...(await contaDaSessao()) };
    },

    async salvarMinhaConta(dados: { nome: string; telefone: string }): Promise<MinhaConta> {
        const nome = dados.nome.trim();
        if (!nome) throw new Error('O nome não pode ficar vazio.');
        const conta = await contaDaSessao();
        contasDaDemonstracao.set(conta.email, { ...conta, nome, telefone: dados.telefone.trim() });
        return this.minhaConta();
    },

    async salvarMinhaPresenca(presenca: MinhaConta['presenca']): Promise<MinhaConta> {
        const conta = await contaDaSessao();
        contasDaDemonstracao.set(conta.email, { ...conta, presenca });
        return this.minhaConta();
    },

    async importarContatos(tipo: TipoImportavel, linhas: LinhaImportada[]): Promise<ResultadoDaImportacao> {
        const recusadas: RecusaDaImportacao[] = [];
        const avisos: RecusaDaImportacao[] = [];
        let criados = 0;
        for (const l of linhas) {
            const contato: NovoContato = { ...l.contato, tipo };
            if (contato.responsavelNome && !contato.profissionalId) {
                const achado = PROFISSIONAIS.find((p) => nomeComparavel(p.nome) === nomeComparavel(contato.responsavelNome ?? ''));
                if (achado) contato.profissionalId = achado.id;
                else avisos.push({ linha: l.linha, motivo: `Responsável "${contato.responsavelNome}" não está na equipe: entrou sem responsável.` });
            }
            try {
                await this.criarContato(contato);
                criados += 1;
            } catch (e) {
                recusadas.push({ linha: l.linha, motivo: e instanceof Error ? e.message : 'Falha ao gravar.' });
            }
        }
        return { criados, recusadas, avisos };
    },

    async etiquetarContatos(contatoIds: string[], etiquetaId: string): Promise<number> {
        let ganharam = 0;
        for (const id of contatoIds) {
            const antes = pacientes.find((p) => p.id === id)?.etiquetas.length ?? 0;
            await this.etiquetarContato(id, etiquetaId);
            if ((pacientes.find((p) => p.id === id)?.etiquetas.length ?? 0) > antes) ganharam += 1;
        }
        return ganharam;
    },

    async trocarProfissionalDosPacientes(contatoIds: string[], profissionalId: string): Promise<number> {
        const nome = PROFISSIONAIS.find((p) => p.id === profissionalId)?.nome ?? naoCadastrado('Profissional');
        const alvos = pacientes.filter((p) => contatoIds.includes(p.id));
        for (const p of alvos) p.profissional = nome;
        return alvos.length;
    },

    async trocarResponsavelDosLeads(leadIds: string[], membroId: string): Promise<number> {
        const nome = PROFISSIONAIS.find((p) => p.id === membroId)?.nome ?? naoCadastrado('Responsável');
        const alvos = leads.filter((l) => leadIds.includes(l.id));
        for (const l of alvos) l.responsavel = nome;
        return alvos.length;
    },

    async moverLeadsDeEtapa(leadIds: string[], etapaId: string): Promise<number> {
        const falhas: string[] = [];
        for (const id of leadIds) {
            try {
                await this.moverLeadDeEtapa(id, etapaId);
            } catch (e) {
                falhas.push(e instanceof Error ? e.message : 'falha ao mover');
            }
        }
        if (falhas.length > 0) throw new Error(`${falhas.length} de ${leadIds.length} não mudaram de etapa: ${falhas[0]}`);
        return leadIds.length;
    },

    async registrarNoHistoricoDoContato(contatoId: string, texto: string): Promise<void> {
        const nota = texto.trim();
        if (!nota) throw new Error('Escreva o que foi conversado.');
        if (!contatosDaDemonstracao().some((c) => c.id === contatoId)) throw new Error(`Contato ${contatoId} não existe.`);
        notasDosContatos.push({ contatoId, texto: nota, quandoEm: new Date().toISOString() });
    },

    async origensDeLead(): Promise<string[]> {
        return [...new Set(leads.map((l) => l.origem).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
    },

    async arquivarContato(contatoId: string): Promise<void> {
        if (!contatosDaDemonstracao().some((c) => c.id === contatoId)) throw new Error(`Contato ${contatoId} não existe.`);
        arquivados.add(contatoId);
    },

    async salvarDadosDaClinicaEUnidade(novos: EdicaoDaClinica, unidade?: { id: string; mudancas: EdicaoDaUnidade }): Promise<void> {
        const problema = primeiraFalta(faltasDaClinica(novos, unidade?.mudancas));
        if (problema) throw new Error(problema);
        const alvo = unidade ? unidades.find((u) => u.id === unidade.id) : undefined;
        if (unidade && !alvo) throw new Error(`Unidade ${unidade.id} não existe.`);
        /* Confere tudo antes de mudar qualquer coisa: é a mesma garantia da função do banco. */
        clinica = { ...clinica, ...novos };
        if (alvo && unidade) Object.assign(alvo, unidade.mudancas);
    },

    /* ---- trilha T5b ---- */

    /* Cada ação recusa como o banco recusa — mesma permissão, mesmo motivo
       obrigatório, mesma mensagem —, para a tela se comportar igual nos dois. */

    async criarClinica(nova: DadosDaNovaClinica): Promise<{ clinicaId: string }> {
        exigirOperador('clientes.editar', 'Só quem pode editar clientes abre uma clínica nova.');
        const nome = nova.nome.trim();
        const apelido = nova.apelido.trim().toLowerCase();
        const documento = nova.documento.replace(/\D/g, '');
        const email = nova.proprietaria.email.trim().toLowerCase();
        if (nome.length < 3) throw new Error('O nome da clínica precisa de ao menos 3 caracteres.');
        if (!/^[a-z0-9][a-z0-9-]{1,39}$/.test(apelido)) {
            throw new Error('O apelido usa letras minúsculas, números e hífen, de 2 a 40 caracteres.');
        }
        if (CLINICAS_DA_PLATAFORMA.some((c) => c.apelido === apelido)) throw new Error(`Já existe uma clínica com o apelido ${apelido}.`);
        if (documento.length !== 14) throw new Error('O CNPJ precisa de 14 dígitos.');
        if (nova.situacao !== 'trial' && nova.situacao !== 'ativa') throw new Error('Clínica nova nasce em trial ou ativa.');
        if (!planos.some((p) => p.codigo === nova.plano && p.ativo)) throw new Error('Escolha um plano ativo.');
        if (nova.unidade.nome.trim().length < 2) throw new Error('A unidade principal precisa de um nome.');
        if (nova.proprietaria.nome.trim().length < 3) throw new Error('Informe o nome de quem responde pela clínica.');
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) throw new Error('Informe um e-mail válido para a proprietária.');

        const id = `clinica-${apelido}`;
        CLINICAS_DA_PLATAFORMA.push({
            id, nome, apelido, plano: nova.plano, situacao: nova.situacao, criadoEm: HOJE,
            membrosAtivos: 0, contatos: 0, agendamentos30d: 0,
        });
        unidadesDasClinicasNovas.set(id, [{
            id: `unidade-${apelido}`,
            nome: nova.unidade.nome.trim(),
            telefone: nova.unidade.telefone?.trim() ?? '',
            email: '',
            cep: nova.unidade.cep?.trim() ?? '',
            estado: (nova.unidade.estado ?? '').trim().toUpperCase(),
            cidade: nova.unidade.cidade?.trim() ?? '',
            bairro: nova.unidade.bairro?.trim() ?? '',
            rua: nova.unidade.rua?.trim() ?? '',
            numero: nova.unidade.numero?.trim() ?? '',
            complemento: nova.unidade.complemento?.trim() ?? '',
            salas: 0,
        }]);
        proprietariasDasClinicasNovas.set(id, {
            id: `membro-${apelido}`, nome: nova.proprietaria.nome.trim(), email, papel: 'proprietaria',
            acesso: 'convite-pendente', clinica: nome, temLogin: false,
        });
        auditarNaDemo(id, 'plataforma.clinica.criada', 'clinicas', id, { plano: nova.plano, situacao: nova.situacao, apelido });
        return { clinicaId: id };
    },

    async suspenderClinica(id: string, motivo: string): Promise<void> {
        exigirOperador('clientes.suspender', 'Só quem pode suspender clientes suspende uma clínica.');
        const texto = exigirMotivo(motivo);
        const clinicaAlvo = clinicaDoPainel(id);
        if (clinicaAlvo.situacao === 'suspensa') throw new Error('Esta clínica já está suspensa.');
        if (clinicaAlvo.situacao === 'cancelada') throw new Error('Clínica cancelada não tem acesso para suspender.');
        const de = clinicaAlvo.situacao;
        clinicaAlvo.situacao = 'suspensa';
        auditarNaDemo(id, 'plataforma.clinica.suspensa', 'clinicas', id, { de, motivo: texto });
    },

    async reativarClinica(id: string, motivo: string): Promise<SituacaoDaClinica> {
        exigirOperador('clientes.suspender', 'Só quem pode suspender clientes reativa uma clínica.');
        const texto = exigirMotivo(motivo);
        const clinicaAlvo = clinicaDoPainel(id);
        if (clinicaAlvo.situacao !== 'suspensa') throw new Error('Só clínica suspensa é reativada.');
        /* Volta ao que era: reativar uma inadimplente como "ativa" apagaria a dívida da tela. */
        const de = auditoriaDaPlataforma.find((a) => a.clinicaId === id && a.acao === 'plataforma.clinica.suspensa')?.detalhe?.de;
        const para: SituacaoDaClinica = de === 'trial' || de === 'inadimplente' || de === 'ativa' ? de : 'ativa';
        clinicaAlvo.situacao = para;
        auditarNaDemo(id, 'plataforma.clinica.reativada', 'clinicas', id, { para, motivo: texto });
        return para;
    },

    async trocarPlanoDaClinica(id: string, planoCodigo: string, motivo: string): Promise<void> {
        exigirOperador('plano.alterar', 'Só quem pode alterar planos troca o plano de uma clínica.');
        const texto = exigirMotivo(motivo);
        const clinicaAlvo = clinicaDoPainel(id);
        const plano = planos.find((p) => p.codigo === planoCodigo && p.ativo);
        if (!plano) throw new Error('Escolha um plano ativo.');
        if (clinicaAlvo.plano === plano.codigo) throw new Error(`A clínica já está no plano ${plano.nome}.`);
        const de = clinicaAlvo.plano;
        clinicaAlvo.plano = plano.codigo;
        auditarNaDemo(id, 'plataforma.plano.trocado', 'assinaturas', id, { de, para: plano.codigo, motivo: texto });
    },

    async definirLimiteDeMembros(clinicaId: string, limite: number | null): Promise<number> {
        exigirOperador('plano.alterar', 'Só quem pode alterar planos define as vagas de uma clínica.');
        const clinicaAlvo = clinicaDoPainel(clinicaId);
        if (limite !== null && (!Number.isInteger(limite) || limite <= 0)) {
            throw new Error('As vagas precisam ser um número maior que zero, ou vazio para valer o plano.');
        }
        folgasDeMembros.set(clinicaId, limite);
        auditarNaDemo(clinicaId, 'plataforma.vagas.definidas', 'clinicas', clinicaId, { para: limite });
        return clinicaId === 'clinica-vecchia' ? vagasOcupadasDaDemo() : clinicaAlvo.membrosAtivos;
    },

    async aplicarDesconto(idDaClinica: string, desconto: DadosDoDesconto): Promise<void> {
        exigirOperador('plano.alterar', 'Só quem pode alterar planos dá desconto.');
        const texto = exigirMotivo(desconto.motivo);
        if (!Number.isInteger(desconto.percentual) || desconto.percentual < 1 || desconto.percentual > 100) {
            throw new Error('O desconto vai de 1% a 100%.');
        }
        if (!Number.isInteger(desconto.meses) || desconto.meses < 1 || desconto.meses > 24) {
            throw new Error('O desconto dura de 1 a 24 meses.');
        }
        clinicaDoPainel(idDaClinica);
        const agora = new Date().toISOString();
        for (const d of descontosDaDemonstracao) {
            if (d.clinicaId === idDaClinica && !d.revogadoEm) d.revogadoEm = agora;
        }
        sequenciaDoPainel += 1;
        const novo: DescontoDaDemo = {
            id: `desconto-${sequenciaDoPainel}`, clinicaId: idDaClinica, percentual: desconto.percentual,
            inicioCompetencia: competenciaDeHoje(), meses: desconto.meses, motivo: texto,
            autorNome: atual()?.nome ?? 'operador', criadoEm: agora,
        };
        descontosDaDemonstracao.unshift(novo);
        auditarNaDemo(idDaClinica, 'plataforma.desconto.aplicado', 'descontos_da_clinica', novo.id, {
            percentual: desconto.percentual, meses: desconto.meses, motivo: texto,
        });
    },

    async removerDesconto(idDaClinica: string, motivo: string): Promise<void> {
        exigirOperador('plano.alterar', 'Só quem pode alterar planos remove desconto.');
        const texto = exigirMotivo(motivo);
        const vivo = descontosDaDemonstracao.find((d) => d.clinicaId === idDaClinica && !d.revogadoEm);
        if (!vivo) throw new Error('Esta clínica não tem desconto vivo.');
        vivo.revogadoEm = new Date().toISOString();
        auditarNaDemo(idDaClinica, 'plataforma.desconto.removido', 'descontos_da_clinica', vivo.id, { motivo: texto });
    },

    async emitirFaturaAvulsa(fatura: DadosDaFaturaAvulsa): Promise<Fatura> {
        exigirOperador('faturamento.gerenciar', 'Só quem gerencia o faturamento emite fatura avulsa.');
        const descricao = fatura.descricao.trim();
        if (descricao.length < 3) throw new Error('A fatura avulsa precisa dizer do que é.');
        if (!Number.isInteger(fatura.valorCentavos) || fatura.valorCentavos <= 0) {
            throw new Error('A fatura avulsa precisa de um valor maior que zero.');
        }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(fatura.vencimento)) throw new Error('Informe o vencimento.');
        clinicaDoPainel(fatura.clinicaId);
        sequenciaDoPainel += 1;
        const nova: FaturaSemente = {
            id: `fat-avulsa-${sequenciaDoPainel}`, clinicaId: fatura.clinicaId,
            competencia: fatura.vencimento.slice(0, 7), vencimento: fatura.vencimento,
            valorCentavos: fatura.valorCentavos, tipo: 'avulsa', descricao,
        };
        faturas.push(nova);
        auditarNaDemo(fatura.clinicaId, 'plataforma.fatura.avulsa', 'faturas', nova.id, {
            descricao, valorCentavos: fatura.valorCentavos, vencimento: fatura.vencimento,
        });
        return paraFatura(nova);
    },

    async estornarFatura(id: string, motivo: string): Promise<Fatura> {
        exigirOperador('estorno.executar', 'Só quem pode estornar registra um estorno.');
        const texto = exigirMotivo(motivo);
        const fatura = faturas.find((f) => f.id === id);
        if (!fatura) throw new Error('Fatura não encontrada.');
        if (fatura.estornadaEm) throw new Error('Esta fatura já foi estornada.');
        if (!fatura.pagoEm) throw new Error('Só fatura paga é estornada.');
        fatura.estornadaEm = HOJE;
        fatura.estornoMotivo = texto;
        auditarNaDemo(fatura.clinicaId, 'plataforma.fatura.estornada', 'faturas', id, {
            valorCentavos: fatura.valorCentavos, competencia: fatura.competencia, motivo: texto,
        });
        return paraFatura(fatura);
    },

    async salvarCredito(idDaClinica: string, id: string | null, credito: DadosDoCredito): Promise<CreditoDaClinica> {
        const texto = exigirMotivo(credito.motivo);
        if (!Number.isInteger(credito.valorCentavos) || credito.valorCentavos <= 0) {
            throw new Error('O crédito precisa de um valor maior que zero.');
        }
        if (!TIPOS_DE_CREDITO.includes(credito.tipo)) throw new Error('Tipo de crédito desconhecido.');

        if (!id) {
            exigirOperador('creditos.adicionar', 'Só quem pode adicionar créditos concede um.');
            clinicaDoPainel(idDaClinica);
            sequenciaDoPainel += 1;
            const novo: CreditoDaDemo = {
                id: `credito-${sequenciaDoPainel}`, clinicaId: idDaClinica, tipo: credito.tipo, motivo: texto,
                valorCentavos: credito.valorCentavos, saldoCentavos: credito.valorCentavos,
                autorNome: atual()?.nome ?? 'operador', criadoEm: new Date().toISOString(),
            };
            creditosDaDemonstracao.push(novo);
            auditarNaDemo(idDaClinica, 'plataforma.credito.adicionado', 'creditos_da_clinica', novo.id, {
                tipo: credito.tipo, valorCentavos: credito.valorCentavos, motivo: texto,
            });
            return paraCreditoDaDemo(novo);
        }

        exigirOperador('creditos.alterar', 'Só quem pode alterar créditos edita um.');
        const existente = creditosDaDemonstracao.find((c) => c.id === id && c.clinicaId === idDaClinica);
        if (!existente || existente.excluidoEm) throw new Error('Crédito não encontrado.');
        if (existente.saldoCentavos !== existente.valorCentavos) {
            throw new Error('Este crédito já foi usado numa fatura e não pode mais ser editado.');
        }
        const de = existente.valorCentavos;
        existente.tipo = credito.tipo;
        existente.motivo = texto;
        existente.valorCentavos = credito.valorCentavos;
        existente.saldoCentavos = credito.valorCentavos;
        auditarNaDemo(idDaClinica, 'plataforma.credito.alterado', 'creditos_da_clinica', id, { de, para: credito.valorCentavos, motivo: texto });
        return paraCreditoDaDemo(existente);
    },

    async excluirCredito(id: string, motivo: string): Promise<void> {
        exigirOperador('creditos.excluir', 'Só quem pode excluir créditos exclui um.');
        const texto = exigirMotivo(motivo);
        const credito = creditosDaDemonstracao.find((c) => c.id === id);
        if (!credito || credito.excluidoEm) throw new Error('Crédito não encontrado.');
        const saldoPerdidoCentavos = credito.saldoCentavos;
        credito.excluidoEm = new Date().toISOString();
        credito.saldoCentavos = 0;
        auditarNaDemo(credito.clinicaId, 'plataforma.credito.excluido', 'creditos_da_clinica', id, { saldoPerdidoCentavos, motivo: texto });
    },

    async definirRolloutDaFlag(id: string, percentual: number | null): Promise<FlagDeModulo> {
        exigirOperador('flags.alternar', 'Só quem alterna flags define o rollout.');
        if (percentual !== null && (!Number.isInteger(percentual) || percentual < 0 || percentual > 100)) {
            throw new Error('O rollout vai de 0% a 100%.');
        }
        const flag = flags.find((f) => f.id === id);
        if (!flag) throw new Error('Flag não encontrada.');
        const de = flag.rolloutPercentual ?? null;
        if (percentual === null) delete flag.rolloutPercentual;
        else flag.rolloutPercentual = percentual;
        auditarNaDemo(null, 'plataforma.flag.rollout', 'flags', id, { chave: flag.chave, de, para: percentual });
        return flagDaCarga(await this.flags(), id);
    },

    async acionarKillSwitch(id: string, desligar: boolean, motivo: string): Promise<FlagDeModulo> {
        exigirOperador('flags.alternar', 'Só quem alterna flags aciona o kill switch.');
        const texto = exigirMotivo(motivo);
        const flag = flags.find((f) => f.id === id);
        if (!flag) throw new Error('Flag não encontrada.');
        if (desligar && flag.desligadaEm) throw new Error('O kill switch desta flag já está acionado.');
        if (!desligar && !flag.desligadaEm) throw new Error('O kill switch desta flag não está acionado.');
        if (desligar) {
            flag.desligadaEm = new Date().toISOString();
            flag.desligadaMotivo = texto;
        } else {
            delete flag.desligadaEm;
            delete flag.desligadaMotivo;
        }
        auditarNaDemo(null, desligar ? 'plataforma.flag.kill_switch' : 'plataforma.flag.religada', 'flags', id, { chave: flag.chave, motivo: texto });
        return flagDaCarga(await this.flags(), id);
    },

    async operadoresDaPlataforma(): Promise<OperadorDaPlataforma[]> {
        /* Como a função do banco: sem `usuarios.gerenciar`, lista vazia. */
        if (!operadorTem(atual()?.permissoes ?? [], 'usuarios.gerenciar')) return [];
        const eu = atual()?.email;
        return operadoresDaDemonstracao.map((o) => ({ ...o, permissoes: [...o.permissoes], souEu: o.email === eu }));
    },

    async definirPermissoesDoOperador(usuarioId: string, permissoes: string[]): Promise<OperadorDaPlataforma[]> {
        const alvo = operadoresDaDemonstracao.find((o) => o.usuarioId === usuarioId);
        const depois = [...new Set(permissoes.map((p) => p.trim()).filter(Boolean))].sort();
        const recusa = recusaAoDefinirPermissoes(atual()?.permissoes ?? [], alvo?.permissoes ?? [], depois, alvo?.email === atual()?.email);
        if (recusa) throw new Error(recusa);
        if (!alvo) throw new Error('Operador não encontrado.');
        const de = alvo.permissoes;
        alvo.permissoes = depois;
        if (!operadoresDaDemonstracao.some((o) => operadorTem(o.permissoes, 'usuarios.gerenciar'))) {
            alvo.permissoes = de;
            throw new Error('A plataforma ficaria sem ninguém que gerencie os usuários.');
        }
        auditarNaDemo(null, 'plataforma.operador.permissoes', 'operadores_da_plataforma', usuarioId, { de, para: depois });
        return this.operadoresDaPlataforma();
    },

    async saudeDoBanco(): Promise<SaudeDoBanco | null> {
        /* Não há banco para medir: nulo, e a tela diz isso em vez de números inventados. */
        return null;
    },

    async chavesDeApi(): Promise<ChaveDeApi[]> {
        const sessao = atual();
        if (!sessao?.clinicaId || !sessao.permissoes.includes('*')) return [];
        return chavesDaDemonstracao
            .filter((k) => k.clinicaId === sessao.clinicaId)
            .sort((a, b) => Number(Boolean(a.revogadaEm)) - Number(Boolean(b.revogadaEm)) || b.criadaEm.localeCompare(a.criadaEm))
            .map(paraChaveDaDemo);
    },

    async criarChaveDeApi(nome: string): Promise<ChaveDeApiCriada> {
        const sessao = atual();
        if (!sessao?.clinicaId || !sessao.permissoes.includes('*') || sessao.impersonando) {
            throw new Error('Só a proprietária da clínica gera chave de API.');
        }
        const limpo = nome.trim();
        if (limpo.length < 3 || limpo.length > 60) throw new Error('Dê à chave um nome de 3 a 60 caracteres, para reconhecê-la depois.');
        if (chavesDaDemonstracao.filter((k) => k.clinicaId === sessao.clinicaId && !k.revogadaEm).length >= 5) {
            throw new Error('A clínica já tem 5 chaves vivas. Revogue uma antes de gerar outra.');
        }
        const { prefixo, segredo } = gerarChaveDaDemonstracao();
        sequenciaDoPainel += 1;
        const nova: ChaveDaDemo = {
            id: `chave-${sequenciaDoPainel}`, clinicaId: sessao.clinicaId, nome: limpo, prefixo,
            escopos: ['leitura'], autorNome: sessao.nome, criadaEm: new Date().toISOString(), usos: 0,
        };
        chavesDaDemonstracao.push(nova);
        return { chave: paraChaveDaDemo(nova), segredo };
    },

    async revogarChaveDeApi(id: string): Promise<ChaveDeApi[]> {
        const sessao = atual();
        const chave = chavesDaDemonstracao.find((k) => k.id === id);
        if (!chave || !sessao?.permissoes.includes('*') || chave.clinicaId !== sessao.clinicaId) {
            throw new Error('Chave não encontrada.');
        }
        if (chave.revogadaEm) throw new Error('Esta chave já foi revogada.');
        chave.revogadaEm = new Date().toISOString();
        return this.chavesDeApi();
    },

    /* ---- trilha T4 ---- */
    async baseDeConhecimentoDisponivel(): Promise<{ disponivel: boolean; provedor?: string }> {
        /* A demonstração não vetoriza de verdade; responde como uma instalação
           com chave OpenAI, para a tela mostrar o caminho normal. */
        return { disponivel: true, provedor: 'openai' };
    },

    async convidarMembro(membroId: string): Promise<{ email: string | null }> {
        const membro = membros.find((m) => m.id === membroId);
        if (!membro) throw new Error('Profissional não encontrado nesta clínica.');
        if (membro.acesso === 'desativado') {
            throw new Error(`O acesso de ${membro.nome} está desativado: reative antes de convidar.`);
        }
        membro.acesso = 'convite-pendente';
        /* Nenhum e-mail sai de uma demonstração: nulo, e a tela diz isso. */
        return { email: null };
    },

    async criarUsuarioDaEquipe(novo: NovoUsuarioDaEquipe): Promise<{ id: string; email: string }> {
        const email = novo.email.trim().toLowerCase();
        if (!novo.nome.trim()) throw new Error('Informe o nome da pessoa.');
        /* A mesma recusa do gatilho da 0165 — aqui só para a demonstração e os
           testes contarem a mesma história que o banco conta. */
        const limiteDeVagas = limiteDeMembrosDaDemo('clinica-vecchia', clinica.plano);
        if (limiteDeVagas !== null && vagasOcupadasDaDemo() >= limiteDeVagas) {
            throw new Error(`O plano desta clínica libera ${limiteDeVagas} pessoa(s) com acesso, e já são ${vagasOcupadasDaDemo()}. Desative alguém da equipe ou peça mais vagas à plataforma.`);
        }
        if (novo.senha.length < 8) throw new Error('A senha precisa de pelo menos 8 caracteres.');
        if (membros.some((m) => (m.email ?? '').toLowerCase() === email)) {
            throw new Error(`Já existe alguém na equipe com o e-mail ${email}.`);
        }
        const id = `m-${Date.now()}`;
        /* Endereço (0163): parte vazia não entra, para o cadastro recém-criado
           ficar igual ao que a leitura devolveria do banco. */
        const endereco: Partial<MembroDaEquipe> = {};
        for (const parte of ['cep', 'estado', 'cidade', 'bairro', 'rua', 'numero', 'complemento'] as const) {
            const valor = novo[parte]?.trim();
            if (valor) endereco[parte] = valor;
        }
        membros.push({
            id, nome: novo.nome.trim(), email, telefone: novo.telefone?.trim() ?? '', papel: novo.papel,
            especialidades: novo.especialidades?.trim() ?? '',
            ...(novo.registro?.trim() ? { registro: novo.registro.trim() } : {}),
            acesso: 'ativo',
            ...endereco,
        });
        return { id, email };
    },

    async definirSenhaDoMembro(membroId: string, senha: string): Promise<void> {
        const membro = membros.find((m) => m.id === membroId);
        if (!membro) throw new Error('Esta pessoa não está na equipe desta clínica.');
        if (membro.papel === 'proprietaria') throw new Error('A senha da proprietária só é trocada por ela mesma.');
        if (membro.acesso !== 'ativo') throw new Error('Reative o acesso antes de definir uma senha.');
        if (senha.length < 8) throw new Error('A senha precisa de pelo menos 8 caracteres.');
    },

    async enviosAutomaticos(): Promise<boolean> {
        return enviosAutomaticosDaDemo;
    },

    async definirEnviosAutomaticos(ligado: boolean): Promise<boolean> {
        enviosAutomaticosDaDemo = ligado;
        return enviosAutomaticosDaDemo;
    },

    async publicoDaCampanha(id: string): Promise<{ contatoId: string; nome: string }[]> {
        const semente = campanhas.find((c) => c.id === id);
        if (!semente) throw new Error(`Campanha ${id} não existe.`);
        const campanha = paraCampanha(semente);
        if (campanha.publicoTipo === 'sem-retorno') {
            return (await this.retornosEmAberto(campanha.publicoParam.dias ?? 90)).map((r) => ({ contatoId: r.contatoId, nome: r.nome }));
        }
        if (campanha.publicoTipo === 'por-origem') {
            return leads
                .filter((l) => l.origem === campanha.publicoParam.origem && l.situacao === 'aberta')
                .map((l) => ({ contatoId: l.contatoId, nome: l.nome }));
        }
        const mes = diaDeHoje().slice(5, 7);
        return pacientes
            .filter((p) => campanha.publicoTipo !== 'aniversariantes' || p.nascimento?.slice(5, 7) === mes)
            .map((p) => ({ contatoId: p.id, nome: p.nome }));
    },

    async dispararCampanha(id: string, mensagem: string): Promise<ResultadoDoDisparo> {
        const problema = problemaDaMensagemDaCampanha(mensagem);
        if (problema) throw new Error(problema);
        const semente = campanhas.find((c) => c.id === id);
        if (!semente) throw new Error(`Campanha ${id} não existe.`);
        const campanha = paraCampanha(semente);
        /* As mesmas recusas do banco (`enfileirar_campanha`), na mesma ordem. */
        if (campanha.canal !== 'whatsapp') throw new Error('Só campanha de WhatsApp é disparada pela Fórmula MED.');
        if (campanha.status !== 'ativa') throw new Error('Ative a campanha antes de disparar.');
        if (!canais.some((c) => c.provedor === 'uazapi' && c.status === 'conectado')) {
            throw new Error('Nenhum WhatsApp conectado: a campanha não teria por onde sair.');
        }
        const publico = await this.publicoDaCampanha(id);
        if (publico.length === 0) throw new Error('O público está vazio.');
        if (publico.length > LIMITE_DO_DISPARO) throw new Error(`O disparo vai a no máximo ${LIMITE_DO_DISPARO} pessoas por vez.`);

        /* Ninguém recebe a mesma campanha duas vezes. Nada sai de uma
           demonstração: fica só a contagem. */
        const jaForam = disparosDaDemo.get(id) ?? new Set<string>();
        let enfileirados = 0;
        let jaNaFila = 0;
        for (const p of publico) {
            if (jaForam.has(p.contatoId)) jaNaFila += 1;
            else { jaForam.add(p.contatoId); enfileirados += 1; }
        }
        disparosDaDemo.set(id, jaForam);
        return { enfileirados, jaNaFila, semTelefone: 0, semConsentimento: 0, foraDaClinica: 0 };
    },

    async enviarArquivo(conversaId: string, arquivo: File, legenda = ''): Promise<Mensagem> {
        const problema = problemaDoArquivo(arquivo);
        if (problema) throw new Error(problema);
        const conversa = conversas.find((c) => c.id === conversaId);
        if (!conversa) throw new Error('Conversa não encontrada.');
        const canal = canais.find((c) => c.id === conversa.canalId);
        const indisponivel = canal ? indisponibilidadeDoProvedor(canal.provedor) : null;
        if (indisponivel) throw new Error(indisponivel);
        if (canal?.status !== 'conectado') throw new Error('Arquivo só sai com o WhatsApp desta conversa conectado.');

        sequencia += 1;
        const nova: Mensagem = {
            id: `msg-${sequencia}`,
            conversaId,
            direcao: 'enviada',
            tipo: tipoDaMensagemDoArquivo(arquivo.type),
            texto: legenda.trim() || arquivo.name,
            autor: 'Você',
            status: 'enviada',
            ocorridoEm: new Date().toISOString(),
        };
        mensagens.push(nova);
        conversa.ultimaMensagem = nova.texto;
        conversa.ultimaMensagemEm = nova.ocorridoEm;
        conversa.naoLidas = 0;
        return { ...nova };
    },

    /* ---- trilha T7 ---- */

    async unidadesAtivas(): Promise<UnidadeDaClinica[]> {
        return copiar(unidadesAtivasDaDemonstracao()).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    },

    async criarUnidade(dados: NovaUnidade): Promise<UnidadeDaClinica> {
        /* As regras e as mensagens de `criar_unidade` (0101). */
        if (!sessaoPode('configuracoes')) throw new Error('Criar unidade exige a permissão de configurações.');
        const nome = dados.nome.trim();
        const telefone = dados.telefone.trim();
        if (!nome) throw new Error('Dê um nome à unidade.');
        if (!telefone) throw new Error('O telefone da unidade é obrigatório.');
        if (unidadesAtivasDaDemonstracao().some((u) => mesmoNome(u.nome, nome))) {
            throw new Error(`Já existe uma unidade ativa chamada "${nome}".`);
        }
        sequencia += 1;
        const nova: UnidadeDaClinica = { ...dados, id: `u-nova-${sequencia}`, nome, telefone };
        unidades.push(nova);
        return { ...nova };
    },

    async arquivarUnidade(id: string): Promise<void> {
        /* As travas de `app.guarda_da_unidade` (0101), com as mesmas mensagens. */
        const unidade = unidades.find((u) => u.id === id);
        if (!unidade) throw new Error('Unidade não encontrada.');
        if (!sessaoPode('configuracoes')) throw new Error('Arquivar unidade exige a permissão de configurações.');
        if (unidadesArquivadas.has(id)) throw new Error(`A unidade "${unidade.nome}" já está arquivada.`);
        const ativas = unidadesAtivasDaDemonstracao();
        if (ativas.length === 1) throw new Error(`"${unidade.nome}" é a única unidade ativa da clínica: crie outra antes de arquivar esta.`);
        const principal = ativas[0]?.id === id;
        const agora = Date.now();
        const futuros = agenda.filter((a) => a.status !== 'cancelado'
            && new Date(a.inicioEm).getTime() > agora
            && (a.unidadeId ? a.unidadeId === id : principal && a.status !== 'bloqueio')).length;
        if (futuros > 0) {
            throw new Error(`A unidade "${unidade.nome}" tem ${futuros} agendamento(s) futuro(s): remarque ou cancele antes de arquivar.`);
        }
        unidadesArquivadas.add(id);
    },

    async funis(): Promise<Funil[]> {
        return ordenarFunis(copiar(funis));
    },

    async criarFunil(dados: { nome: string; modelo: ModeloDeFunil; responsavelPadraoId?: string }): Promise<Funil> {
        if (!sessaoPode('configuracoes')) throw new Error('Criar pipeline exige a permissão de configurações.');
        const nome = dados.nome.trim();
        if (!nome) throw new Error('Dê um nome ao pipeline.');
        if (funis.some((f) => mesmoNome(f.nome, nome))) throw new Error(`Já existe um pipeline chamado "${nome}".`);
        sequencia += 1;
        const id = `fu-novo-${sequencia}`;
        const novo: Funil = {
            id, nome, ordem: Math.max(-1, ...funis.map((f) => f.ordem)) + 1,
            ...(dados.responsavelPadraoId ? { responsavelPadraoId: dados.responsavelPadraoId } : {}),
            etapas: etapasDoModelo(dados.modelo).map((e, i) => ({
                id: `${id}-et-${i + 1}`, funilId: id, titulo: e.titulo, tipo: e.tipo, ordem: i + 1, cor: e.cor,
            })),
        };
        funis.push(novo);
        return ordenarFunis(copiar([novo]))[0] as Funil;
    },

    async salvarFunil(funilId: string, dados: { nome?: string; etapas: EtapaEditada[] }): Promise<Funil> {
        if (!sessaoPode('configuracoes')) throw new Error('Editar o pipeline exige a permissão de configurações.');
        const funil = funis.find((f) => f.id === funilId);
        if (!funil) throw new Error('Pipeline não encontrado.');
        const ativas = funil.etapas.filter((e) => !e.arquivada);
        /* O tipo vem do que existe, nunca da lista — como no banco. */
        const lista: EtapaEditada[] = dados.etapas.map((e) => {
            if (!e.id) return { ...e, tipo: 'aberta' };
            const existente = ativas.find((x) => x.id === e.id);
            if (!existente) throw new Error(`A etapa "${e.titulo}" não é deste pipeline, ou está arquivada.`);
            return { ...e, tipo: existente.tipo };
        });
        if (ativas.some((e) => !lista.some((x) => x.id === e.id))) throw new Error('Faltou etapa na lista: para tirar uma etapa do pipeline, arquive-a.');
        const problema = problemaDaEdicao(lista);
        if (problema) throw new Error(problema);
        const nome = dados.nome?.trim();
        if (nome && funis.some((f) => f.id !== funilId && mesmoNome(f.nome, nome))) throw new Error(`Já existe um pipeline chamado "${nome}".`);
        if (nome) funil.nome = nome;
        const arquivadas = funil.etapas.filter((e) => e.arquivada);
        funil.etapas = [
            ...lista.map((e, i): EtapaDoFunil => {
                const existente = e.id ? ativas.find((x) => x.id === e.id) : undefined;
                if (existente) return { ...existente, titulo: e.titulo.trim(), cor: e.cor, ordem: i + 1 };
                sequencia += 1;
                return { id: `${funilId}-et-novo-${sequencia}`, funilId, titulo: e.titulo.trim(), tipo: 'aberta', ordem: i + 1, cor: e.cor };
            }),
            ...arquivadas,
        ];
        /* O lead mostra o título de agora: no banco vem pelo embutido; aqui, copia-se. */
        for (const l of leads) {
            const etapa = funil.etapas.find((e) => e.id === l.etapaId);
            if (etapa) l.etapa = etapa.titulo;
        }
        return ordenarFunis(copiar([funil]))[0] as Funil;
    },

    async arquivarEtapa(etapaId: string, destinoId?: string): Promise<number> {
        if (!sessaoPode('configuracoes')) throw new Error('Arquivar etapa exige a permissão de configurações.');
        const etapa = etapaDaDemonstracao(etapaId);
        if (!etapa) throw new Error('Etapa não encontrada.');
        if (etapa.arquivada) throw new Error(`A etapa "${etapa.titulo}" já está arquivada.`);
        if (etapa.tipo !== 'aberta') {
            throw new Error(`"${etapa.titulo}" é a etapa final de ${etapa.tipo === 'ganho' ? 'ganho' : 'perda'} do funil e não se arquiva.`);
        }
        const funil = funis.find((f) => f.id === etapa.funilId);
        if (!funil) throw new Error('Pipeline não encontrado.');
        const nela = leads.filter((l) => l.etapaId === etapaId);
        const destino = destinoId ? funil.etapas.find((e) => e.id === destinoId) : undefined;
        if (nela.length > 0) {
            if (!destinoId) throw new Error(`A etapa "${etapa.titulo}" tem ${nela.length} lead(s): escolha para qual etapa eles vão.`);
            if (!destino || destino.arquivada || destino.id === etapaId) throw new Error('O destino dos leads precisa ser outra etapa ativa do mesmo pipeline.');
        }
        if (!funil.etapas.some((e) => e.tipo === 'aberta' && !e.arquivada && e.id !== etapaId)) {
            throw new Error(`"${etapa.titulo}" é a única etapa aberta do funil: sem ela não há onde um lead novo entrar.`);
        }
        if (destino) {
            for (const l of nela) Object.assign(l, { etapaId: destino.id, etapa: destino.titulo, situacao: destino.tipo });
        }
        etapa.arquivada = true;
        return nela.length;
    },

    /* ---- trilha T6 ---- */
    /* A demonstração não recebe chamada de API nenhuma: não há uso a contar. */
    async usoDasChavesDeApi(): Promise<UsoDeChaveDeApi[]> {
        return [];
    },

    /* Sem servidor não há onde guardar arquivo: fingir o envio devolveria "salvo"
       para um logotipo que some ao recarregar. */
    async logoDaClinica(): Promise<string | null> {
        return null;
    },

    async enviarLogoDaClinica(): Promise<string> {
        throw new Error(SEM_ARMAZENAMENTO_NA_DEMO);
    },

    async removerLogoDaClinica(): Promise<void> {
        throw new Error(SEM_ARMAZENAMENTO_NA_DEMO);
    },

    async convidarProprietaria(clinicaId: string): Promise<{ email: string | null }> {
        exigirOperador('clientes.editar', 'Só quem edita clientes convida a proprietária de uma clínica.');
        if (!clinicaId) throw new Error('Informe a clínica.');
        /* Nenhum e-mail sai de uma demonstração: nulo, e a tela diz isso. */
        return { email: null };
    },

    async mfaObrigatorioParaEscrita(): Promise<boolean> {
        return false;
    },

    async definirMfaObrigatorioParaEscrita(): Promise<boolean> {
        throw new Error('A demonstração não tem servidor de autenticação: exigir o MFA só existe com a aplicação ligada ao servidor.');
    },

    /* ---- trilha T9 ---- */
    /* A configuração é guardada em memória, com as mesmas recusas do banco (0115),
       para a tela ter o que mostrar. Nenhum gateway é chamado: sem servidor não há
       onde guardar a chave com segurança nem por onde o aviso de pagamento chegar. */
    async gatewaysDePagamento(): Promise<GatewayDePagamento[]> {
        return copiar(gatewaysDaDemonstracao);
    },

    async salvarGateway(provedor: ProvedorDeGateway, d: DadosDoGateway): Promise<void> {
        exigirOperador('integracoes.editar', 'Só quem edita as integrações configura um gateway de pagamento.');
        const atual = gatewaysDaDemonstracao.find((g) => g.provedor === provedor);
        const chave = d.chave?.trim() ?? '';
        const segredo = d.segredoWebhook?.trim() ?? '';
        if (!chave && !atual?.temChave) throw new Error('Informe a chave do gateway.');
        if (chave && chave.length < 16) throw new Error('Esta chave é curta demais para ser uma chave de API.');
        if (chave && provedor === 'asaas' && d.ambiente === 'producao' && !chave.startsWith('$aact_prod_')) {
            throw new Error('A chave de produção do Asaas começa com $aact_prod_.');
        }
        if (chave && provedor === 'asaas' && d.ambiente === 'sandbox' && !chave.startsWith('$aact_hmlg_')) {
            throw new Error('A chave de sandbox do Asaas começa com $aact_hmlg_.');
        }
        if (!chave && atual && atual.ambiente !== d.ambiente) throw new Error('Para mudar de ambiente, informe a chave do novo ambiente.');
        if (segredo && provedor === 'stripe' && !segredo.startsWith('whsec_')) throw new Error('O segredo do webhook da Stripe começa com whsec_.');
        if (segredo && segredo.length < 16) throw new Error('O segredo do webhook precisa de ao menos 16 caracteres.');
        /* A chave publicável, com as mesmas recusas da migração 0170 — a demonstração
           existe para a tela ser exercitada com as regras de verdade. */
        const publicavel = d.chavePublicavel?.trim() ?? '';
        if (publicavel && provedor !== 'stripe') throw new Error('Só a Stripe usa chave publicável.');
        if (publicavel && (publicavel.startsWith('sk_') || publicavel.startsWith('rk_'))) {
            throw new Error('Esta é uma chave SECRETA, e o campo da publicável vai ao navegador. A publicável começa com pk_.');
        }
        if (publicavel && publicavel.length < 16) throw new Error('Esta chave publicável é curta demais.');
        if (publicavel && d.ambiente === 'producao' && !publicavel.startsWith('pk_live_')) {
            throw new Error('A chave publicável de produção da Stripe começa com pk_live_.');
        }
        if (publicavel && d.ambiente === 'sandbox' && !publicavel.startsWith('pk_test_')) {
            throw new Error('A chave publicável de teste da Stripe começa com pk_test_.');
        }
        if (provedor === 'stripe' && !publicavel && (!atual?.chavePublicavel || atual.ambiente !== d.ambiente)) {
            throw new Error('Informe a chave publicável da Stripe (pk_), que o navegador usa para tokenizar o cartão.');
        }

        const registro: GatewayDePagamento = {
            provedor,
            ambiente: d.ambiente,
            temChave: true,
            ...(chave ? { dica: chave.slice(-4) } : atual?.dica ? { dica: atual.dica } : {}),
            temSegredoWebhook: Boolean(segredo) || Boolean(atual?.temSegredoWebhook),
            ...(publicavel ? { chavePublicavel: publicavel } : atual?.chavePublicavel ? { chavePublicavel: atual.chavePublicavel } : {}),
            ativo: atual?.ativo ?? false,
            atualizadoEm: new Date().toISOString(),
        };
        gatewaysDaDemonstracao = [...gatewaysDaDemonstracao.filter((g) => g.provedor !== provedor), registro]
            .sort((a, b) => a.provedor.localeCompare(b.provedor));
    },

    async definirGatewayAtivo(provedor: ProvedorDeGateway | null): Promise<void> {
        exigirOperador('integracoes.editar', 'Só quem edita as integrações escolhe o gateway ativo.');
        if (provedor) {
            const g = gatewaysDaDemonstracao.find((x) => x.provedor === provedor);
            if (!g?.temChave) throw new Error('Configure o gateway antes de ativá-lo.');
            if (provedor !== 'mercadopago' && !g.temSegredoWebhook) {
                throw new Error('Guarde o segredo do webhook antes de ativar este gateway: sem ele, o pagamento não dá baixa.');
            }
        }
        gatewaysDaDemonstracao = gatewaysDaDemonstracao.map((g) => ({ ...g, ativo: g.provedor === provedor }));
    },

    async removerGateway(provedor: ProvedorDeGateway): Promise<void> {
        exigirOperador('integracoes.editar', 'Só quem edita as integrações remove um gateway.');
        if (gatewaysDaDemonstracao.find((g) => g.provedor === provedor)?.ativo) {
            throw new Error('Este é o gateway ativo. Ative outro, ou desative a cobrança, antes de removê-lo.');
        }
        gatewaysDaDemonstracao = gatewaysDaDemonstracao.filter((g) => g.provedor !== provedor);
    },

    async testarGateway(): Promise<TesteDoGateway> {
        return { ok: false, mensagem: SEM_GATEWAY_NA_DEMO };
    },

    async criarCobranca(): Promise<CobrancaDaFatura> {
        throw new Error(SEM_GATEWAY_NA_DEMO);
    },

    async criarCobrancasPendentes(): Promise<CobrancasCriadas> {
        if (!gatewaysDaDemonstracao.some((g) => g.ativo)) return { criadas: 0, falhas: [], restantes: 0, semGateway: true };
        throw new Error(SEM_GATEWAY_NA_DEMO);
    },

    async cancelarCobranca(): Promise<void> {
        throw new Error(SEM_GATEWAY_NA_DEMO);
    },

    async linkDePagamento(): Promise<CobrancaDaFatura> {
        throw new Error(SEM_GATEWAY_NA_DEMO);
    },

    /* A demonstração não cobra ninguém, então ninguém fica inadimplente — e o só
       leitura, que o banco impõe por gatilho, não tem como ser imitado aqui. */
    async minhaInadimplencia(): Promise<InadimplenciaDaClinica | null> {
        return null;
    },

    /* Na demonstração não há autocadastro: nenhuma clínica está em teste pago. */
    async meuTeste() {
        return null;
    },

    /* A central de notificações nasce dos gatilhos do banco (0151): sem servidor, ela
       fica vazia — e o sininho diz isso, em vez de mostrar aviso inventado. */
    async minhasNotificacoes() {
        return [];
    },

    async contarNotificacoes() {
        return 0;
    },

    async marcarNotificacaoLida() {
        return undefined;
    },

    async marcarTodasNotificacoesLidas() {
        return undefined;
    },

    /* Sem servidor não há Google: a tela diz isso em vez de fingir uma conexão. */
    async minhaAgendaGoogle() {
        return null;
    },

    async conectarAgendaGoogle(): Promise<string> {
        throw new Error('A demonstração não conecta ao Google: a agenda Google precisa da aplicação ligada ao servidor.');
    },

    async desconectarAgendaGoogle() {
        return undefined;
    },

    async webhooks() {
        const sessao = atual();
        if (!sessao?.clinicaId || !(sessao.permissoes.includes('*') || sessao.permissoes.includes('configuracoes'))) return [];
        return webhooksDaDemo.filter((w) => w.clinicaId === sessao.clinicaId).map((w) => ({ ...w.webhook }));
    },

    async criarWebhook(dados: DadosDoWebhook) {
        const clinicaId = clinicaDoGestorDeWebhooks();
        conferirWebhookDaDemo(dados);
        if (webhooksDaDemo.filter((w) => w.clinicaId === clinicaId).length >= 10) throw new Error('A clínica já tem 10 webhooks. Exclua um antes de criar outro.');
        const segredo = segredoDeWebhookDaDemo();
        sequenciaDoPainel += 1;
        const webhook: Webhook = {
            id: `webhook-${sequenciaDoPainel}`, nome: dados.nome.trim(), url: dados.url.trim(), eventos: [...new Set(dados.eventos)].sort(),
            segredoDica: segredo.slice(-4), ativo: true, falhasSeguidas: 0, ultimaEntregaEm: null, ultimoErro: null,
            autorNome: atual()?.nome ?? null, criadoEm: new Date().toISOString(), pendentes: 0, entregues24h: 0, desistencias24h: 0,
        };
        webhooksDaDemo.push({ clinicaId, webhook });
        return { webhook: { ...webhook }, segredo };
    },

    async atualizarWebhook(id: string, dados: DadosDoWebhook) {
        const clinicaId = clinicaDoGestorDeWebhooks();
        const alvo = webhooksDaDemo.find((w) => w.clinicaId === clinicaId && w.webhook.id === id);
        if (!alvo) throw new Error('Webhook não encontrado.');
        conferirWebhookDaDemo(dados);
        alvo.webhook = {
            ...alvo.webhook, nome: dados.nome.trim(), url: dados.url.trim(), eventos: [...new Set(dados.eventos)].sort(), ativo: dados.ativo,
            falhasSeguidas: dados.ativo && !alvo.webhook.ativo ? 0 : alvo.webhook.falhasSeguidas,
        };
        return this.webhooks();
    },

    async girarSegredoDoWebhook(id: string) {
        const clinicaId = clinicaDoGestorDeWebhooks();
        const alvo = webhooksDaDemo.find((w) => w.clinicaId === clinicaId && w.webhook.id === id);
        if (!alvo) throw new Error('Webhook não encontrado.');
        const segredo = segredoDeWebhookDaDemo();
        alvo.webhook = { ...alvo.webhook, segredoDica: segredo.slice(-4) };
        return segredo;
    },

    async excluirWebhook(id: string) {
        const clinicaId = clinicaDoGestorDeWebhooks();
        if (!webhooksDaDemo.some((w) => w.clinicaId === clinicaId && w.webhook.id === id)) throw new Error('Webhook não encontrado.');
        webhooksDaDemo = webhooksDaDemo.filter((w) => !(w.clinicaId === clinicaId && w.webhook.id === id));
        return this.webhooks();
    },

    async testarWebhook(): Promise<void> {
        clinicaDoGestorDeWebhooks();
        throw new Error(SEM_ENVIO_DE_WEBHOOK_NA_DEMO);
    },

    async entregasDoWebhook() {
        clinicaDoGestorDeWebhooks();
        return [];
    },

    async reenviarEntregaDeWebhook(): Promise<void> {
        throw new Error(SEM_ENVIO_DE_WEBHOOK_NA_DEMO);
    },

    /* ---- trilha B4: brain ---- */
    ...brainNaDemonstracao,

    /* ---- Meta Ads (0162) ---- */
    ...anunciosNaDemonstracao({
        leads: () => leads,
        pode: sessaoPode,
        impersonando: () => Boolean(sessaoAtual()?.impersonando),
        operador: () => sessaoAtual()?.perfil === 'plataforma',
        hoje: () => HOJE,
    }),

    /* ---- nota fiscal (0176) ---- */
    ...notasFiscaisNaDemonstracao({
        pode: sessaoPode,
        operador: () => sessaoAtual()?.perfil === 'plataforma',
        clinicaId: () => sessaoAtual()?.clinicaId ?? undefined,
        titulo: (id) => titulos.find((t) => t.id === id),
        ficha: (contatoId) => fichas[contatoId],
        fatura: (id) => faturas.find((f) => f.id === id),
        nomeDaClinica: (id) => CLINICAS_DA_PLATAFORMA.find((c) => c.id === id)?.nome ?? 'Clínica',
    }),

    /* ---- nota da consulta pela IA ---- */
    ...notaDeIaNaDemonstracao({
        situacaoDoAtendimento: (id) => estadoDoAtendimento.get(id),
        contatoDoAtendimento: (id) => agenda.find((a) => `at-${a.id}` === id)?.pacienteId,
        modelos: () => modelosDeFicha,
        criarModelo: (nome, campos) => {
            sequencia += 1;
            const novo: ModeloDeFicha = { id: `mod-${sequencia}`, nome, campos: campos.map((c) => ({ ...c })), versao: 1 };
            modelosDeFicha.push(novo);
            return novo;
        },
        salvarFicha: (nova) => adaptadorDeDemonstracao.salvarFicha(nova),
        pode: sessaoPode,
        papel: () => sessaoAtual()?.papel,
        nomeDeQuemUsa: () => sessaoAtual()?.nome,
        preferencia: (chave) => preferenciasSalvas.find((p) => p.chave === chave)?.valor,
    }, () => sessaoAtual()?.email),
};

const SEM_ARMAZENAMENTO_NA_DEMO = 'A demonstração não guarda arquivos: o logotipo só é enviado com a aplicação ligada ao servidor.';
const SEM_GATEWAY_NA_DEMO = 'A demonstração não fala com gateway de pagamento: a cobrança online só existe com a aplicação ligada ao servidor.';

/* ---- trilha T9: gateways configurados na demonstração ---- */
let gatewaysDaDemonstracao: GatewayDePagamento[] = [];

/* ---- trilha T5a: estado dos cadastros das Configurações ---- */

let preferenciasSalvas: PreferenciaSalva[] = [];
let departamentosDaClinica: DepartamentoSemente[] = copiar(DEPARTAMENTOS);
let filasDeAtendimento: FilaSemente[] = copiar(FILAS_DE_ATENDIMENTO);
let etiquetasDaClinica: EtiquetaSemente[] = copiar(ETIQUETAS);
let mensagensRapidasDaClinica: MensagemRapidaSemente[] = copiar(MENSAGENS_RAPIDAS);
let pacotesDoCatalogoDaClinica: PacoteDoCatalogo[] = copiar(PACOTES_DO_CATALOGO);
let categoriasDeProcedimentoDaClinica: { id: string; nome: string; ativa: boolean }[] = [];
let etiquetasDasConversas = ETIQUETAS_DAS_CONVERSAS.map((m) => ({ ...m }));
/** A fila de atendimento de cada conversa — a coluna `conversas.fila_id` da `0081`. */
let filaDasConversas = new Map<string, string>();
let transferenciasDaDemonstracao: Transferencia[] = [];
let extratoDaDemonstracao: LinhaDoExtratoImportada[] = [];
let observacoesDosTitulos = new Map<string, string>();
/** Baixas desfeitas, por título — a `pagamentos_estornados` da 0175. */
let estornosDaDemonstracao = new Map<string, PagamentoEstornado[]>();
/** A coluna `titulos.orcamento_id`: de que venda nasceu cada parcela. */
let orcamentoDoTitulo = new Map<string, string>();
let extrasDasContas = new Map<string, { agencia?: string; numero?: string }>();
let contasDesativadas = new Set<string>();
let metodosDesativados = new Set<string>();
let pausasDosAtendimentos = new Map<string, PausaDoAtendimento[]>();
/** O rascunho da ficha, por atendimento, modelo e autor — ver `chaveDoRascunho`. */
let rascunhosDasFichas = new Map<string, RascunhoDaFicha>();
/** O cadastro completo dos planos. `CONVENIOS` é a lista de escolha da ficha: só os ativos. */
let planosDaDemonstracao = PLANOS_DE_SAUDE_SEMENTE.map((p) => ({ ...p }));
const CONVENIOS_DA_SEMENTE = CONVENIOS.map((c) => ({ ...c }));
/* `CONTAS` e `METODOS` são lidos direto pelo financeiro da demonstração; o
   cadastro os altera no lugar, e o reinício os devolve a esta cópia. */
const CONTAS_DA_SEMENTE = CONTAS.map((c) => ({ ...c }));
const METODOS_DA_SEMENTE = METODOS.map((m) => ({ ...m }));

function exigirPermissaoDoFinanceiro(): void {
    if (!sessaoPode('financeiro')) throw new Error('Esta ação exige a permissão do financeiro.');
}

function exigirPermissaoDeAtender(): void {
    if (!sessaoPode('atendimentos')) throw new Error('Esta ação exige a permissão de atendimento.');
}

/** O total da `editar_orcamento` da `0084`: item nunca abaixo de zero. */
function totalDoOrcamento(itens: { quantidade: number; valorUnitarioCentavos: number; descontoCentavos: number }[]): number {
    return itens.reduce((s, i) => s + Math.max(0, i.quantidade * i.valorUnitarioCentavos - i.descontoCentavos), 0);
}

/** "Dra. Ana Prado" na agenda e "Ana Prado" na sessão são a mesma pessoa. */
function semTitulo(nome: string): string {
    return nome.replace(/^dra?\.\s*/i, '').trim();
}

/** O rascunho é de quem escreveu: a chave leva o autor, como a RLS da `0083`. */
function chaveDoRascunho(atendimentoId: string, modeloId: string): string {
    return `${atendimentoId}|${modeloId}|${membroDaSessao() ?? sessaoAtual()?.email ?? ''}`;
}

/** O gatilho `fecha_pausa_ao_encerrar` da `0083`: encerrar com a pausa correndo fecha a pausa no encerramento. */
function pausasFechadasNoEncerramento(atendimentoId: string): PausaDoAtendimento[] {
    const pausas = pausasDosAtendimentos.get(atendimentoId) ?? [];
    const fim = finaisDeAtendimento.get(atendimentoId);
    const aberta = pausas[pausas.length - 1];
    if (fim && aberta && !aberta.ate) aberta.ate = fim < aberta.de ? aberta.de : fim;
    return pausas;
}

function validarTituloDaDemonstracao(dados: EdicaoDeTitulo): void {
    if (!dados.descricao.trim()) throw new Error('Descreva o lançamento.');
    if (!Number.isInteger(dados.valorCentavos) || dados.valorCentavos <= 0) throw new Error('Informe um valor maior que zero.');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dados.vencimento)) throw new Error('Informe o vencimento.');
}

function nomeDoContato(contatoId: string | undefined): string {
    const nome = pacientes.find((p) => p.id === contatoId)?.nome ?? leads.find((l) => l.contatoId === contatoId)?.nome;
    if (!nome) throw new Error('Paciente não encontrado.');
    return nome;
}

/** Cópia funda: o valor composto tem lista dentro, e a tela não pode mexer no guardado. */
function copiarValorDePreferencia(valor: ValorDePreferencia): ValorDePreferencia {
    return JSON.parse(JSON.stringify(valor)) as ValorDePreferencia;
}

/** O membro de quem está logado, pelo nome — a demonstração não tem `usuario_id`. */
function membroDaSessao(): string | undefined {
    const nome = sessaoAtual()?.nome;
    return nome ? membros.find((m) => m.nome === nome)?.id : undefined;
}

/** A regra de leitura da política `mensagens_rapidas_leitura`, em memória. */
function podeVerMensagemRapida(m: MensagemRapidaSemente, eu: string | undefined): boolean {
    if (m.escopo === 'publica') return true;
    if (m.escopo === 'privada') return Boolean(eu) && m.autorId === eu;
    return sessaoPode('configuracoes');
}

/** A permissão que a RLS da `0080` cobra para escrever nos cadastros. */
function exigirPermissaoDeConfigurar(): void {
    if (!sessaoPode('configuracoes')) throw new Error('Só quem administra a clínica altera este cadastro.');
}

/** O gatilho `categoria_do_procedimento` do banco: categoria usada vira cadastro. */
function garantirCategoriasDosProcedimentos(): void {
    for (const p of procedimentos) {
        const nome = p.categoria.trim();
        if (nome && !categoriasDeProcedimentoDaClinica.some((c) => c.nome === nome)) {
            categoriasDeProcedimentoDaClinica.push({ id: `catp-${categoriasDeProcedimentoDaClinica.length + 1}`, nome, ativa: true });
        }
    }
}

function reiniciarCadastrosDaTrilhaT5a(): void {
    preferenciasSalvas = [];
    departamentosDaClinica = copiar(DEPARTAMENTOS);
    filasDeAtendimento = copiar(FILAS_DE_ATENDIMENTO);
    etiquetasDaClinica = copiar(ETIQUETAS);
    mensagensRapidasDaClinica = copiar(MENSAGENS_RAPIDAS);
    pacotesDoCatalogoDaClinica = copiar(PACOTES_DO_CATALOGO);
    categoriasDeProcedimentoDaClinica = [];
    etiquetasDasConversas = ETIQUETAS_DAS_CONVERSAS.map((m) => ({ ...m }));
    filaDasConversas = new Map();
    transferenciasDaDemonstracao = [];
    extratoDaDemonstracao = [];
    observacoesDosTitulos = new Map();
    estornosDaDemonstracao = new Map();
    orcamentoDoTitulo = new Map();
    extrasDasContas = new Map();
    contasDesativadas = new Set();
    metodosDesativados = new Set();
    pausasDosAtendimentos = new Map();
    rascunhosDasFichas = new Map();
    planosDaDemonstracao = PLANOS_DE_SAUDE_SEMENTE.map((p) => ({ ...p }));
    CONVENIOS.splice(0, CONVENIOS.length, ...CONVENIOS_DA_SEMENTE.map((c) => ({ ...c })));
    CONTAS.splice(0, CONTAS.length, ...CONTAS_DA_SEMENTE.map((c) => ({ ...c })));
    METODOS.splice(0, METODOS.length, ...METODOS_DA_SEMENTE.map((m) => ({ ...m })));
    garantirCategoriasDosProcedimentos();
    /* Atestado e prescrição são modelos de documento: entram na mesma lista. */
    modelosDeDocumento.push(...copiar(MODELOS_DE_ATESTADO));
}

reiniciarCadastrosDaTrilhaT5a();

/* ---- trilha T2 ---- */

/** Nome comparável: sem acento, sem caixa e sem "Dra." — agenda e cadastro nem sempre escrevem igual. */
function nomeComparavel(nome: string): string {
    return nome.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/^dra?\.?\s+/, '').trim();
}

/** Pacientes, e leads que ainda não são pacientes — a mesma pessoa não aparece duas vezes. */
function contatosDaDemonstracao(): ContatoParaEscolha[] {
    const idsDePaciente = new Set(pacientes.map((p) => p.id));
    return [
        ...pacientes
            .filter((p) => !arquivados.has(p.id))
            .map((p): ContatoParaEscolha => ({ id: p.id, nome: p.nome, telefone: p.telefone, tipo: 'paciente' })),
        ...leads
            .filter((l) => !idsDePaciente.has(l.contatoId) && !arquivados.has(l.contatoId))
            .map((l): ContatoParaEscolha => ({ id: l.contatoId, nome: l.nome, telefone: l.telefone, tipo: 'lead' })),
    ].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** O único registro com esse nome. Nenhum ou dois é erro — escolher um em silêncio grava no nome errado. */
function unicoPorNome<T extends { nome: string }>(lista: T[], nome: string, rotulo: string): T {
    const alvo = nomeComparavel(nome);
    if (!alvo) throw new Error(`Escolha ${rotulo === 'sala' ? 'a' : 'o'} ${rotulo} na lista.`);
    const achados = lista.filter((item) => nomeComparavel(item.nome) === alvo);
    if (achados.length > 1) throw new Error(`Há mais de um ${rotulo} chamado ${nome.trim()}: escolha na lista.`);
    const [unico] = achados;
    if (!unico) throw new Error(`${rotulo.charAt(0).toUpperCase()}${rotulo.slice(1)} "${nome.trim()}" não está no cadastro.`);
    return unico;
}

function naoCadastrado(rotulo: string): never {
    throw new Error(`${rotulo} não está no cadastro.`);
}

async function contaDaSessao(): Promise<MinhaConta> {
    /* `atual` vem do import do topo, que a T5b trouxe: importar de novo aqui
       só sombrearia o mesmo nome. */
    const sessao = atual();
    if (!sessao) throw new Error('Sessão expirada: entre novamente.');
    const salva = contasDaDemonstracao.get(sessao.email);
    if (salva) return salva;
    const membro = membros.find((m) => nomeComparavel(m.nome) === nomeComparavel(sessao.nome));
    return { nome: sessao.nome, telefone: membro?.telefone ?? '', email: sessao.email, presenca: 'online' };
}

/* ---- trilha T5b: apoio do painel na demonstração ---- */

function reiniciarPainelDaDemonstracao(): void {
    CLINICAS_DA_PLATAFORMA.splice(0, CLINICAS_DA_PLATAFORMA.length, ...CLINICAS_ORIGINAIS.map((c) => ({ ...c })));
    auditoriaDaPlataforma = AUDITORIA_SEMENTE.map((a) => ({ ...a }));
    creditosDaDemonstracao = [];
    descontosDaDemonstracao = [];
    chavesDaDemonstracao = [];
    operadoresDaDemonstracao = OPERADORES_SEMENTE.map((o) => ({ ...o, permissoes: [...o.permissoes] }));
    unidadesDasClinicasNovas = new Map();
    proprietariasDasClinicasNovas = new Map();
    sequenciaDoPainel = 0;
}

function exigirOperador(permissao: string, mensagem: string): void {
    if (!operadorTem(atual()?.permissoes ?? [], permissao)) throw new Error(mensagem);
}

function exigirMotivo(motivo: string): string {
    const texto = motivo.trim();
    if (texto.length < 10) throw new Error('O motivo precisa dizer por quê: escreva ao menos 10 caracteres.');
    return texto;
}

function clinicaDoPainel(id: string): ClinicaNaPlataforma {
    const encontrada = CLINICAS_DA_PLATAFORMA.find((c) => c.id === id);
    if (!encontrada) throw new Error('Clínica não encontrada.');
    return encontrada;
}

function auditarNaDemo(clinicaId: string | null, acao: string, tabela: string, registroId: string, detalhe: Record<string, unknown>): void {
    sequenciaDoPainel += 1;
    auditoriaDaPlataforma.unshift({
        id: `aud-painel-${sequenciaDoPainel}`,
        clinica: clinicaId ? (CLINICAS_DA_PLATAFORMA.find((c) => c.id === clinicaId)?.nome ?? '—') : '—',
        autorNome: atual()?.nome ?? 'operador',
        acao,
        tabela,
        ocorridoEm: new Date().toISOString(),
        ...(clinicaId ? { clinicaId } : {}),
        registroId,
        detalhe,
    });
}

function descontoVigenteNa(clinicaId: string, competencia: string): DescontoDaDemo | undefined {
    return descontosDaDemonstracao.find((d) => d.clinicaId === clinicaId && !d.revogadoEm
        && competencia >= d.inicioCompetencia && competencia < competenciaSomada(d.inicioCompetencia, d.meses));
}

function paraDescontoDaDemo(d: DescontoDaDemo): DescontoDaClinica {
    return {
        id: d.id,
        percentual: d.percentual,
        inicioCompetencia: d.inicioCompetencia,
        fimCompetencia: competenciaSomada(d.inicioCompetencia, d.meses - 1),
        meses: d.meses,
        motivo: d.motivo,
        ...(d.autorNome ? { autorNome: d.autorNome } : {}),
        criadoEm: d.criadoEm,
        ...(d.revogadoEm ? { revogadoEm: d.revogadoEm } : {}),
        vigente: !d.revogadoEm && competenciaDeHoje() < competenciaSomada(d.inicioCompetencia, d.meses),
    };
}

function paraCreditoDaDemo(c: CreditoDaDemo): CreditoDaClinica {
    return {
        id: c.id,
        tipo: c.tipo,
        motivo: c.motivo,
        valorCentavos: c.valorCentavos,
        saldoCentavos: c.saldoCentavos,
        ...(c.autorNome ? { autorNome: c.autorNome } : {}),
        criadoEm: c.criadoEm,
        ...(c.excluidoEm ? { excluidoEm: c.excluidoEm } : {}),
    };
}

function paraChaveDaDemo(k: ChaveDaDemo): ChaveDeApi {
    return {
        id: k.id,
        nome: k.nome,
        prefixo: k.prefixo,
        escopos: [...k.escopos],
        ...(k.autorNome ? { autorNome: k.autorNome } : {}),
        criadaEm: k.criadaEm,
        ...(k.ultimoUsoEm ? { ultimoUsoEm: k.ultimoUsoEm } : {}),
        usos: k.usos,
        ...(k.revogadaEm ? { revogadaEm: k.revogadaEm } : {}),
    };
}

function flagDaCarga(carga: CargaDasFlags, id: string): FlagDeModulo {
    const flag = carga.flags.find((f) => f.id === id);
    if (!flag) throw new Error('Flag não encontrada.');
    return flag;
}

/** O mesmo formato do servidor: `vk_` + 8 hex + `_` + 32 base64url. */
function gerarChaveDaDemonstracao(): { prefixo: string; segredo: string } {
    const aleatorios = (n: number) => crypto.getRandomValues(new Uint8Array(n));
    const prefixo = `vk_${[...aleatorios(4)].map((b) => b.toString(16).padStart(2, '0')).join('')}`;
    const alfabeto = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    const corpo = [...aleatorios(32)].map((b) => alfabeto.charAt(b % 64)).join('');
    return { prefixo, segredo: `${prefixo}_${corpo}` };
}
